// BOTCIERGE ITV — App.js v5.5 — BizBarrett feedback fixes (mobile menu, link modal, bow-out auth, package icons, input guard)
// Sprint 3: Voice overlap fix, text/voice sync, activity ledger on login
// Sprint 4: Avatar encapsulation, BG Art Gallery, Search Engine, expanded icons
// Sprint 4A: Fix1-LinkModal, Fix2-SearchWideMode, Fix3-SearchResultCards,
//            Fix4-greetingDone reset, Fix5-6 voice/2D, Fix7-SearchFetch, Fix8-onOpenLink
//            Fix10-LinkModalBackButton, Fix11-BotStoreZoneTabs
//            Fix12-AlwaysReconnectWS (tts.ws undefined on deployed HeadTTS)
// Sprint 5: Cities + Scenes dual gallery
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  registerController,
  controlBotcierge,
  botciergeSpeak,
  detectIntent,
  loadProfile,
  logoutUser,
  sendToBackend,
  localDateString,
  setUserLocation,
} from "./control/botciergeController";
import PostNeedFormWeb from "./components/xshare/PostNeedFormWeb";
import NeedsScreenWeb from "./components/xshare/NeedsScreenWeb";
import ScheduleModalWeb from "./components/ScheduleModalWeb";
import GoalKeeperModalWeb, { GOAL_META, GOAL_TOKEN_TO_ID } from "./components/GoalKeeperModalWeb";
import MealBreakdownWeb from "./components/MealBreakdownWeb";
import { ViewChecklistWeb, EducationResourcesWeb, SafetyContactsWeb } from "./components/ChatInfoModalsWeb";
import { ChecklistPickerWeb, ChecklistViewWeb } from "./components/ChecklisterWeb";
import CatchphraseModal from "./components/CatchphraseModal";
import SecurityQuestionsModal from "./components/SecurityQuestionsModal";
import EyeIcon from "./components/EyeIcon";
import { LIST } from "./components/listStyle";
import LinkPanel from "./components/LinkPanel";
import { STORE_FROM_DB_ENABLED, loadStoreCatalog, botsInCategory, catalogBot, botVariants, chosenVariantId, variantIcon, saveBotVariant } from "./utils/storeCatalog";
import { screenText, screenFields, screenLedgerAnswer } from "./utils/screen";
import { thumbSrc, isEmptyThumb, canAskWeather } from "./utils/thumbs";
import { ASK_ACCEPT, MAX_ATTACHMENTS, uploadAskFile, cleanAskText, askFileUrl, fileSizeLabel } from "./utils/askAttachments";
import { savedAvatarPrefs, LOGIN_AVATAR_TYPE_ENABLED, waitForHostVoice } from "./utils/avatarPrefs";
import { formatLedgerDatePhrases, isYesResponse, isNoResponse } from "./utils/ledgerHelpers";
import { AUTH_RP_BASE, GOOGLE_WEB_CLIENT_ID } from "./utils/api";
import WeatherCard from "./components/WeatherCard";
import EditListButton from "./components/EditListButton";
import { sortTodos } from "./utils/todoOrder";
import TwoDAvatar from "./components/TwoDAvatar";
import { llmLabelFor } from "./utils/llmLabel";
import ThreeDAvatar from "./components/ThreeDAvatar";
// CATCHPHRASE_POPUP_ENABLED (default on): the catchphrase is typed in a pop-up
// that never auto-capitalises (src/components/CatchphraseModal.js).
const CATCHPHRASE_POPUP_ENABLED = process.env.REACT_APP_CATCHPHRASE_POPUP_ENABLED !== "false";

// Sprint 5A switches (build-time; set REACT_APP_<NAME>=false to turn one off).
const MEAL_BREAKDOWN_ENABLED = process.env.REACT_APP_MEAL_BREAKDOWN_ENABLED !== "false";
const GOOGLE_SERVER_VERIFY_ENABLED = process.env.REACT_APP_GOOGLE_SERVER_VERIFY_ENABLED !== "false";
const FIELD_PREFILTER_ENABLED = process.env.REACT_APP_FIELD_PREFILTER_ENABLED !== "false";

// ── BACKGROUND THEMES ──────────────────────────────────────────────────────────
const BG_THEMES = {
  default: {
    id: "default", label: "✦ Default",
    bgStyle: "linear-gradient(135deg,#f0ecff 0%,#e8f0ff 50%,#f5f0ff 100%)", bgImage: null,
    primary: "#6B4EAA", primaryLight: "#9B7FD4",
    headerBg: "rgba(255,255,255,0.97)", headerBorder: "rgba(107,78,170,0.1)",
    chatBg: "linear-gradient(180deg,#fdfcff 0%,#f8f6ff 100%)",
    bubbleBot: "linear-gradient(135deg,#6B4EAA,#9B7FD4)",
    bubbleUser: "rgba(240,235,255,0.9)", bubbleUserBorder: "rgba(107,78,170,0.12)",
    emotionBarBg: "rgba(250,249,255,0.97)", inputBg: "rgba(248,246,255,0.9)",
    inputBorder: "rgba(107,78,170,0.2)", barBg: "rgba(250,249,255,0.97)",
    pill: "#6B4EAA", pillBorder: "rgba(107,78,170,0.35)",
    sectionBorder: "rgba(107,78,170,0.08)",
    particleColor: "rgba(107,78,170,0.18)", glowColor: "rgba(107,78,170,0.2)",
    textPrimary: "#333", textMeta: "#c0b8d8",
    nameTagBg: "rgba(107,78,170,0.5)",
    particles: ["✦","◆","◇","⬡","✧"],
  },
  egypt: {
    id: "egypt", label: "🏜️ Egypt", bgStyle: null, bgImage: "/bg1.jpg",
    primary: "#C8860A", primaryLight: "#E8A830",
    headerBg: "rgba(30,18,5,0.92)", headerBorder: "rgba(200,134,10,0.3)",
    chatBg: "linear-gradient(180deg,rgba(30,18,5,1) 0%,rgba(20,10,2,1) 100%)",
    bubbleBot: "linear-gradient(135deg,#C8860A,#E8A830)",
    bubbleUser: "rgba(60,35,5,0.88)", bubbleUserBorder: "rgba(200,134,10,0.3)",
    emotionBarBg: "rgba(30,18,5,0.97)", inputBg: "rgba(45,26,5,0.92)",
    inputBorder: "rgba(200,134,10,0.35)", barBg: "rgba(30,18,5,0.97)",
    pill: "#E8A830", pillBorder: "rgba(200,134,10,0.5)",
    sectionBorder: "rgba(200,134,10,0.15)",
    particleColor: "rgba(232,168,48,0.35)", glowColor: "rgba(200,134,10,0.35)",
    textPrimary: "#f5d99a", textMeta: "#8a6535",
    nameTagBg: "rgba(100,55,5,0.75)",
    particles: ["☥","𓂀","◈","△","⊕"],
  },
  nyc: {
    id: "nyc", label: "🗽 NYC", bgStyle: null, bgImage: "/bg2.jpg",
    primary: "#4A90D9", primaryLight: "#7AB8F5",
    headerBg: "rgba(8,12,25,0.92)", headerBorder: "rgba(74,144,217,0.25)",
    chatBg: "linear-gradient(180deg,rgba(8,12,28,1) 0%,rgba(4,7,18,1) 100%)",
    bubbleBot: "linear-gradient(135deg,#1a3a6e,#4A90D9)",
    bubbleUser: "rgba(15,25,55,0.88)", bubbleUserBorder: "rgba(74,144,217,0.25)",
    emotionBarBg: "rgba(8,12,25,0.97)", inputBg: "rgba(12,18,40,0.92)",
    inputBorder: "rgba(74,144,217,0.3)", barBg: "rgba(8,12,25,0.97)",
    pill: "#7AB8F5", pillBorder: "rgba(74,144,217,0.4)",
    sectionBorder: "rgba(74,144,217,0.12)",
    particleColor: "rgba(74,144,217,0.28)", glowColor: "rgba(74,144,217,0.3)",
    textPrimary: "#c8dff8", textMeta: "#3a5a8a",
    nameTagBg: "rgba(8,25,70,0.7)",
    particles: ["★","◆","✦","⬡","✧"],
  },
  jungle: {
    id: "jungle", label: "🌿 Jungle", bgStyle: null, bgImage: "/bg3.jpg",
    primary: "#2E8B57", primaryLight: "#52C888",
    headerBg: "rgba(5,20,8,0.92)", headerBorder: "rgba(46,139,87,0.3)",
    chatBg: "linear-gradient(180deg,rgba(5,20,10,1) 0%,rgba(3,12,6,1) 100%)",
    bubbleBot: "linear-gradient(135deg,#1a5c35,#2E8B57)",
    bubbleUser: "rgba(10,38,18,0.88)", bubbleUserBorder: "rgba(46,139,87,0.25)",
    emotionBarBg: "rgba(5,20,8,0.97)", inputBg: "rgba(8,30,14,0.92)",
    inputBorder: "rgba(46,139,87,0.35)", barBg: "rgba(5,20,8,0.97)",
    pill: "#52C888", pillBorder: "rgba(46,139,87,0.45)",
    sectionBorder: "rgba(46,139,87,0.12)",
    particleColor: "rgba(82,200,136,0.28)", glowColor: "rgba(46,139,87,0.35)",
    textPrimary: "#b8f0d0", textMeta: "#2a6040",
    nameTagBg: "rgba(5,45,20,0.7)",
    particles: ["✿","❋","⁕","✾","❃"],
  },
  waterfall: {
    id: "waterfall", label: "💧 Waterfall", bgStyle: null, bgImage: "/bg4.jpg",
    primary: "#0891B2", primaryLight: "#38BDF8",
    headerBg: "rgba(3,15,28,0.92)", headerBorder: "rgba(8,145,178,0.25)",
    chatBg: "linear-gradient(180deg,rgba(3,15,30,1) 0%,rgba(2,8,18,1) 100%)",
    bubbleBot: "linear-gradient(135deg,#0e4f6e,#0891B2)",
    bubbleUser: "rgba(5,25,48,0.88)", bubbleUserBorder: "rgba(8,145,178,0.25)",
    emotionBarBg: "rgba(3,15,28,0.97)", inputBg: "rgba(5,22,42,0.92)",
    inputBorder: "rgba(8,145,178,0.32)", barBg: "rgba(3,15,28,0.97)",
    pill: "#38BDF8", pillBorder: "rgba(8,145,178,0.4)",
    sectionBorder: "rgba(8,145,178,0.12)",
    particleColor: "rgba(56,189,248,0.32)", glowColor: "rgba(8,145,178,0.35)",
    textPrimary: "#b0e8ff", textMeta: "#1a6a8a",
    nameTagBg: "rgba(3,35,70,0.7)",
    particles: ["💧","◌","○","◎","⊙"],
  },
  city: {
    id: "city", label: "🌆 City", bgStyle: null, bgImage: "/bg5.jpg",
    primary: "#D946EF", primaryLight: "#F0ABFC",
    headerBg: "rgba(10,3,20,0.92)", headerBorder: "rgba(217,70,239,0.25)",
    chatBg: "linear-gradient(180deg,rgba(10,3,22,1) 0%,rgba(5,2,12,1) 100%)",
    bubbleBot: "linear-gradient(135deg,#7c1fa8,#D946EF)",
    bubbleUser: "rgba(28,8,52,0.88)", bubbleUserBorder: "rgba(217,70,239,0.22)",
    emotionBarBg: "rgba(10,3,20,0.97)", inputBg: "rgba(16,5,35,0.92)",
    inputBorder: "rgba(217,70,239,0.3)", barBg: "rgba(10,3,20,0.97)",
    pill: "#F0ABFC", pillBorder: "rgba(217,70,239,0.4)",
    sectionBorder: "rgba(217,70,239,0.12)",
    particleColor: "rgba(217,70,239,0.28)", glowColor: "rgba(217,70,239,0.3)",
    textPrimary: "#f0d0ff", textMeta: "#7030a0",
    nameTagBg: "rgba(70,8,110,0.65)",
    particles: ["✦","★","◆","⬟","✧"],
  },
};

// ── Sprint 5: CITIES gallery ─────────────────────────────────────────────────
const BG_CITIES = [
  { id: "bg_hongkong",  label: "🏙️ Hong Kong",         file: "/bg_hongkong.jpg",  accent: "#FF6B35" },
  { id: "bg_dubai",     label: "🌃 Dubai",              file: "/bg_dubai.jpg",     accent: "#F7C59F" },
  { id: "bg_nyc2",      label: "🗽 New York City",      file: "/bg_nyc2.jpg",      accent: "#4A90D9" },
  { id: "bg_kingston",  label: "🎵 Kingston, Jamaica",  file: "/bg_kingston.jpg",  accent: "#F7D842" },
  { id: "bg_jamaica",   label: "🌴 Jamaica",            file: "/bg_jamaica.jpg",   accent: "#2ECC71" },
  { id: "bg_honolulu",  label: "🌺 Honolulu",           file: "/bg_honolulu.jpg",  accent: "#00BCD4" },
  { id: "bg_beijing",   label: "🏯 Beijing",            file: "/bg_beijing.jpg",   accent: "#E53935" },
  { id: "bg_nigeria",   label: "🌍 Ozubulu, Nigeria",   file: "/bg_nigeria.jpg",   accent: "#8BC34A" },
  { id: "bg_glasgow",   label: "🏴󠁧󠁢󠁳󠁣󠁴󠁿 Glasgow, Scotland",   file: "/bg_glasgow.jpg",   accent: "#4A90D9" },
  { id: "bg_mumbai",    label: "🇮🇳 Mumbai, India",      file: "/bg_mumbai.jpg",    accent: "#FF8F00" },
  { id: "bg_munich",    label: "🇩🇪 Munich, Germany",    file: "/bg_munich.jpg",    accent: "#2ECC71" },
  { id: "bg_chicago",   label: "🌃 Chicago (Gemini)",     file: "/bg_chicago_gemini.jpg", accent: "#4A90D9" },
];

// ── Sprint 5: SCENES gallery ─────────────────────────────────────────────────
const BG_SCENES = [
  { id: "bg_spaceship",    label: "🚀 Space Ship",       file: "/bg_spaceship.jpg",    accent: "#2979FF" },
  { id: "bg_newsanchor",   label: "📺 News Anchor",      file: "/bg_newsanchor.jpg",   accent: "#1565C0" },
  { id: "bg_mma",          label: "🥊 MMA Fight",        file: "/bg_mma.jpg",          accent: "#E53935" },
  { id: "bg_digital_city", label: "💻 Digital City",     file: "/bg_digital_city.jpg", accent: "#00E5FF" },
  { id: "bg_global_network",label: "🌐 Global Network",  file: "/bg_global_network.jpg",accent: "#00BCD4" },
  { id: "bg_autumn_forest", label: "🍂 Autumn Forest",   file: "/bg_autumn_forest.jpg", accent: "#FF8F00" },
];

const BG_GALLERY = [...BG_CITIES, ...BG_SCENES];
const BG_LIST    = ["default","egypt","nyc","jungle","waterfall","city"];
const EMOTIONS   = ["neutral","happy","sad","angry","love","suggestive"];
const EMOTION_EMOJIS = { neutral:"😐", happy:"😄", sad:"😢", angry:"😠", love:"❤️", suggestive:"😉" };
// TASK 2: upsell detection — read-only. Package name varies (FLOWPLANNER, TASKMASTERAI, …),
// so match the shape, never a hardcoded package. The response text is never modified.
const UPSELL_RE = /\bpurchase the\b[\s\S]{0,60}?\bpackage\b/i;
// TEMP GUARD (TASK 2): the backend's "a bit beyond…" capability upsell comes back
// type:"unknown" with the same flags as a real AskMyLLM answer, so we can't tell
// them apart by shape yet. Until AI-DIAG yields a clean backend type flag, exclude
// it from the LLM badge by its own text. Matched on "a bit beyond" (no apostrophe —
// the backend uses a curly one) so both known variants are caught.
const CAPABILITY_UPSELL_RE = /a bit beyond/i;

// Way out of any verification step. Every one of these steps forwards raw input
// to the gate as a candidate answer, so without this a user typing "logout" is
// told their catchphrase did not match — which is what happened on the live app.
// Exact match only: a first name, a security answer or a catchphrase can all
// legitimately contain "bye", and the full LOGOUT regex would then lock someone
// out of their own login.
const VERIFY_ESCAPE_CMDS = new Set(["logout", "log out", "sign out", "cancel"]);
function isVerifyEscape(text) {
  return VERIFY_ESCAPE_CMDS.has((text || "").trim().toLowerCase());
}

const PKG_ICONS = {
  REMEMBOT:"/RememBot.png", TASKMASTERAI:"/TodoList.png", SHOPSAVVY:"/ShoppingAssistant.png",
  FOODLINK:"/FoodShare.png", BLOODSHARE:"/BloodShare.png", BILLBRIDGE:"/BillPayShare.png",
  FLOWPLANNER:"/Flow.png", MATHMASTER:"/MathAssistant.png", DIGITHER:"/DigitHer.png",
  RESCUEBIDS:"/RescueBids.jpg", GIFTMINDER:"/Benevolence.png", EDUCATIONX:"/EducationX.webp",
  CHECKLISTER:"/CheckLister.jpeg", GOALKEEPER:"/GoalKeeper.jpeg", READYSCHEDULER:"/ReadyScheduler.jpeg",
  // Biz assets (helperbots / workflow bots) — ready when packages go live
  ASKMYLLM:"/AskMyLLM.png", CAREMEDIC:"/CareMedic.png", TIFFANY:"/Tiffany.png",
  ARTBOT:"/icons/artbot/artbot-green.webp", // green is the default; the owner can pick another
  // STORE_ICONS_FIX_ENABLED (Round 11): Zone B / C bots with their own artwork
  // (the same images as the phone app). They used to fall back to /boticon.png,
  // which is the Love & Relationship robot.
  ...(process.env.REACT_APP_STORE_ICONS_FIX_ENABLED !== "false" ? {
    "DATAHIVE AI":"/bots/DataHiveAI.png", "GET SALES IO":"/bots/GetSalesIO.png",
    "LIVE FLOW AI":"/bots/LiveFlowAI.png", "TALENT PILOT":"/bots/TalentPilot.png",
  } : {}),
};
// A package with no artwork of its own: a neutral bot, never another bot's icon.
const DEFAULT_PKG_ICON = process.env.REACT_APP_STORE_ICONS_FIX_ENABLED !== "false" ? "/bots/default.svg" : "/boticon.png";
/** Map latest MBTS package names → icon assets (Severity 1: show every package in user array) */
const PKG_ICON_ALIASES = {
  TASKMASTERX: "TASKMASTERAI", TASKMASTER: "TASKMASTERAI",
  BLOODSAVVY: "BLOODSHARE", BLOODSHARE: "BLOODSHARE",
  SHOPSAVVY: "SHOPSAVVY", FOODLINK: "FOODLINK",
  BOTCIERGE: "GIFTMINDER", MBTS08: "GIFTMINDER",
  ARTCREATOR: "ARTBOT", ARTMAKER: "ARTBOT",
};
function resolvePackageIcon(pkg, choices) {
  const key = PKG_ICON_ALIASES[pkg] || pkg;
  const chosen = variantIcon(STORE_CATALOG, key, choices);
  if (chosen) return chosen;
  return PKG_ICONS[key] || PKG_ICONS[String(key).toUpperCase()] || DEFAULT_PKG_ICON;
}
const AVATAR_ICONS = { Camille:"👩", Prithi:"🧕", Benjamin:"🧔", John:"👨", Margie:"👩‍🦰", Genie:"🧞" };
const AVATAR_LIST = ["Camille", "Prithi", "Benjamin", "John", "Margie", "Genie"];

/** Biz: hide in-development / non-purchasable packages from store + icon bar */
const HIDDEN_PACKAGES = new Set(["FREEMIUM"]);
function visibleUserPackages(pkgs = []) {
  return (pkgs || []).filter((p) => p && !HIDDEN_PACKAGES.has(p));
}

// ── FIX 11: BOT STORE ZONES A / B / C ─────────────────────────────────────────
const BOT_STORE_ZONES = {
  A: {
    label: "Zone A", sublabel: "Apple", icon: "🍎", color: "#007AFF",
    categories: [
      "Books","Business","Developer Tools","Education","Entertainment","Finance",
      "Food & Drink","Games","Graphics & Design","Health & Fitness","Lifestyle",
      "Kids","Magazines & Newspapers","Medical","Music","Navigation","News",
      "Personal Development","Photo & Video","Productivity","Reference","Safari Extensions","Shopping",
      "Social Networking","Sports","Travel","Utilities","Weather",
    ],
  },
  B: { label: "Zone B", sublabel: "B2B",     icon: "🏢", color: "#34C759", categories: [] },
  C: { label: "Zone C", sublabel: "Agentic", icon: "🤖", color: "#AF52DE", categories: [] },
};

// ── Sprint 4B: Zone A service → Apple category mapping ────────────────────────
const ZONE_A_SERVICE_CATEGORY = {
  GIFTMINDER:"Lifestyle", FLOWPLANNER:"Productivity", REMEMBOT:"Health & Fitness",
  RESCUEBIDS:"Finance", BILLBRIDGE:"Finance", BLOODSHARE:"Medical",
  FOODLINK:"Food & Drink", SHOPSAVVY:"Shopping", TASKMASTERAI:"Productivity",
  MATHMASTER:"Education", DIGITHER:"Utilities", EDUCATIONX:"Education",
  READYSCHEDULER:"Productivity", GOALKEEPER:"Personal Development", CHECKLISTER:"Productivity",
  ARTBOT:"Graphics & Design", // Leonardo pictures (31 Oct 2026)
  // Unhidden for purchase (client). Category is a PRODUCT CHOICE — "Productivity"
  // is my default (matches TASKMASTERAI/FLOWPLANNER); change this one line if the
  // client wants it elsewhere. Without a category entry it will not appear in the
  // store browse at all, so unhiding alone was not enough.
  ASKMYLLM:"Utilities",
};
const ZONE_A_SERVICE_DISPLAY = {
  GIFTMINDER:"GiftMinder", FLOWPLANNER:"FlowPlanner", REMEMBOT:"RememBOT",
  RESCUEBIDS:"RescueBids", BILLBRIDGE:"BillBridge", BLOODSHARE:"BloodShare",
  FOODLINK:"FoodLink", SHOPSAVVY:"ShopSavvy", TASKMASTERAI:"TaskMasterAI",
  MATHMASTER:"MathMaster", DIGITHER:"DigiTher", EDUCATIONX:"EducationX",
  READYSCHEDULER:"ReadyScheduler", GOALKEEPER:"GoalKeeper", CHECKLISTER:"CheckLister",
  ASKMYLLM:"AskMyLLM",
  ARTBOT:"ArtBOT",
};
const ZONE_A_SERVICE_DESC = {
  GIFTMINDER:"Thoughtful gift giving, wishlists and events",
  FLOWPLANNER:"Plan your day, manage goals and daily flow",
  REMEMBOT:"Remember and recall anything you need",
  RESCUEBIDS:"Avoid bank fees and access small loans",
  BILLBRIDGE:"Get help managing and splitting bills",
  BLOODSHARE:"Connect blood donors with those in need",
  FOODLINK:"Share food or find food in your community",
  SHOPSAVVY:"Manage your shopping list with ease",
  TASKMASTERAI:"Add, view and manage your to-do list",
  MATHMASTER:"Invoke math functions and calculations",
  DIGITHER:"Community safety and distress alerts for women",
  EDUCATIONX:"Access educational resources and study help",
  READYSCHEDULER:"Schedule tasks and manage appointments",
  GOALKEEPER:"Set, track and manage your personal goals",
  CHECKLISTER:"Create and manage checklists efficiently",
  ASKMYLLM:"Ask any AI model your questions",
  ARTBOT:"Makes a picture of anything you describe, right in the chat",
};
const ZONE_A_SERVICE_PRICE = {
  GIFTMINDER:1.99, FLOWPLANNER:1.99, REMEMBOT:3.99, RESCUEBIDS:2.99,
  BILLBRIDGE:1.99, BLOODSHARE:1.99, FOODLINK:0.99, SHOPSAVVY:1.99,
  TASKMASTERAI:1.99, MATHMASTER:1.99, DIGITHER:1.99, EDUCATIONX:1.99,
  READYSCHEDULER:2.99, GOALKEEPER:3.49, CHECKLISTER:2.99,
  ASKMYLLM:4.99, // client-confirmed server-side price; keep in sync with backend
  ARTBOT:6.99, // Biz, 2 Nov 2026 (30 pictures a month)
};
// The database catalogue (src/utils/storeCatalog.js), once loaded. Zone A keeps
// the presentation above and shows only services the database also lists;
// Zones B and C come from the database.
let STORE_CATALOG = null;
function getZoneAServicesForCategory(category) {
  return Object.keys(ZONE_A_SERVICE_CATEGORY).filter(k => ZONE_A_SERVICE_CATEGORY[k] === category && (!STORE_CATALOG || STORE_CATALOG.A.keys.has(k)));
}
function getZoneACategoriesWithServices() {
  const withServices = new Set(Object.values(ZONE_A_SERVICE_CATEGORY));
  return BOT_STORE_ZONES.A.categories.filter(cat => withServices.has(cat));
}
function getBotStoreCategories(zone) {
  if (zone === "A") return getZoneACategoriesWithServices();
  if (STORE_FROM_DB_ENABLED && STORE_CATALOG) return STORE_CATALOG[zone]?.categories || [];
  return BOT_STORE_ZONES[zone]?.categories || [];
}
function buildPackageListFromIcons(userPackages = []) {
  return Object.keys(PKG_ICONS)
    .filter((name) => !HIDDEN_PACKAGES.has(name))
    .map(name => ({
      name,
      selected: userPackages.includes(name),
      price: ZONE_A_SERVICE_PRICE[name] ?? 0,
    }));
}
function computePackageChanges(pkgs, baseline = []) {
  const base = baseline.filter(p => p !== "FREEMIUM");
  const selected = pkgs.filter(p => p.selected).map(p => p.name).filter(n => n !== "FREEMIUM");
  return {
    subscribe: selected.filter(n => !base.includes(n)),
    unsubscribe: base.filter(n => !selected.includes(n)),
    selected,
  };
}
function calcMonthlyPrice(pkgs) {
  let price = 6.95;
  pkgs.filter(p => p.selected && p.name !== "FREEMIUM").forEach(pkg => {
    price += ZONE_A_SERVICE_PRICE[pkg.name] ?? pkg.price ?? 0;
  });
  return price;
}
function calcMonthlyPriceFromNames(names = []) {
  let price = 6.95;
  names.filter(n => n !== "FREEMIUM").forEach(name => {
    price += ZONE_A_SERVICE_PRICE[name] ?? 0;
  });
  return price;
}
const SHOPSAVVY_CATEGORIES = ["groceries", "hardware", "electronics", "cosmetics", "clothing", "home", "sports", "automotive", "other"];
// Fallback unit list (exact mobile UnitListModal set) — used only if the
// backend doesn't send units from the thing database.
const SHOPSAVVY_UNITS = ["Each","Pack","Set","Case","Bundle","Piece","Unit","Box","Roll","Yard","Meter","Sheet","Download","License","Copy","Kit","Bottle","Jar","Pound","Kilogram","Ounce","Album","Song","Volume","Coin","Disc","DVD","Blu-Ray","Game","Subscription","eBook","Card","Pair","Bag","Container"];
function isSrxContinuation(s) {
  if (!s || !Object.keys(s).length) return false;
  if (s.type === "input") return true;
  if (s.type === "modal" && (s.modalType === "viewCategory" || s.modalType === "viewUnit")) return true;
  return false;
}

function enrichSrxState(s, user, sessionName) {
  if (!s || !Object.keys(s).length) return {};
  return {
    ...s,
    _id: s._id || user?._id || null,
    packages: (s.packages && s.packages.length) ? s.packages : (user?.package || []),
    firstName: s.firstName || user?.name?.split?.(" ")?.[0] || sessionName || "",
  };
}

const EMOTION_KEYWORDS = {
  happy:      /\b(happy|joy|excited|cheerful|glad|wonderful|amazing|great|awesome|smile)\b/i,
  sad:        /\b(sad|cry|depressed|unhappy|miserable|lonely|tears)\b/i,
  angry:      /\b(angry|mad|furious|annoyed|frustrated|rage|irritated)\b/i,
  love:       /\b(love|heart|romantic|adore|affection|sweetheart|darling)\b/i,
  suggestive: /\b(wink|flirt|suggestive|tease|playful|naughty)\b/i,
};

function detectEmotion(text) {
  for (const [mood, regex] of Object.entries(EMOTION_KEYWORDS))
    if (regex.test(text)) return mood;
  return null;
}
function formatTime() { return new Date().toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }); }

function parseMemoryImageSrc(data) {
  if (!data) return null;
  const raw = typeof data === "string" ? data.trim() : "";
  if (!raw) return null;
  if (raw.startsWith("data:image")) return raw;
  if (raw.startsWith("{") || raw.startsWith("[")) {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string") return parseMemoryImageSrc(parsed);
      if (parsed?.base64) return `data:image/jpeg;base64,${parsed.base64}`;
      if (parsed?.uri) return parsed.uri;
    } catch (_) {}
  }
  if (/^https?:\/\//i.test(raw)) return raw;
  return `data:image/jpeg;base64,${raw}`;
}

// Active Goals card extractor. view_goal returns TEXT ONLY — no modalType, no
// structured data — so the goals have to be recovered from the message. The
// detection signal is deliberately narrow: a line must contain one of the 12
// known master goal tokens (RELAX, HAVE_FUN, …). Arbitrary replies that merely
// mention "goal" cannot match, and anything unrecognised returns null so the
// reply falls through to the existing plain bubble — the current behaviour.
// Structured data is preferred if the backend ever starts sending it.
function extractActiveGoals(data) {
  if (!data) return null;
  const structured = data.data?.goals || data.data;
  if (Array.isArray(structured) && structured.length) {
    const mapped = structured.map(g => {
      const id = Number(g?.id ?? GOAL_TOKEN_TO_ID[String(g?.name || "").toUpperCase()]);
      return {
        displayName: g?.displayName || GOAL_META[id]?.displayName || g?.name || "Goal",
        description: g?.description || "",
        icon: g?.icon || GOAL_META[id]?.icon || "",
      };
    });
    if (mapped.length) return mapped;
  }
  const text = data.reply || data.message || "";
  if (!text) return null;
  const found = [];
  const seen = new Set();
  // Match the house list format ("1. RELAX - description"), but key off the
  // token rather than the numbering so a reformat does not silently break it.
  for (const line of text.split("\n")) {
    const m = line.match(/\b([A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*)\b/g);
    if (!m) continue;
    for (const token of m) {
      const id = GOAL_TOKEN_TO_ID[token];
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const after = line.split(token)[1] || "";
      const desc = after.replace(/^\s*[-–—:.]\s*/, "").trim();
      found.push({
        displayName: GOAL_META[id]?.displayName || token,
        description: desc,
        icon: GOAL_META[id]?.icon || "",
      });
    }
  }
  return found.length ? found : null;
}

function isWideViewResponse(data) {
  if (!data) return false;
  if (data.type === "askmyllm" || data.type === "wideView") return true;
  return !!(data.wideView && Array.isArray(data.results) && data.results.length);
}

// xShare service packages (menu options appear for users who own any of these)
const XSHARE_PACKAGES = ["FOODLINK", "BLOODSHARE", "BILLBRIDGE", "RESCUEBIDS", "DIGITHER", "EDUCATIONX"];

// How long "Resend code" stays disabled after a code goes out. The mobile app
// shows this countdown but the duration is not recoverable from its bundle —
// this is our own value, pending confirmation, not parity with the app.
const OTP_RESEND_COOLDOWN_SECONDS = 30;

// Catchphrase length, from the client's team: 4–128 measured after trimming, with
// spaces allowed and counted. The gate is authoritative — these exist so the user
// is told before a round trip, not after one. Creation only; login checks
// non-empty and lets bcrypt decide.
const CATCHPHRASE_MIN = 4;
const CATCHPHRASE_MAX = 128;

function playPopSound() {
  try {
    const ctx=new(window.AudioContext||window.webkitAudioContext)();
    const osc=ctx.createOscillator(); const gain=ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(520,ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(320,ctx.currentTime+0.08);
    gain.gain.setValueAtTime(0.08,ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+0.12);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime+0.12);
  } catch(_) {}
}
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return width;
}

// ── SUB-COMPONENTS ─────────────────────────────────────────────────────────────
function ParticleLayer({ theme }) {
  const [items] = useState(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i, symbol: theme.particles[i % theme.particles.length],
      left: `${5+Math.random()*90}%`,
      dur: `${7+Math.random()*10}s`, del: `${-Math.random()*12}s`,
      size: `${10+Math.random()*14}px`, op: 0.15+Math.random()*0.3,
    }))
  );
  return (
    <div style={{ position:"absolute", inset:0, pointerEvents:"none", overflow:"hidden", zIndex:1 }}>
      {items.map(p => (
        <div key={p.id} style={{ position:"absolute", left:p.left, bottom:"-20px", fontSize:p.size, color:theme.particleColor, opacity:p.op, animation:`floatUp ${p.dur} ${p.del} linear infinite` }}>
          {p.symbol}
        </div>
      ))}
    </div>
  );
}

function EmojiReaction({ emoji, onDone }) {
  const [pos] = useState({ x: 40+Math.random()*20, y: 20+Math.random()*20 });
  useEffect(() => { const t=setTimeout(onDone,1600); return ()=>clearTimeout(t); }, [onDone]);
  return (
    <div style={{ position:"absolute", left:`${pos.x}%`, top:`${pos.y}%`, fontSize:"28px", zIndex:20, pointerEvents:"none", animation:"floatEmoji 1.5s ease-out forwards" }}>
      {emoji}
    </div>
  );
}

function SearchResultCard({ result, onOpen }) {
  const { title, url, snippet, thumbnail, videoThumb, external } = result;
  const imgSrc = thumbSrc(thumbnail || videoThumb || null);
  const domain = (() => { try { return new URL(url).hostname.replace('www.', ''); } catch(_) { return ''; } })();
  return (
    <div onClick={() => onOpen && onOpen(url, title, external)}
      style={{ display:"flex", alignItems:"flex-start", gap:"10px", padding:"10px 12px", borderRadius:"10px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", cursor:"pointer", marginBottom:"8px", transition:"background 0.15s ease" }}
      onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.12)"}
      onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,0.06)"}>
      <div style={{ width:"80px", height:"56px", borderRadius:"6px", flexShrink:0, overflow:"hidden", background:"rgba(107,78,170,0.2)", display:"flex", alignItems:"center", justifyContent:"center" }}>
        {imgSrc ? (
          <img src={imgSrc} alt="" style={{width:"100%", height:"100%", objectFit:"cover"}}
            onLoad={e => { if (isEmptyThumb(e.target)) e.target.style.display="none"; }}
            onError={e => { e.target.style.display="none"; e.target.parentNode.innerHTML='<img src="/boticon.png" style="width:28px;height:28px;opacity:0.35;object-fit:contain" alt=""/>'; }} />
        ) : (
          <img src="/boticon.png" style={{width:"28px",height:"28px",opacity:0.35,objectFit:"contain"}} alt="" />
        )}
      </div>
      <div style={{flex:1, minWidth:0}}>
        <div style={{ fontSize:"13px", fontWeight:"700", color:"#fff", lineHeight:"1.3", marginBottom:"3px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{title}</div>
        {snippet && <div style={{ fontSize:"11px", color:"rgba(255,255,255,0.6)", lineHeight:"1.4", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{snippet}</div>}
        {domain && <div style={{ fontSize:"10px", color:"rgba(155,127,212,0.9)", marginTop:"4px", fontWeight:"600" }}>{domain}</div>}
        {external && <div style={{ fontSize:"10px", color:"rgba(255,255,255,0.55)", marginTop:"2px", fontStyle:"italic" }}>This site can't be shown here. It opens in a new tab.</div>}
      </div>
      <div style={{color:"rgba(255,255,255,0.3)", fontSize:"16px", flexShrink:0, alignSelf:"center"}} title={external ? "Opens in a new tab" : "View in panel"}>{external ? "↗" : "›"}</div>
    </div>
  );
}

function ChatBubble({ msg, avatarName, theme, onOpenLink, onEditGoals }) {
  const [vis, setVis] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVis(true), 30); return () => clearTimeout(t); }, []);
  const isSearch  = !!(msg.isSearch && !msg.isUser);
  const isLlm     = !!(msg.isLlm && !msg.isUser);
  const isGoals   = !!(msg.isGoals && !msg.isUser);
  const isWide    = isSearch || isLlm || isGoals;
  const imageSrc  = msg.isImage ? (msg.imageSrc || parseMemoryImageSrc(msg.text)) : null;
  const displayed = imageSrc ? "" : (msg.text || "");
  function renderText(text) {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) => {
      if (urlRegex.test(part)) {
        urlRegex.lastIndex = 0;
        return <span key={i} onClick={() => onOpenLink && onOpenLink(part, part)} style={{color:"#93c5fd", textDecoration:"underline", wordBreak:"break-all", cursor:"pointer"}}>{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  }
  const containerStyle = {
    display: "flex", flexDirection: "column", gap: "4px",
    width: isWide ? "100%" : undefined, maxWidth: isWide ? "100%" : "580px",
    opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(14px)",
    transition: "opacity 0.3s ease,transform 0.3s ease",
  };
  const bubbleStyle = isWide ? {
    width: "100%", padding: "14px 18px", fontSize: "14px", lineHeight: "1.6", borderRadius: "12px",
    background: theme.bubbleBot, color: "#fff", boxShadow: `0 4px 18px ${theme.glowColor}`,
  } : msg.isUser ? {
    maxWidth: "92%", padding: "12px 18px", fontSize: "14px", lineHeight: "1.6", whiteSpace: "pre-wrap",
    borderRadius: "20px 20px 4px 20px", background: theme.bubbleUser, backdropFilter: "blur(8px)",
    color: theme.textPrimary, alignSelf: "flex-end", border: `1px solid ${theme.bubbleUserBorder}`,
    boxShadow: `0 2px 10px ${theme.glowColor}`,
  } : {
    maxWidth: "92%", padding: "12px 18px", fontSize: "14px", lineHeight: "1.6", whiteSpace: "pre-wrap",
    borderRadius: "20px 20px 20px 4px", background: theme.bubbleBot, color: "#fff", alignSelf: "flex-start",
    boxShadow: `0 4px 18px ${theme.glowColor}`,
  };
  // Item I: system/error notice — a connection failure is NOT avatar speech, so it renders
  // centred and unattributed (no avatar name), and is never passed to TTS.
  if (msg.isSystem) {
    return (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", alignSelf:"center", maxWidth:"92%", margin:"2px 0" }}>
        <div style={{
          padding:"8px 14px", fontSize:"12.5px", lineHeight:"1.5", textAlign:"center",
          borderRadius:"10px", background:"rgba(239,68,68,0.10)", color:"#ef4444",
          border:"1px solid rgba(239,68,68,0.35)", whiteSpace:"pre-wrap",
        }}>
          ⚠️ {msg.text}
        </div>
        <span style={{ fontSize:"10px", color:theme.textMeta, marginTop:"3px" }}>System · {msg.time}</span>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={bubbleStyle}>
        {isLlm && (
          <div style={{ marginBottom:"10px" }}>
            <span style={{ fontSize:"11px", fontWeight:"700", letterSpacing:"0.5px", color:"#7dd3fc", background:"rgba(125,211,252,0.15)", border:"1px solid rgba(125,211,252,0.4)", borderRadius:"6px", padding:"3px 9px" }}>{msg.llmLabel || "ASKMYLLM"}</span>
          </div>
        )}
        {msg.isUser && msg.isImage && imageSrc ? (
          <img src={imageSrc} alt="Uploaded memory" style={{ maxWidth:"100%", maxHeight:"220px", borderRadius:"12px", objectFit:"cover" }} />
        ) : msg.isUser ? msg.text : (
          <>
            {!msg.isUser && imageSrc && (
              <img src={imageSrc} alt="Recalled memory" style={{ maxWidth:"100%", maxHeight:"240px", borderRadius:"12px", objectFit:"cover", marginBottom: msg.text ? "10px" : 0 }} />
            )}
            {renderText(displayed)}
          </>
        )}
        {isGoals && msg.goals && msg.goals.length > 0 && (
          <div style={{marginTop:"14px"}}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"10px" }}>
              <span style={{ fontSize:"13px", fontWeight:"700", color:"#fff" }}>Your Active Goals</span>
              <span style={{ fontSize:"11px", fontWeight:"700", color:"#D4ADFF", background:"rgba(155,127,212,0.22)", border:"1px solid rgba(155,127,212,0.45)", borderRadius:"10px", padding:"1px 8px" }}>{msg.goals.length}</span>
              <span style={{flex:1}}/>
              <button onClick={() => onEditGoals && onEditGoals()}
                style={{ border:"1px solid rgba(155,127,212,0.6)", background:"rgba(107,78,170,0.35)", color:"#D4ADFF", fontSize:"11px", fontWeight:"700", borderRadius:"14px", padding:"4px 12px", cursor:"pointer" }}>Edit</button>
            </div>
            {/* Rendered in the order received — the backend returns the user's
                own priority order, so no sort is applied anywhere. */}
            {msg.goals.map((g, i) => (
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:"10px", padding:"10px 12px", borderRadius:"10px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", marginBottom:"8px" }}>
                <span style={{ fontSize:"11px", fontWeight:"700", color:"#D4ADFF", minWidth:"14px" }}>{i + 1}</span>
                {g.icon && <span style={{ fontSize:"16px", lineHeight:1.2 }}>{g.icon}</span>}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:"13px", fontWeight:"700", color:"#fff", lineHeight:"1.3" }}>{g.displayName}</div>
                  {g.description && <div style={{ fontSize:"11px", color:"rgba(255,255,255,0.6)", lineHeight:"1.4", marginTop:"2px" }}>{g.description}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
        {isSearch && msg.results && msg.results.length > 0 && (
          <div style={{marginTop:"14px"}}>
            {msg.searchQuery && (
              <div style={{ fontSize:"11px", fontWeight:"700", color:"rgba(255,255,255,0.55)", marginBottom:"8px", letterSpacing:"0.3px" }}>
                RESULTS FOR &ldquo;{msg.searchQuery}&rdquo;
              </div>
            )}
            {msg.results.map((r, i) => <SearchResultCard key={i} result={r} onOpen={onOpenLink} />)}
            <div style={{ marginTop:"6px" }}>
              <span style={{ fontSize:"10px", color:"rgba(255,255,255,0.45)" }}>Tap a result to preview on this screen</span>
            </div>
          </div>
        )}
        {/* ASK (Round 10): files the LLM made, then the sources it searched. */}
        {!msg.isUser && Array.isArray(msg.askFiles) && msg.askFiles.length > 0 && (
          <div style={{ marginTop:"12px", display:"flex", flexDirection:"column", gap:"8px" }}>
            {msg.askFiles.map((f, i) => (
              <a key={i} href={askFileUrl(f.link)} download={f.name} target="_blank" rel="noopener noreferrer"
                style={{ display:"flex", alignItems:"center", gap:"10px", padding:"10px 12px", borderRadius:"10px", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.18)", color:"#fff", textDecoration:"none" }}>
                <span style={{ fontSize:"20px" }}>📄</span>
                <span style={{ flex:1, minWidth:0 }}>
                  <span style={{ display:"block", fontSize:"13px", fontWeight:"700", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                  <span style={{ fontSize:"11px", color:"rgba(255,255,255,0.6)" }}>{fileSizeLabel(f.size)} · Download</span>
                </span>
                <span style={{ fontSize:"16px" }}>⬇</span>
              </a>
            ))}
          </div>
        )}
        {!msg.isUser && Array.isArray(msg.askSources) && msg.askSources.length > 0 && (
          <div style={{ marginTop:"12px" }}>
            <div style={{ fontSize:"11px", fontWeight:"700", color:"rgba(255,255,255,0.55)", marginBottom:"6px", letterSpacing:"0.3px" }}>SOURCES</div>
            {msg.askSources.slice(0, 5).map((s, i) => (
              <div key={i} style={{ fontSize:"12px", lineHeight:"1.5", marginBottom:"4px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                <span style={{ color:"rgba(255,255,255,0.55)" }}>{i + 1}. </span>
                <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color:"#7dd3fc" }}>{s.title || s.url}</a>
              </div>
            ))}
          </div>
        )}
      </div>
      <span style={{ fontSize:"10px", color:theme.textMeta, alignSelf:msg.isUser?"flex-end":"flex-start", padding:msg.isUser?"0 6px 0 0":"0 0 0 6px" }}>
        {msg.isUser ? "You" : avatarName} · {msg.time}
      </span>
    </div>
  );
}

function TypingIndicator({ theme }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"6px", padding:"10px 16px", borderRadius:"20px 20px 20px 4px", background:theme.bubbleBot, alignSelf:"flex-start", maxWidth:"80px", boxShadow:`0 4px 18px ${theme.glowColor}` }}>
      {[0,1,2].map(i => <div key={i} style={{ width:"7px", height:"7px", borderRadius:"50%", background:"rgba(255,255,255,0.8)", animation:`typingDot 1.2s ${i*0.2}s ease-in-out infinite` }} />)}
    </div>
  );
}

function ViewTodoModal({ theme, items, onClose, onEdit }) {
  // The shared list style (components/listStyle.js), like checklists and the shopping list.
  return (
    <div style={LIST.backdrop} onClick={onClose}>
      <div role="dialog" aria-label="Todo List" style={LIST.card} onClick={e=>e.stopPropagation()}>
        <button type="button" onClick={onClose} aria-label="Close" style={LIST.close}>✕</button>
        <div style={LIST.title}>📋 Todo List</div>
        {(items||[]).length===0 ? (
          <p style={LIST.empty}>No tasks yet.</p>
        ) : (
          <ul style={LIST.list}>
            {(items||[]).map((item,i) => (
              <li key={i} style={{ ...LIST.row, ...(item.isEmergency ? LIST.rowUrgent : {}) }}>
                <span style={{ ...LIST.label, color:item.isEmergency?"#b91c1c":"#111" }}>{i+1}. {item.task}</span>
                {item.isEmergency && <span style={{ fontSize:"10px", background:"#ef4444", color:"#fff", padding:"2px 6px", borderRadius:"8px" }}>URGENT</span>}
              </li>
            ))}
          </ul>
        )}
        {onEdit && <EditListButton onClick={onEdit} />}
      </div>
    </div>
  );
}

function UpdateTodoModal({ theme, items, onClose, onSave }) {
  // Same order as the view (emergencies first, each group oldest first), kept
  // after every add or edit; rows are numbered 1..n in that order (26 Oct).
  const [todoItems, setTodoItems] = React.useState(()=>sortTodos(items||[]));
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [editIndex, setEditIndex] = React.useState(-1);
  const [task, setTask] = React.useState("");
  const [place, setPlace] = React.useState("");
  const [isEmergency, setIsEmergency] = React.useState(false);
  const resetForm = () => { setTask(""); setPlace(""); setIsEmergency(false); setShowAddForm(false); setEditIndex(-1); };
  const [formError, setFormError] = React.useState("");
  const handleSave = async () => {
    // Task and place go through the same prefilter as chat.
    if (FIELD_PREFILTER_ENABLED) {
      const check = await screenFields([["task", task], ["place", place]]);
      if (!check.ok) { setFormError(check.message); return; }
    }
    setFormError("");
    if (editIndex>=0) {
      const updated=[...todoItems]; updated[editIndex]={...updated[editIndex],task,place,isEmergency};
      setTodoItems(sortTodos(updated)); setEditIndex(-1);
    } else {
      // A new emergency goes after the existing emergencies (oldest first), with its date (23 Oct).
      const added={task,place,isEmergency,delete:false,dateAdded:new Date().toISOString()};
      setTodoItems(p => sortTodos([...p,added]));
    }
    resetForm();
  };
  if (showAddForm||editIndex>=0) {
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:300, backdropFilter:"blur(8px)" }}>
        <div style={{ background:theme.headerBg, border:`1px solid ${theme.sectionBorder}`, borderRadius:"16px", padding:"24px", width:"min(400px,94vw)", display:"flex", flexDirection:"column", gap:"16px", boxShadow:`0 12px 48px ${theme.glowColor}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:"16px", fontWeight:"800", color:theme.pill }}>{editIndex>=0?"✏️ Edit":"➕ Add"} Todo</span>
            <button onClick={()=>{resetForm();onClose();}} style={{ border:"none", background:"transparent", color:theme.textMeta, cursor:"pointer", fontSize:"20px" }}>✕</button>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
            <label style={{ fontSize:"12px", fontWeight:"700", color:theme.primaryLight }}>TASK</label>
            <input value={task} onChange={e=>setTask(e.target.value)} placeholder="Enter task..."
              style={{ padding:"10px 14px", borderRadius:"10px", border:`1.5px solid ${theme.inputBorder}`, background:theme.inputBg, color:theme.textPrimary, fontSize:"13px", outline:"none" }} />
          </div>
          {/* Edit Todo has the Place field too ("none" = home, "online" = no place). */}
          {(
            <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
              <label style={{ fontSize:"12px", fontWeight:"700", color:theme.primaryLight }}>PLACE (optional)</label>
              <input value={place} onChange={e=>setPlace(e.target.value)} placeholder="Where? (none = home)"
                style={{ padding:"10px 14px", borderRadius:"10px", border:`1.5px solid ${theme.inputBorder}`, background:theme.inputBg, color:theme.textPrimary, fontSize:"13px", outline:"none" }} />
            </div>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
            <label style={{ fontSize:"12px", fontWeight:"700", color:theme.primaryLight }}>EMERGENCY</label>
            <button onClick={()=>setIsEmergency(p=>!p)}
              style={{ padding:"6px 14px", borderRadius:"8px", border:"none", cursor:"pointer", fontSize:"12px", fontWeight:"700", background:isEmergency?"#ef4444":"rgba(239,68,68,0.1)", color:isEmergency?"#fff":"#ef4444" }}>
              {isEmergency?"✅ YES":"NO"}
            </button>
          </div>
          {formError && <div role="alert" style={{ color:"#c00", fontSize:"13px" }}>{formError}</div>}
          <button onClick={handleSave} disabled={!task.trim()}
            style={{ padding:"12px", borderRadius:"10px", border:"none", background:`linear-gradient(135deg,${theme.primary},${theme.primaryLight})`, color:"#fff", fontSize:"14px", fontWeight:"700", cursor:task.trim()?"pointer":"not-allowed", opacity:task.trim()?1:0.5 }}>
            Save
          </button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:300, backdropFilter:"blur(8px)" }} onClick={onClose}>
      <div style={{ background:theme.headerBg, border:`1px solid ${theme.sectionBorder}`, borderRadius:"16px", padding:"24px", width:"min(400px,94vw)", maxHeight:"75vh", display:"flex", flexDirection:"column", gap:"12px", boxShadow:`0 12px 48px ${theme.glowColor}` }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:"16px", fontWeight:"800", color:theme.pill }}>📋 Update Todo List</span>
          <button onClick={onClose} style={{ border:"none", background:"transparent", color:theme.textMeta, cursor:"pointer", fontSize:"20px" }}>✕</button>
        </div>
        <div style={{ overflowY:"auto", flex:1, display:"flex", flexDirection:"column", gap:"8px" }}>
          {todoItems.map((item,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:"10px", padding:"10px 14px", background:theme.inputBg, borderRadius:"10px", border:`1px solid ${item.isEmergency?"#ef4444":theme.sectionBorder}`, opacity:item.delete?0.4:1 }}>
              <input type="checkbox" checked={!item.delete} onChange={()=>{const u=[...todoItems];u[i]={...u[i],delete:!u[i].delete};setTodoItems(u);}} style={{ width:"16px", height:"16px", cursor:"pointer" }} />
              <span onClick={()=>{setEditIndex(i);setTask(item.task);setPlace(item.place||"");setIsEmergency(item.isEmergency||false);}}
                style={{ flex:1, fontSize:"13px", color:item.isEmergency?"#ef4444":theme.textPrimary, cursor:"pointer", fontWeight:item.isEmergency?"700":"400", textDecoration:item.delete?"line-through":"none" }}>
                {i+1}. {item.task}
              </span>
              {item.isEmergency && <span style={{ fontSize:"10px", background:"#ef4444", color:"#fff", padding:"2px 6px", borderRadius:"8px" }}>URGENT</span>}
            </div>
          ))}
        </div>
        <div style={{ display:"flex", gap:"10px" }}>
          <button onClick={()=>onSave(todoItems)} style={{ flex:1, padding:"11px", borderRadius:"10px", border:"none", background:`linear-gradient(135deg,${theme.primary},${theme.primaryLight})`, color:"#fff", fontSize:"13px", fontWeight:"700", cursor:"pointer" }}>Save</button>
          <button onClick={()=>setShowAddForm(true)} style={{ flex:1, padding:"11px", borderRadius:"10px", border:`1.5px solid ${theme.pillBorder}`, background:"transparent", color:theme.pill, fontSize:"13px", fontWeight:"700", cursor:"pointer" }}>Add</button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [botState, setBotState]                       = useState({ avatarType:"3D", emotion:"neutral" });
  // The mode right now, for a reply sent straight after sign-in changes it (botState is a render behind).
  const avatarTypeRef = useRef(botState.avatarType); avatarTypeRef.current = botState.avatarType;
  const [activeAvatar, setActiveAvatar]               = useState("Camille");
  const [activeEmotion, setActiveEmotion]             = useState("neutral");
  const [activeBgKey, setActiveBgKey]                 = useState("default");
  const [messages, setMessages]                       = useState([]);
  const [inputText, setInputText]                     = useState("");
  const [menuOpen, setMenuOpen]                       = useState(false);
  const [inputFocused, setInputFocused]               = useState(false);
  const [parallax, setParallax]                       = useState({ x:0, y:0 });
  const [flash, setFlash]                             = useState(false);
  const [isTyping, setIsTyping]                       = useState(false);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [emojiReactions, setEmojiReactions]           = useState([]);
  const [listening, setListening]                     = useState(false);
  const [pageVisible, setPageVisible]                 = useState(false);
  const [avatarReady, setAvatarReady]                 = useState(false);
  const [engineReady, setEngineReady]                 = useState(false);
  const [avatarLoading, setAvatarLoading]             = useState(false);
  const [weatherMood, setWeatherMood]                 = useState(null);
  // WeatherBOT card: { city, temp, unit, icon, description, rainChance, aqi } from /api/weather.
  const [weather, setWeather]                         = useState(null);
  const weatherCoordsRef                              = useRef(null);
  // °C/°F chosen on the weather card, remembered per person on this browser.
  const [weatherUnit, setWeatherUnit]                 = useState(null);
  const [showCitiesGallery, setShowCitiesGallery]     = useState(false);
  const [showScenesGallery, setShowScenesGallery]     = useState(false);
  const [galleryBg, setGalleryBg]                     = useState(null);
  const [galleryType, setGalleryType]                 = useState(null);
  const [currentUser, setCurrentUser]                 = useState(null);
  const [loginStep, setLoginStep]                     = useState(null);
  const loginStepRef = useRef(null);
  const setStep = (val) => { loginStepRef.current = val; setLoginStep(val); };
  const [greetingAskedName, setGreetingAskedName]     = useState(true);
  const [sessionFirstName, setSessionFirstName]       = useState("");
  const [showYesNo, setShowYesNo]                     = useState(false);
  const [chatError, setChatError]                     = useState(false);
  const [srx, setSrx]                                 = useState({});
  const srxRef                                        = useRef({});
  // Trunk routes any intent starting with "ask" to ASKMYLLM but tags the answer
  // only as type:"result" — so the browser tracks the exchange to render it wide.
  const askLlmActiveRef                               = useRef(false);
  // Set after "What would you like me to search for?": the next message is the query.
  const pendingSearchRef                              = useRef(false);
  const [ledgerActive, setLedgerActive]               = useState(false);
  const [ledgerStep, setLedgerStep]                   = useState(0);
  const [ledgerEntry, setLedgerEntry]                 = useState({ date: "", activity: {}, food: {} });
  const [ledgerEmotions, setLedgerEmotions]           = useState([]);
  const [showFeelingPicker, setShowFeelingPicker]       = useState(false);
  const [showBotsPanel, setShowBotsPanel]             = useState(false);
  const [showBotStore, setShowBotStore]               = useState(false);
  const [botStoreZone, setBotStoreZone]               = useState("A"); // FIX 11: zone tabs
  const [selectedCategory, setSelectedCategory]       = useState(null); // Sprint 4B: Zone A drill-down
  const [botStoreView, setBotStoreView]               = useState("browse"); // browse | cart | success
  const [botStoreSuccessMsg, setBotStoreSuccessMsg]   = useState("");
  const botStoreBaselineRef                           = useRef([]);
  const [showViewSA, setShowViewSA]                   = useState(false);
  const [showUpdateSA, setShowUpdateSA]               = useState(false);
  const [showPurchaseSA, setShowPurchaseSA]           = useState(false);
  const [saModalMode, setSaModalMode]                 = useState("update");
  const [purchaseItems, setPurchaseItems]             = useState([]);
  const [showCategoryModal, setShowCategoryModal]     = useState(false);
  const [showUnitModal, setShowUnitModal]             = useState(false);
  const [saModalData, setSaModalData]                 = useState([]);
  const [packages, setPackages]                       = useState([]);
  const [showShopSavvy, setShowShopSavvy]             = useState(false);
  const [showViewTodoModal, setShowViewTodoModal]     = useState(false);
  const [showUpdateTodoModal, setShowUpdateTodoModal] = useState(false);
  const [todoModalData, setTodoModalData]             = useState([]);
  function avatarGreetingEmotion() {
    return (activeAvatar === "Camille" || activeAvatar === "Margie") && botState.avatarType === "2D"
      ? "neutral" : "happy";
  }

  function avatarAppearanceEmotion(mood) {
    if ((activeAvatar === "Camille" || activeAvatar === "Margie") && botState.avatarType === "2D") {
      return "neutral";
    }
    return mood || "neutral";
  }

  // Live verification gate: { firstName, sessionToken, verificationToken }. The
  // tokens are opaque and short-lived, so they stay in memory only.
  const verifyRef = useRef(null);
  // Final email step once the gate recognises the account:
  // { stage: "email" | "code", email, maskedEmail, busy, error }.
  const [otpModal, setOtpModal] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  // Seconds left before "Resend code" is live again. The shipped app shows a
  // countdown here; the length of it is not in the bundle, so it lives in
  // OTP_RESEND_COOLDOWN_SECONDS until Paul confirms the real value.
  const [otpResendIn, setOtpResendIn] = useState(0);
  // Offered once, after access is already granted, and only on the email branch —
  // that branch is reached precisely when the account has no catchphrase on file.
  const [catchPhraseOffer, setCatchPhraseOffer] = useState(null);
  const googleBtnRef = useRef(null);
  // Read at submit time so an autofilled address is seen even if onChange never fired.
  const otpEmailRef = useRef(null);
  const photoInputRef = useRef(null);
  // ASK attachments (Round 10): uploaded files waiting for the next ASK request.
  const askFileInputRef = useRef(null);
  const [askAttachments, setAskAttachments] = useState([]);
  const [askUploading, setAskUploading] = useState(false);
  const [showLinkModal, setShowLinkModal]             = useState(false); // FIX 10
  const [linkModalUrl, setLinkModalUrl]               = useState("");
  const [linkModalTitle, setLinkModalTitle]           = useState("");
  const [showPostNeedModal, setShowPostNeedModal]     = useState(false);
  const [infoModal, setInfoModal]                     = useState(null); // viewChecklist | educationResources | safetyContacts
  const [catchphrasePopup, setCatchphrasePopup]       = useState(null); // { busy, error } while the pop-up is open
  const [checklistModal, setChecklistModal]           = useState(null); // { kind: checklistPicker | checklistView, data }
  const [mealModal, setMealModal]                     = useState(null); // { description } while the meal breakdown is open
  const [postNeedPreset, setPostNeedPreset]           = useState("");
  const [showScheduleModal, setShowScheduleModal]     = useState(false);
  const [showGoalModal, setShowGoalModal]             = useState(false);
  // "change my security questions": the checkbox form (null when closed).
  const [secqForm, setSecqForm]                       = useState(null);
  const [secqBusy, setSecqBusy]                       = useState(false);
  // Show / hide a masked answer typed in the chat input (hidden again on every new step).
  const [showSecret, setShowSecret]                   = useState(false);
  useEffect(() => { setShowSecret(false); }, [loginStep, srx?.pendingAction]);
  const [goalModalData, setGoalModalData]             = useState({ goals: [], selectedGoals: [] });
  const [showNeedsScreen, setShowNeedsScreen]         = useState(false);
  const [needsScreenMode, setNeedsScreenMode]         = useState("activeNeeds");
  const [needsTypeFilter, setNeedsTypeFilter]         = useState("");
  const [ledgerPrompt, setLedgerPrompt]               = useState(null);
  const pendingLedgerFollowUpRef                      = useRef(null);

  const chatEndRef     = useRef(null);
  const headEngineRef  = useRef(null);
  const avatarRef      = useRef(null);
  const typingRef      = useRef(null);
  const recognitionRef = useRef(null);

  const theme       = BG_THEMES[activeBgKey] || BG_THEMES.default;
  const winWidth    = useWindowWidth();
  const isMobile    = winWidth <= 480;
  const botStoreChanges = computePackageChanges(packages, botStoreBaselineRef.current || []);
  const hasBotStoreChanges = botStoreChanges.subscribe.length > 0 || botStoreChanges.unsubscribe.length > 0;
  const botStoreMonthlyPrice = calcMonthlyPrice(packages).toFixed(2);
  const [storeCatalog, setStoreCatalog]               = useState(null);
  useEffect(() => {
    // Signed in too: the icon bar shows the icon this person chose.
    if ((!showBotStore && !currentUser) || !STORE_FROM_DB_ENABLED || storeCatalog) return;
    let alive = true;
    loadStoreCatalog().then((c) => { if (alive && c) { STORE_CATALOG = c; setStoreCatalog(c); } });
    return () => { alive = false; };
  }, [showBotStore, storeCatalog, currentUser]);
  const isTablet    = winWidth > 480 && winWidth <= 768;
  const isSmall     = winWidth <= 768;
  const activeBgImage = galleryBg ? galleryBg.file : (theme.bgImage || null);

  // ── Global CSS ──────────────────────────────────────────────────────────────
  useEffect(() => {
    registerController(setBotState);
    window.controlBotcierge = controlBotcierge;
    const el = document.createElement("style");
    el.id = "bc-global";
    el.innerHTML = `
      *{box-sizing:border-box;margin:0;padding:0;}
      html,body,#root{height:100%;width:100%;}
      body{background:#000;}
      @keyframes floatUp{0%{transform:translateY(0) rotate(0deg);opacity:0;}8%{opacity:1;}92%{opacity:0.7;}100%{transform:translateY(-105vh) rotate(360deg);opacity:0;}}
      @keyframes pulse{0%,100%{opacity:1;transform:scale(1);}50%{opacity:0.45;transform:scale(1.35);}}
      @keyframes slideInMenu{from{transform:translateX(-100%);}to{transform:translateX(0);}}
      @keyframes flashIn{0%{opacity:0.7;}100%{opacity:0;}}
      @keyframes floatEmoji{0%{opacity:1;transform:translateY(0) scale(1);}100%{opacity:0;transform:translateY(-80px) scale(1.5);}}
      @keyframes typingDot{0%,60%,100%{transform:translateY(0);}30%{transform:translateY(-6px);}}
      @keyframes spin{to{transform:rotate(360deg);}}
      @keyframes micPulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.5);}50%{box-shadow:0 0 0 10px rgba(239,68,68,0);}}
      @keyframes splashSpin{to{transform:rotate(360deg);}}
      @keyframes splashSpinReverse{to{transform:rotate(-360deg);}}
      @keyframes splashPulse{0%,100%{transform:scale(1);}50%{transform:scale(1.1);}}
      @keyframes splashDot{0%,80%,100%{transform:scale(0.6);opacity:0.4;}40%{transform:scale(1);opacity:1;}}
      .hov-pill:hover{opacity:.85;transform:scale(1.05)!important;}
      .hov-send:hover{transform:scale(1.1)!important;}
      .hov-bg:hover{transform:scale(1.2)!important;}
      .hov-em:hover{transform:scale(1.06)!important;filter:brightness(1.15);}
      .emotion-bar::-webkit-scrollbar{display:none;}
      .emotion-bar{-ms-overflow-style:none;scrollbar-width:none;}
      ::-webkit-scrollbar{width:3px;}
      ::-webkit-scrollbar-thumb{border-radius:4px;background:rgba(180,160,220,0.3);}
    `;
    document.head.appendChild(el);
    setTimeout(() => setPageVisible(true), 100);
    return () => { const s=document.getElementById("bc-global"); if(s) s.remove(); };
  }, []);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(async (pos) => {
      try {
        const { latitude:lat, longitude:lon } = pos.coords;
        // Same fix already gathered for Camille's weather mood — hand it to the
        // controller so every chat payload can carry it for FlowPlanner. The
        // setter validates; a denied prompt never reaches here and location
        // stays omitted from the payload.
        setUserLocation({ latitude:lat, longitude:lon });
        weatherCoordsRef.current = { lat, lon };
        loadWeather();
      } catch(_) {}
    }, ()=>{ loadWeather(); }, { timeout:5000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on load; loadWeather reads refs
  }, []);

  // WeatherBOT card (MBTS GET /weather through /api/weather). Signed in, the
  // server uses the person's current location, then this GPS, then their home
  // city; a guest gets the GPS city (no GPS: no card). Camille's mood follows
  // the condition.
  async function loadWeather(fresh = false) {
    try {
      const c = weatherCoordsRef.current;
      if (!canAskWeather(c, currentUser)) { setWeather(null); return; }
      // fresh: the person's place just changed, so skip the browser's 10-minute copy.
      const res = await fetch(c ? `/api/weather?lat=${c.lat}&lng=${c.lon}` : "/api/weather", fresh ? { cache: "no-store" } : undefined);
      if (!res.ok) { if (!c) setWeather(null); return; }
      const data = await res.json();
      if (!data?.condition) return;
      setWeather(data);
      setWeatherMood(data.condition==="sunny"||data.condition==="clear" ? "happy" : data.condition==="rain"||data.condition==="snow" ? "sad" : data.condition==="storm" ? "angry" : "neutral");
    } catch(_) {}
  }
  const weatherUnitKey = () => `weatherUnit:${currentUser?._id || "guest"}`;
  useEffect(() => {
    try { const u = localStorage.getItem(weatherUnitKey()); setWeatherUnit(u === "C" || u === "F" ? u : null); } catch(_) { setWeatherUnit(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the key follows the person
  }, [currentUser?._id]);
  const chooseWeatherUnit = (u) => { setWeatherUnit(u); try { localStorage.setItem(weatherUnitKey(), u); } catch(_) {} };
  // Signing in or out changes whose place the card shows.
  const weatherUserRef = useRef(undefined);
  useEffect(() => {
    const id = currentUser?._id || null;
    if (weatherUserRef.current === undefined) { weatherUserRef.current = id; return; }
    if (weatherUserRef.current === id) return;
    weatherUserRef.current = id;
    loadWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on sign-in/out
  }, [currentUser?._id]);

  useEffect(() => {
    window._wordRevealAborted = true;
    const isMenuSwitch = !!window._menuAvatarSwitch;
    // Menu/2D switches use the in-avatar loader only — full-screen overlay blocks chat forever if onReady never fires.
    let loadGuard = null;
    if (isMenuSwitch || botState.avatarType === "2D") {
      setAvatarLoading(false);
    } else {
      setAvatarLoading(true);
      loadGuard = setTimeout(() => setAvatarLoading(false), 90000);
    }
    killAllAudio();
    try {
      setTimeout(() => {
        try {
          const gm=window.currentAvatarHead?.gainMaster;
          const ctx=window.currentAvatarHead?.audioCtx;
          if (gm && ctx?.state==="running") gm.gain.setValueAtTime(1, ctx.currentTime);
        } catch(_) {}
      }, 100);
    } catch(_) {}
    if (window._bowOutInProgress) {
      window._bowOutInProgress = false;
      // FIX: preserve auth — use window._bowOutUser so currentUser closure is always fresh
      const bowUser = window._bowOutUser || currentUser;
      window._bowOutUser = null;
      if (bowUser) {
        const firstName = bowUser.name.split(" ")[0];
        const greeting  = `Hi ${firstName}! I'm ${activeAvatar}. How may I assist you?`;
        window._pendingGreeting = greeting;
        window._greetingDone   = false;
        setGreetingAskedName(false);
        setSessionFirstName(firstName);
        // Keep user logged in — restore currentUser if it got wiped
        setCurrentUser(u => u || bowUser);
        // FIX (Biz): show the takeover greeting bubble immediately so the chat
        // is never a blank screen while the new avatar's GLB loads. The avatar
        // will still speak the greeting once ready (via _pendingGreeting).
        setMessages([{ text: greeting, time: formatTime() }]);
      }
      return () => { if (loadGuard) clearTimeout(loadGuard); };
    }
    window.currentAvatar = activeAvatar;
    if (window._menuAvatarSwitch) {
      window._menuAvatarSwitch = false;
      setAvatarLoading(false);
      setMessages(prev => prev.map((msg, i) => {
        if (i === 0 && !msg.isUser && msg.text) {
          const updated = msg.text
            .replace(/Hi\s+[^!]+!\s+I'm\s+\w+/gi, (m) => m.replace(/I'm\s+\w+/i, `I'm ${activeAvatar}`))
            .replace(/Hi[,!]?\s+I'm\s+\w+/gi, `Hi! I'm ${activeAvatar}`)
            .replace(/I'm\s+\w+\.\s+How/gi, `I'm ${activeAvatar}. How`);
          return { ...msg, text: updated };
        }
        return msg;
      }));
      // The new avatar says hello with its wave (22 Oct; it used to stay silent).
      // The conversation stays: _greetingVoiceOnly stops onReady replacing it.
      const nameNow = currentUser ? currentUser.name.split(" ")[0] : sessionFirstName;
      window._pendingGreeting = nameNow ? `Hi ${nameNow}! I'm ${activeAvatar}. How may I assist you?` : `Hi! I'm ${activeAvatar}. What is your name?`;
      window._greetingVoiceOnly = botState.avatarType === "3D";
      window._greetingDone = botState.avatarType !== "3D";
      if (!window._greetingVoiceOnly) window._pendingGreeting = null;
      return () => { if (loadGuard) clearTimeout(loadGuard); };
    }
    if (currentUser) {
      const greeting = `Hi ${currentUser.name}! Welcome to the BOTCIERGE® experience. How may I assist you?`;
      setMessages([]);
      setGreetingAskedName(false);
      window._pendingGreeting = greeting;
    } else {
      setMessages([]);
      window._pendingGreeting = `Hi! I'm ${activeAvatar}. What is your name?`;
      setGreetingAskedName(true);
    }
    // The new avatar greets: clear the previous avatar's "greeting done", or
    // ThreeDAvatar skips the voice and only the bubble shows (22 Oct).
    window._greetingDone = false;
    window._greetingVoiceOnly = false;
    setStep(null);
    setShowYesNo(false);
    setSessionFirstName(currentUser ? currentUser.name.split(" ")[0] : "");
    verifyRef.current = null;
    if (weatherMood) {
      setTimeout(() => {
        const mood = avatarAppearanceEmotion(weatherMood);
        setActiveEmotion(mood);
        controlBotcierge({ avatarType: botState.avatarType, emotion: mood });
      }, 2000);
    }
    return () => { if (loadGuard) clearTimeout(loadGuard); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on avatar/weather change; currentUser/botState read intentionally from closure
  }, [activeAvatar, weatherMood]);

  useEffect(() => {
    const el=avatarRef.current; if(!el) return;
    const move  = (e) => { const r=el.getBoundingClientRect(); setParallax({ x:((e.clientX-r.left-r.width/2)/r.width)*20, y:((e.clientY-r.top-r.height/2)/r.height)*10 }); };
    const leave = () => setParallax({x:0,y:0});
    el.addEventListener("mousemove",move); el.addEventListener("mouseleave",leave);
    return () => { el.removeEventListener("mousemove",move); el.removeEventListener("mouseleave",leave); };
  }, []);
  useEffect(() => { srxRef.current = srx; }, [srx]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages, showTypingIndicator]);
  useEffect(() => {
    if ((activeAvatar === "Camille" || activeAvatar === "Margie") && botState.avatarType === "2D") {
      setActiveEmotion("neutral");
    }
  }, [activeAvatar, botState.avatarType]);

  useEffect(() => { setActiveEmotion(botState.emotion); }, [botState.emotion]);
  useEffect(() => {
    if (botState.avatarType==="2D") {
      setAvatarLoading(false);
      try {
        const gm=window.currentAvatarHead?.gainMaster;
        const ctx=window.currentAvatarHead?.audioCtx;
        if (gm && ctx && ctx.state==="running") {
          gm.gain.cancelScheduledValues(ctx.currentTime);
          gm.gain.setTargetAtTime(0,ctx.currentTime,0.05);
          setTimeout(()=>{ try{gm.gain.setTargetAtTime(1,ctx.currentTime,0.1);}catch(_){} },200);
        }
        window._ttsPlaying = false;
      } catch(_) {}
    } else {
      if (window.camille2DStop) window.camille2DStop();
    }
  }, [botState.avatarType]);

  const toggleVoice = useCallback(() => {
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if (!SR) { alert("Speech recognition not supported. Try Chrome!"); return; }
    if (listening) { recognitionRef.current?.stop(); setListening(false); return; }
    const rec=new SR();
    rec.continuous=false; rec.interimResults=false; rec.lang="en-US";
    rec.onresult=(e)=>{ setInputText(e.results[0][0].transcript); };
    rec.onend=()=>setListening(false); rec.onerror=()=>setListening(false);
    recognitionRef.current=rec; rec.start(); setListening(true);
  }, [listening]);

  function switchBg(key) { setFlash(true); setTimeout(()=>{ setActiveBgKey(key); setFlash(false); },220); }
  function handleEmotionClick(mood) {
    setActiveEmotion(mood);
    controlBotcierge({ avatarType:botState.avatarType, emotion:mood });
    const id=Date.now();
    setEmojiReactions(prev=>[...prev,{id,emoji:EMOTION_EMOJIS[mood]}]);
  }
  // TASK 4: schedule the upsell wink so it lands just AFTER the spoken word "Purchase".
  // 3D: ThreeDAvatar reads the real Kokoro word timings (words/wtimes/wdurations) and fires
  //     the wink itself — we only arm the marker here.
  // 2D: the 2D path speaks via camille2DSpeak/Web Speech, which expose no word timings, so
  //     the delay is ESTIMATED with the same per-word heuristic used for the word reveal.
  function scheduleUpsellWink(text) {
    const wink = () => handleEmotionClick("suggestive");
    if (botState.avatarType !== "2D") { window._winkAfterWord = "purchase"; return; }
    const words = String(text || "").split(" ");
    const idx = words.findIndex(w => /purchase/i.test(w));
    if (idx < 0) { wink(); return; }
    let delay = 0;
    for (let i = 0; i <= idx; i++) {
      const w = words[i];
      delay += 100 + Math.max(0, (w.length - 4) * 15) + (/[.,!?;:]/.test(w) ? 120 : 0);
    }
    setTimeout(wink, delay);
  }

  function speakAvatar(text) {
    if (window.currentAvatarTTS && window.currentAvatarObject) {
      try {
        window.currentAvatarTTS.setup({ voice:window.currentAvatarObject.voice, language:window.currentAvatarObject.language||"en-us", speed:window.currentAvatarObject.speed||1, audioEncoding:window.currentAvatarObject.encoding||"wav" });
      } catch(_) {}
    }
    botciergeSpeak(text, null, botState.avatarType);
  }
  function killAllAudio() {
    try {
      const gm=window.currentAvatarHead?.gainMaster;
      const ctx=window.currentAvatarHead?.audioCtx;
      if (gm && ctx?.state==="running") { gm.gain.cancelScheduledValues(ctx.currentTime); gm.gain.setValueAtTime(0,ctx.currentTime); }
      if (window.currentAvatarTTS) { try{window.currentAvatarTTS.terminate?.();}catch(_){} window.currentAvatarTTS=null; }
      window.speechSynthesis?.cancel();
      if (window.camille2DStop) window.camille2DStop();
      window._ttsPlaying=false;
      window._speechCancelId=(window._speechCancelId||0)+1;
    } catch(_) {}
  }

  // extra: optional fields for the bubble (ASK answers: askSources, askFiles).
  async function botReply(text, emotion, extra = null) {
    if (emotion) handleEmotionClick(emotion);
    window._wordRevealAborted = false;
    const msgId = Date.now();
    setMessages(p=>[...p,{ text:"", fullText:text, time:formatTime(), msgId, ...(extra || {}) }]);
    if (avatarTypeRef.current !== "2D" && window.currentAvatarTTS && window.currentAvatarObject) {
      try { window.currentAvatarTTS.setup({ voice:window.currentAvatarObject.voice, language:window.currentAvatarObject.language||"en-us", speed:window.currentAvatarObject.speed||1, audioEncoding:window.currentAvatarObject.encoding||"wav" }); } catch(_) {}
    }
    botciergeSpeak(text, emotion||"neutral", avatarTypeRef.current, (revealed) => {
      setMessages(p=>p.map(m=>m.msgId===msgId ? {...m,text:revealed} : m));
    });
  }

  async function botReplySearch(text, results, emotion, searchQuery = "") {
    if (emotion) handleEmotionClick(emotion);
    window._wordRevealAborted = false;
    const msgId = Date.now();
    setMessages(p => [...p, { text:"", fullText:text, time:formatTime(), msgId, isSearch:true, results: results || [], searchQuery: searchQuery || "" }]);
    if (botState.avatarType !== "2D" && window.currentAvatarTTS && window.currentAvatarObject) {
      try { window.currentAvatarTTS.setup({ voice:window.currentAvatarObject.voice, language:window.currentAvatarObject.language||"en-us", speed:window.currentAvatarObject.speed||1, audioEncoding:window.currentAvatarObject.encoding||"wav" }); } catch(_) {}
    }
    botciergeSpeak(text, emotion||"neutral", botState.avatarType, (revealed) => {
      setMessages(p => p.map(m => m.msgId === msgId ? { ...m, text: revealed } : m));
    });
  }

  // Same shape as botReplySearch: the spoken text plus a structured payload
  // rendered inside the normal bubble by ChatBubble.
  async function botReplyGoals(text, goals, emotion) {
    if (emotion) handleEmotionClick(emotion);
    window._wordRevealAborted = false;
    const msgId = Date.now();
    setMessages(p => [...p, { text:"", fullText:text, time:formatTime(), msgId, isGoals:true, goals: goals || [] }]);
    if (botState.avatarType !== "2D" && window.currentAvatarTTS && window.currentAvatarObject) {
      try { window.currentAvatarTTS.setup({ voice:window.currentAvatarObject.voice, language:window.currentAvatarObject.language||"en-us", speed:window.currentAvatarObject.speed||1, audioEncoding:window.currentAvatarObject.encoding||"wav" }); } catch(_) {}
    }
    botciergeSpeak(text, emotion||"neutral", botState.avatarType, (revealed) => {
      setMessages(p => p.map(m => m.msgId === msgId ? { ...m, text: revealed } : m));
    });
  }

  // extra: the ASK answer's sources, files and its LLM label (client QA, 10 Oct:
  // every ASK answer opens in the wide window, labelled with the LLM and version).
  async function botReplyLlm(text, emotion, extra = null) {
    if (emotion) handleEmotionClick(emotion);
    window._wordRevealAborted = false;
    const msgId = Date.now();
    setMessages(p => [...p, { text:"", fullText:text, time:formatTime(), msgId, isLlm:true, ...(extra || {}) }]);
    if (botState.avatarType !== "2D" && window.currentAvatarTTS && window.currentAvatarObject) {
      try { window.currentAvatarTTS.setup({ voice:window.currentAvatarObject.voice, language:window.currentAvatarObject.language||"en-us", speed:window.currentAvatarObject.speed||1, audioEncoding:window.currentAvatarObject.encoding||"wav" }); } catch(_) {}
    }
    botciergeSpeak(text, emotion||"neutral", botState.avatarType, (revealed) => {
      setMessages(p => p.map(m => m.msgId === msgId ? { ...m, text: revealed } : m));
    });
  }

  async function botReplyMemoryImage(imageData, emotion = "happy") {
    if (emotion) handleEmotionClick(emotion);
    const imageSrc = parseMemoryImageSrc(imageData);
    setMessages(p => [...p, { text:"", imageSrc, time:formatTime(), isImage:true }]);
    const caption = "Here's what I recalled.";
    if (botState.avatarType !== "2D" && window.currentAvatarTTS && window.currentAvatarObject) {
      try { window.currentAvatarTTS.setup({ voice:window.currentAvatarObject.voice, language:window.currentAvatarObject.language||"en-us", speed:window.currentAvatarObject.speed||1, audioEncoding:window.currentAvatarObject.encoding||"wav" }); } catch(_) {}
    }
    botciergeSpeak(caption, emotion, botState.avatarType, () => {});
  }

  async function submitRememBotImage(base64) {
    if (!base64) return;
    const imageSrc = parseMemoryImageSrc(base64);
    playPopSound();
    setMessages(p => [...p, { text:"", imageSrc, time:formatTime(), isUser:true, isImage:true }]);
    setShowTypingIndicator(true);
    try {
      const sessionName = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
      const activeSrx = enrichSrxState(srxRef.current, currentUser, sessionName);
      const data = await sendToBackend({
        message: "",
        imageData: base64,
        avatarName: activeAvatar,
        user: currentUser,
        sessionName,
        packages: currentUser?.package || [],
        srxState: activeSrx,
      });
      setShowTypingIndicator(false);
      await applyBackendResponse(data);
    } catch (err) {
      setShowTypingIndicator(false);
      console.warn("[RememBOT] image upload failed:", err.message);
      await botReply("Something went wrong saving that image. Please try again.", "sad");
      setSrx({});
    }
  }

  function handlePhotoInputChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      botReply("Please choose an image file.", "neutral");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = typeof result === "string" && result.includes(",") ? result.split(",")[1] : result;
      submitRememBotImage(base64);
    };
    reader.onerror = () => botReply("I couldn't read that image. Please try another one.", "sad");
    reader.readAsDataURL(file);
  }

  async function saveBackgroundToProfile(bgId) {
    if (!currentUser) return;
    try {
      await fetch("/api/saveBackground", { method:"POST", headers:{ "Content-Type":"application/json", "Authorization":`Bearer ${currentUser.token}` }, body: JSON.stringify({ userId:currentUser._id, backgroundId:bgId }) });
    } catch(e) { console.warn("[BG Gallery] Could not save:", e.message); }
  }

  function startActivityLedgerFlow({ entryDate, promptMessage } = {}) {
    const date = entryDate || new Date().toISOString().split("T")[0];
    setLedgerActive(true);
    setLedgerStep(0);
    setLedgerEntry({ date, activity: {}, food: {} });
    fetch("/api/activityLedger/emotions").then(r => r.json()).then(d => {
      if (Array.isArray(d?.data)) setLedgerEmotions(d.data);
    }).catch(() => {});
    botReply(promptMessage || "What did you do today?", "happy");
  }

  function startFoodLedgerFlow({ entryDate, promptMessage } = {}) {
    const date = entryDate || new Date().toISOString().split("T")[0];
    setLedgerActive(true);
    setLedgerStep(6);
    setLedgerEntry((prev) => ({
      ...prev,
      date,
      activity: prev?.activity || {},
      food: { ...(prev?.food || {}) },
    }));
    botReply(promptMessage || "What did you eat?", "happy");
  }

  function handleLedgerFollowUp(followUp) {
    if (!followUp) return;
    pendingLedgerFollowUpRef.current = null;
    if (followUp.promptType === "today" || followUp.promptType === "activity") {
      startActivityLedgerFlow({ entryDate: followUp.entryDate, promptMessage: followUp.message });
    } else if (followUp.promptType === "food") {
      startFoodLedgerFlow({ entryDate: followUp.entryDate, promptMessage: followUp.message });
    }
  }

  // RETAINED BUT CURRENTLY UNREACHABLE. Its only caller was the login-path
  // checkActivityLedger() the client asked us to remove, and line 1157's
  // setLedgerPrompt({type:"yesterday"}) is the only place that prompt is ever
  // created — so deleting this would also delete the "yesterday" ledger prompt
  // feature outright. That is a bigger call than the client made, so it stays
  // here (unused) until they say whether the yesterday prompt should have
  // another trigger or be dropped as well.
  // eslint-disable-next-line no-unused-vars
  function processLedgerCheckResponse(data) {
    if (!data || data.status !== "OK" || !data.needsPrompting || ledgerActive) return;

    if (data.promptType === "yesterday") {
      if (data.message) botReply(data.message, "suggestive");
      setLedgerPrompt({
        type: "yesterday",
        stage: "initial",
        entryDate: data.entryDate,
        followUp: data.followUpPrompt || null,
      });
      setShowYesNo(true);
      pendingLedgerFollowUpRef.current = null;
      return;
    }

    if (data.promptType === "today" || data.promptType === "activity") {
      setTimeout(() => startActivityLedgerFlow({ entryDate: data.entryDate, promptMessage: data.message }), 400);
      return;
    }

    if (data.promptType === "food") {
      setTimeout(() => startFoodLedgerFlow({ entryDate: data.entryDate, promptMessage: data.message }), 400);
      return;
    }

    if (data.message) setTimeout(() => botReply(data.message, "suggestive"), 400);
  }

  async function checkForReframeSuggestions(entry, activityLedgerId) {
    const pkgs = currentUser?.package || [];
    if (!pkgs.includes("REMEMBOT")) return;
    if (!entry.activity?.beforeFeeling || !entry.activity?.afterFeeling) return;
    try {
      const res = await fetch("/api/activityLedger/reframe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(currentUser.token ? { Authorization: `Bearer ${currentUser.token}` } : {}),
        },
        body: JSON.stringify({
          userId: currentUser._id,
          beforeFeeling: entry.activity.beforeFeeling,
          afterFeeling: entry.activity.afterFeeling,
          city: entry.activity?.place?.city,
          state: entry.activity?.place?.state,
          country: entry.activity?.place?.country,
          activityLedgerId,
          accompaniment: entry.activity?.accompaniment || null,
        }),
      });
      const data = await res.json();
      if (data.status === "OK" && data.hasSuggestion && data.message) {
        setMessages((msgs) => [...msgs, {
          text: data.message,
          time: formatTime(),
          isReframeSuggestion: true,
        }]);
        speakAvatar(data.message);
      }
    } catch (_) {}
  }

  function isValidLedgerEmotion(text) {
    if (!ledgerEmotions.length) return true;
    const t = text.trim().toLowerCase();
    return ledgerEmotions.some(e => e.toLowerCase() === t);
  }

  // MealBreakdownWeb save: same shape as the phone's handleMealBreakdownSave.
  async function finishMealBreakdown({ protein, starch, vegetable }) {
    const norm = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");
    const foodItems = [["protein", norm(protein)], ["starch", norm(starch)], ["vegetable", norm(vegetable)]]
      .filter(([, v]) => v && v !== "none");
    const entry = { ...ledgerEntry, food: { ...ledgerEntry.food, protein, starch, vegetable, foodItems } };
    setMealModal(null);
    setLedgerEntry(entry);
    const saved = await saveLedgerEntry(entry);
    const phrases = formatLedgerDatePhrases(entry.date);
    setLedgerActive(false);
    setLedgerStep(0);
    await botReply(saved?.message || `Perfect! I've recorded your activity and food for ${phrases.summaryPhrase}. Your activity ledger is now complete!`, "happy");
    const followUp = pendingLedgerFollowUpRef.current;
    if (followUp) handleLedgerFollowUp(followUp);
  }

  async function saveLedgerEntry(entry) {
    if (!currentUser?._id) return null;
    try {
      const res = await fetch("/api/activityLedger/save", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(currentUser.token ? { Authorization: `Bearer ${currentUser.token}` } : {}),
        },
        body: JSON.stringify({ userId: currentUser._id, date: entry.date, activity: entry.activity, food: entry.food }),
      });
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  async function handleLedgerInput(text) {
    const trimmed = text.trim();
    // Typed ledger answers get the same prefilter as every other box (numbers,
    // dates and skip pass); a refused answer keeps the same question open.
    if (FIELD_PREFILTER_ENABLED) {
      const screened = await screenLedgerAnswer(ledgerStep, trimmed);
      if (!screened.ok) { await botReply(screened.message, "neutral"); return; }
    }
    let entry = { ...ledgerEntry };
    let nextStep = ledgerStep;
    let prompt = null;

    if (ledgerStep === 0) {
      entry.activity = { ...entry.activity, description: trimmed };
      nextStep = 1;
      prompt = "Where did this activity take place? (name, address, city, state, country)";
    } else if (ledgerStep === 1) {
      const parts = trimmed.split(",").map(s => s.trim());
      entry.activity = {
        ...entry.activity,
        place: { name: parts[0] || "", address: parts[1] || "", city: parts[2] || "", state: parts[3] || "", country: parts[4] || "" },
      };
      nextStep = 2;
      prompt = "Who were you with? (name and relationship)";
    } else if (ledgerStep === 2) {
      const [name, relationship] = trimmed.split(",").map(s => s.trim());
      entry.activity = { ...entry.activity, accompaniment: { name: name || trimmed, relationship: relationship || "Friend" } };
      nextStep = 3;
      prompt = "How did it make you feel? Choose from the list.";
      setLedgerEntry(entry);
      setLedgerStep(nextStep);
      if (prompt) await botReply(prompt, "happy");
      if (ledgerEmotions.length) setShowFeelingPicker(true);
      return;
    } else if (ledgerStep === 3) {
      if (!isValidLedgerEmotion(trimmed)) {
        await botReply("Please choose a feeling from the list (tap a button below).", "neutral");
        setShowFeelingPicker(true);
        return;
      }
      const feeling = ledgerEmotions.find(e => e.toLowerCase() === trimmed.toLowerCase()) || trimmed;
      entry.activity = { ...entry.activity, afterFeeling: feeling };
      nextStep = 4;
      prompt = "How were you feeling before this activity? Choose from the list.";
      setLedgerEntry(entry);
      setLedgerStep(nextStep);
      if (prompt) await botReply(prompt, "happy");
      if (ledgerEmotions.length) setShowFeelingPicker(true);
      return;
    } else if (ledgerStep === 4) {
      if (!isValidLedgerEmotion(trimmed)) {
        await botReply("Please choose a feeling from the list (tap a button below).", "neutral");
        setShowFeelingPicker(true);
        return;
      }
      const feeling = ledgerEmotions.find(e => e.toLowerCase() === trimmed.toLowerCase()) || trimmed;
      entry.activity = { ...entry.activity, beforeFeeling: feeling };
      nextStep = 5;
      prompt = "Any additional comments about your experience?";
    } else if (ledgerStep === 5) {
      entry.activity = { ...entry.activity, additionalComments: trimmed };
      setLedgerEntry(entry);
      const saved = await saveLedgerEntry(entry);
      if (saved?.status !== "OK") {
        await botReply(saved?.message || "I couldn't save your activity entry. Please try again.", "sad");
        return;
      }
      await checkForReframeSuggestions(entry, saved?.data?._id || saved?._id);
      nextStep = 6;
      prompt = "What did you eat?";
    } else if (ledgerStep === 6) {
      if (/^skip$/i.test(trimmed)) {
        setLedgerActive(false);
        setLedgerStep(0);
        await botReply("Thank you! Your activity ledger has been updated.", "happy");
        const followUp = pendingLedgerFollowUpRef.current;
        if (followUp) handleLedgerFollowUp(followUp);
        return;
      }
      entry.food = { ...entry.food, foodName: trimmed };
      nextStep = 7;
      prompt = "Where did you eat? (name, address, city, state, country - optional, press Enter to skip)";
    } else if (ledgerStep === 7) {
      if (trimmed === "") {
        entry.food = {
          ...entry.food,
          place: entry.activity?.place || { name: "", address: "", city: "", state: "", country: "" },
        };
      } else {
        const parts = trimmed.split(",").map((s) => s.trim());
        entry.food = {
          ...entry.food,
          place: {
            name: parts[0] || "",
            address: parts[1] || "",
            city: parts[2] || "",
            state: parts[3] || "",
            country: parts[4] || "",
          },
        };
      }
      nextStep = 8;
      prompt = "Describe the food:";
    } else if (ledgerStep === 8) {
      entry.food = { ...entry.food, foodDescription: trimmed };
      if (MEAL_BREAKDOWN_ENABLED) {
        // Phone parity: the meal is broken into protein / starch / vegetable in
        // a popup (MealBreakdownWeb) instead of a free-text list.
        setLedgerEntry(entry);
        setLedgerStep(9);
        setMealModal({ description: trimmed });
        return;
      }
      nextStep = 9;
      prompt = "List the food items (separate with commas):";
    } else if (ledgerStep === 9) {
      const foodItems = trimmed ? trimmed.split(",").map((item) => item.trim()) : [];
      entry.food = { ...entry.food, foodItems };
      setLedgerEntry(entry);
      const saved = await saveLedgerEntry(entry);
      const phrases = formatLedgerDatePhrases(entry.date);
      setLedgerActive(false);
      setLedgerStep(0);
      await botReply(
        saved?.message || `Perfect! I've recorded your activity and food for ${phrases.summaryPhrase}. Your activity ledger is now complete!`,
        "happy"
      );
      const followUp = pendingLedgerFollowUpRef.current;
      if (followUp) handleLedgerFollowUp(followUp);
      return;
    }

    setLedgerEntry(entry);
    setLedgerStep(nextStep);
    if (prompt) await botReply(prompt, "happy");
  }

  async function selectLedgerFeeling(feeling) {
    setShowFeelingPicker(false);
    setMessages(p => [...p, { text: feeling, time: formatTime(), isUser: true }]);
    setShowTypingIndicator(true);
    try {
      await handleLedgerInput(feeling);
    } finally {
      setShowTypingIndicator(false);
    }
  }

  // The BOTCIERGE Catchphrase step, from the chat box or the catchphrase pop-up
  // (CATCHPHRASE_POPUP_ENABLED). Same gate calls and attempt rules either way.
  async function submitCatchphrase(text) {
    if (isVerifyEscape(text)) {
      endVerification(); setShowTypingIndicator(false);
      await botReply('Okay, sign-in cancelled. Say "login" whenever you are ready.',"neutral");
      return;
    }
    setStep(null); setShowTypingIndicator(true);
    const { status, data } = await postVerify("catchphrase/verify", { verificationToken: verifyRef.current?.verificationToken, catchphrase: text });
    setShowTypingIndicator(false);
    // Pick up email/maskedEmail if the failure response carries them — belt
    // and braces alongside the catchphrase_required capture.
    if (data?.email && !verifyRef.current?.email) verifyRef.current={ ...verifyRef.current, email:data.email };
    if (data?.maskedEmail && !verifyRef.current?.maskedEmail) verifyRef.current={ ...verifyRef.current, maskedEmail:data.maskedEmail };
    // The shipped app words the load failure for this path specifically — it
    // has already told the user their catchphrase was accepted.
    if (status === 200 && data?.status === "OK") {
      await finishVerifiedLogin(data.data?._id, "Your catchphrase was correct, but we could not load your account. Please try again.");
      return;
    }
    // A wrong catchphrase is answerable up to 3 attempts. The client's spec:
    // on the third failure Camille says the verbatim line below and pivots
    // to the OTP terminal — same modal the otp_required branch uses — so
    // the user is not left stuck on a step they cannot pass.
    if (data?.verified === false && status !== 409) {
      const attempts=(verifyRef.current?.catchphraseAttempts||0)+1;
      verifyRef.current={ ...verifyRef.current, catchphraseAttempts:attempts };
      if (attempts>=3) {
        const email=verifyRef.current?.email||data?.email||"";
        const maskedEmail=verifyRef.current?.maskedEmail||data?.maskedEmail||"";
        await botReply("I am unable to verify your identity by catchphrase but you may login a different way.","sad");
        if (email) {
          setShowYesNo(false); setStep(null); setOtpCode("");
          setOtpModal({ stage:"email", email, maskedEmail });
          return;
        }
        // Gate did not surface the account email at either capture point.
        // End cleanly rather than open an empty modal that cannot send a code.
        endVerification();
        await botReply("Please say \"login\" to try again.","neutral");
        return;
      }
      setStep("awaiting_catchphrase");
      await botReply(data.message || "Your catchphrase did not match.", "sad");
      return;
    }
    await applyVerifyStep(status, data);
    return;
  }

  // handleSend(text): a button can send its own text (the CheckLister Edit
  // button); otherwise the typed input. (An onClick passes an event, not text.)
  async function handleSend(override) {
    const text=(typeof override==="string" ? override : inputText).trim(); if(!text) return;

    // CLEAR_CHAT_ENABLED (default on, Round 12): "clear chat" empties the
    // conversation on screen and ends a half-finished step. Sign-in, the name,
    // the host and everything saved stay. No confirmation. (Not at a sign-in or
    // catchphrase step, where the words are an answer.)
    if (process.env.REACT_APP_CLEAR_CHAT_ENABLED !== "false" && !loginStepRef.current && /^\/?(?:clear|clear chat|clear the chat|clear screen)[.!]*$/i.test(text)) {
      setInputText(""); setIsTyping(false); setSrx({});
      setMessages([{ text: greetingAskedName && !currentUser ? `Hi! I'm ${activeAvatar}. What is your name?` : `Hi${sessionFirstName ? ` ${sessionFirstName}` : ""}! How may I assist you?`, time: formatTime() }]);
      return;
    }

    if (/^ask(\s|$)/i.test(text) || /^devaskmyllm/i.test(text)) askLlmActiveRef.current = true;

    playPopSound();
    // The catchphrase is a credential typed into the ordinary chat input, so the
    // echoed bubble is masked and the raw value is never written to message
    // state — it would otherwise sit in the visible transcript for the rest of
    // the session. `text` below still carries the real value to the gate.
    // A security-question answer is masked the same way (SECQ_ANSWER on the back end).
    const isCatchphraseEntry = loginStepRef.current==="awaiting_catchphrase" || loginStepRef.current==="awaiting_verify_answer" || srxRef.current?.pendingAction==="SECQ_ANSWER";
    setMessages(p=>[...p,{text: isCatchphraseEntry ? "••••••••" : text, time:formatTime(), isUser:true}]);
    setInputText(""); setIsTyping(false);

    // Profanity is screened by the backend's one prefilter (MBTS
    // src/intent/prefilter.js), the same for guests and signed-in users; the
    // browser keeps no copy of the rules (round 8).

    setShowTypingIndicator(true);

    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.isReframeSuggestion) {
      if (isYesResponse(text)) await botReply("OK, go and enjoy yourself!", "happy");
      else if (isNoResponse(text)) await botReply("OK I hope you feel better.", "sad");
      else await botReply("Please respond with yes or no.", "neutral");
      setShowTypingIndicator(false);
      return;
    }

    if (ledgerPrompt) {
      const phrases = formatLedgerDatePhrases(ledgerPrompt.entryDate);
      const trimmed = text.trim();
      if (!isYesResponse(trimmed) && !isNoResponse(trimmed)) {
        setShowTypingIndicator(false);
        await botReply("Please respond with yes or no.", "suggestive");
        return;
      }
      if (ledgerPrompt.type === "yesterday") {
        if (ledgerPrompt.stage === "initial") {
          if (isYesResponse(trimmed)) {
            pendingLedgerFollowUpRef.current = ledgerPrompt.followUp || null;
            setLedgerPrompt(null);
            setShowYesNo(false);
            startActivityLedgerFlow({
              entryDate: ledgerPrompt.entryDate,
              promptMessage: `Great! Let's capture what you did ${phrases.questionPhrase}. What did you do ${phrases.questionPhrase}?`,
            });
          } else {
            await botReply(`Okay, this means you won't have any historical data about ${phrases.summaryPhrase}. Is this what you want?`, "suggestive");
            setLedgerPrompt((p) => (p ? { ...p, stage: "confirmSkip" } : p));
          }
        } else if (ledgerPrompt.stage === "confirmSkip") {
          const followUp = ledgerPrompt.followUp || null;
          const entryDate = ledgerPrompt.entryDate;
          setShowYesNo(false);
          setLedgerPrompt(null);
          if (isYesResponse(trimmed)) {
            const isAfterEvening = new Date().getHours() >= 18;
            await botReply(isAfterEvening ? "Ok" : "Ok then.", "suggestive");
            if (followUp) handleLedgerFollowUp(followUp);
          } else {
            pendingLedgerFollowUpRef.current = followUp;
            startActivityLedgerFlow({
              entryDate,
              promptMessage: `No problem. Let's capture what you did ${phrases.questionPhrase}. What did you do ${phrases.questionPhrase}?`,
            });
          }
        }
        setShowTypingIndicator(false);
        return;
      }
    }

    if (ledgerActive) {
      try {
        await handleLedgerInput(text);
      } finally {
        setShowTypingIndicator(false);
      }
      return;
    }

    if (greetingAskedName && !currentUser && loginStepRef.current===null) {
      setGreetingAskedName(false); setShowTypingIndicator(false);
      // NAME_STEP_RULE_ENABLED (backend): nothing typed here is a request. The
      // backend's name rule takes the first clean token (D'Angelo, Anne-Marie,
      // José) and answers "This is not a name. Try again." otherwise; the step loops.
      let firstName=text.trim().split(" ")[0];
      if (FIELD_PREFILTER_ENABLED) {
        const screened = await screenText(text.trim(), "name");
        if (!screened.ok) { setGreetingAskedName(true); await botReply(screened.message, "sad"); return; }
        if (screened.name) firstName = screened.name;
      }
      if (!firstName || !/^[\p{L}\p{M}]+(?:['’-][\p{L}\p{M}]+)*$/u.test(firstName)) {
        setGreetingAskedName(true);
        await botReply("This is not a name. Try again.", "sad");
        return;
      }
      setSessionFirstName(firstName); sessionStorage.setItem("btcg_session_name",firstName);
      window._greetingDone=true;
      // Mobile MBTS parity: name greeting is client-side — do not send to intentHandler
      await botReply(`Hi ${firstName}, welcome to the BOTCIERGE® experience. Type 'login' anytime to sign in.`, avatarGreetingEmotion());
      return;
    }

    if (showYesNo && verifyRef.current) {
      const isYes=/^(yes|yeah|yep|y|correct|right|sure|yup|that's me|thats me)$/i.test(text.trim());
      const isNo =/^(no|nope|n|wrong|not me|nah)$/i.test(text.trim());
      if (isYes) { await handleYes(); return; }
      else if (isNo) { await handleNo(); return; }
      setShowTypingIndicator(false); await botReply("Please say yes or no.","neutral"); return;
    }

    if (loginStepRef.current==="awaiting_verify_name") {
      if (isVerifyEscape(text)) {
        endVerification(); setShowTypingIndicator(false);
        await botReply('Okay, sign-in cancelled. Say "login" whenever you are ready.',"neutral");
        return;
      }
      // Biz re-login flow: after "What's your first name?" the reply is the
      // NAME — it must never be forwarded to the backend as an intent.
      setStep(null); setShowTypingIndicator(false);
      let firstName=text.trim().split(" ")[0];
      if (FIELD_PREFILTER_ENABLED) {
        const screened = await screenText(text.trim(), "name");
        if (!screened.ok) { setStep("awaiting_verify_name"); await botReply(screened.message, "sad"); return; }
        if (screened.name) firstName = screened.name;
      }
      if (!firstName || !/^[\p{L}\p{M}]+(?:['’-][\p{L}\p{M}]+)*$/u.test(firstName)) {
        setStep("awaiting_verify_name");
        await botReply("This is not a name. Try again.","sad");
        return;
      }
      setSessionFirstName(firstName); sessionStorage.setItem("btcg_session_name",firstName);
      await startVerification(firstName);
      return;
    }

    // "No, that's not me": the person's first name or account email.
    if (loginStepRef.current==="awaiting_identify") {
      if (isVerifyEscape(text)) {
        endVerification(); setShowTypingIndicator(false);
        await botReply('Okay, sign-in cancelled. Say "login" whenever you are ready.',"neutral");
        return;
      }
      setStep(null); setShowTypingIndicator(true);
      const { status, data } = await postVerify("verify/identify", { sessionToken: verifyRef.current?.sessionToken, answer: text.trim() });
      setShowTypingIndicator(false);
      if (data?.firstName && verifyRef.current) verifyRef.current={ ...verifyRef.current, firstName:data.firstName };
      await applyVerifyStep(status, data);
      return;
    }

    if (loginStepRef.current==="awaiting_verify_answer") {
      if (isVerifyEscape(text)) {
        endVerification(); setShowTypingIndicator(false);
        await botReply('Okay, sign-in cancelled. Say "login" whenever you are ready.',"neutral");
        return;
      }
      setStep(null); setShowTypingIndicator(true);
      const { status, data } = await postVerify("verify/answer", { sessionToken: verifyRef.current?.sessionToken, answer: text });
      setShowTypingIndicator(false);
      await applyVerifyStep(status, data);
      return;
    }

    if (loginStepRef.current==="awaiting_catchphrase") {
      await submitCatchphrase(text);
      return;
    }

    const sessionName = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
    const activeSrx = enrichSrxState(srxRef.current, currentUser, sessionName);

    if (isSrxContinuation(activeSrx)) {
      try {
        const data = await sendToBackend({ message:text, avatarName:activeAvatar, user:currentUser, sessionName, packages:currentUser?.package||[], srxState:activeSrx });
        setShowTypingIndicator(false);
        await applyBackendResponse(data);
      } catch(err) {
        setShowTypingIndicator(false);
        console.warn("[SRX] continuation failed:", err.message);
        // Item I: presentation only — same trigger, same text. Pushed as a system notice
        // instead of botReply so it is not attributed to the avatar and is not spoken by TTS.
        setMessages(p=>[...p,{ text:"Something went wrong. Please try your request again.", time:formatTime(), isSystem:true }]);
        setSrx({});
      }
      return;
    }

    // The answer to "What would you like me to search for?" is the query itself
    // (the back end hands SEARCH requests to this page: SEARCH_HANDOFF_ENABLED).
    if (pendingSearchRef.current) {
      pendingSearchRef.current = false;
      if (/^(?:cancel|never ?mind|nothing|no|stop)\.?$/i.test(text)) {
        setShowTypingIndicator(false);
        await botReply("Okay, no search. How else may I assist you?", "neutral");
        return;
      }
      await runWebSearch(text);
      return;
    }
    const SEARCH_BARE    = /^search\s*$/i;
    const SEARCH_TRIGGER = /^search(?:\s+for(?:\s+information\s+on)?)?\s+(.+)/i;
    if (SEARCH_BARE.test(text)) {
      setShowTypingIndicator(false);
      pendingSearchRef.current = true;
      await botReply("What would you like me to search for?", "neutral");
      return;
    }
    const searchMatch = text.match(SEARCH_TRIGGER);
    if (searchMatch) {
      const query = searchMatch[1].trim();
      if (query) {
        await runWebSearch(query);
        return;
      }
    }

    const intent=detectIntent(text);
    if (intent==="LOGIN") {
      setShowTypingIndicator(false);
      if (currentUser) { await botReply(`You're already logged in as ${currentUser.name}!`,"happy"); return; }
      const knownName=sessionFirstName||sessionStorage.getItem("btcg_session_name")||"";
      if (knownName) { await startVerification(knownName); } else { setStep("awaiting_verify_name"); await botReply("What's your first name?","neutral"); }
      return;
    }
    if (intent==="LOGOUT") {
      setShowTypingIndicator(false);
      const everywhere=/\b(everywhere|all (?:my )?devices)\b/i.test(String(text||""));
      if (currentUser) { const name=currentUser.name; logoutUser({ everywhere }); setCurrentUser(null); setGalleryBg(null); await botReply(everywhere?`Goodbye, ${name}! You are signed out on all your devices.`:`Goodbye, ${name}!`,"neutral"); }
      else { await botReply("You're not currently logged in.","neutral"); }
      return;
    }
    try {
      const conversationHistory=messages.filter(m=>m.text).slice(-10).map(m=>({role:m.isUser?"user":"assistant",content:m.text}));
      // ASK attachments go with this request and are then cleared.
      const askFiles = askAttachments.map((a) => a.id);
      if (askFiles.length) setAskAttachments([]);
      const data=await sendToBackend({ message:text, avatarName:activeAvatar, user:currentUser, sessionName:sessionFirstName||sessionStorage.getItem("btcg_session_name")||"", packages:currentUser?.package||[], srxState:null, conversationHistory, askFiles });
      setShowTypingIndicator(false); setChatError(false);
      await applyBackendResponse(data);
    } catch(err) {
      setShowTypingIndicator(false); setChatError(true);
      console.warn("[BOTCIERGE] Backend unavailable:", err.message);
    } finally { setShowTypingIndicator(false); }
  }

  function handleKeyDown(e) { if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();handleSend();} }

  // Polls /api/askJob/:id every 3s for up to 3 minutes and shows the answer.
  async function pollAskJob(jobId) {
    const userId = currentUser?._id || "";
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(`/api/askJob/${jobId}?userId=${encodeURIComponent(userId)}`);
        const job = await res.json();
        if (job.status === "done") { await replyWithAskExtras(job.message || "", "neutral", job); return; }
        if (job.status === "unknown") break;
      } catch (_) { /* keep polling */ }
    }
    await botReply("That answer took too long to arrive. Please ask again.", "sad");
  }

  // Brave web search through the proxy; results render as cards.
  async function runWebSearch(query) {
    try {
      const sRes = await fetch("/api/search", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ query, firstName:sessionFirstName||sessionStorage.getItem("btcg_session_name")||"", userId:currentUser?._id||null, authenticated:!!currentUser, avatar:activeAvatar }) });
      setShowTypingIndicator(false);
      const sData = await sRes.json();
      if (sData.reply) { await botReplySearch(sData.reply, sData.results||[], sData.emotion||"neutral", sData.query||query); }
      else { await botReply(`I searched for "${query}" — check the BOTCIERGE BOT Store for specialized services!`, "neutral"); }
    } catch(err) { setShowTypingIndicator(false); await botReply(`I searched for "${query}". Please try again or visit brave.com/search.`, "neutral"); }
  }

  // An ASK answer with sources or files: the text without those lists (they
  // render as links and download cards and are not read aloud).
  async function replyWithAskExtras(text, emotion, data) {
    const askSources = Array.isArray(data?.askSources) ? data.askSources : [];
    const askFiles = Array.isArray(data?.askFiles) ? data.askFiles : [];
    // Signed in: the wide window, labelled with the LLM and version. A guest's
    // answer stays a plain bubble (no LLM surface when logged out).
    if (currentUser && llmLabelFor(data, text)) {
      const llm = llmLabelFor(data, text);
      if (!askSources.length && !askFiles.length) return botReplyLlm(text, emotion, { llmLabel: llm });
      return botReplyLlm(cleanAskText(text), emotion, { askSources, askFiles, llmLabel: llm });
    }
    if (!askSources.length && !askFiles.length) return botReply(text, emotion);
    return botReply(cleanAskText(text), emotion, { askSources, askFiles });
  }

  // ASK attachments: upload on pick (signed-in users only), at most MAX_ATTACHMENTS.
  async function handleAskFileChange(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !currentUser?._id) return;
    setAskUploading(true);
    try {
      for (const file of files.slice(0, Math.max(0, MAX_ATTACHMENTS - askAttachments.length))) {
        const r = await uploadAskFile(file, currentUser._id);
        if (r.ok) setAskAttachments((p) => [...p, r.file]);
        else await botReply(r.message, "neutral");
      }
    } finally {
      setAskUploading(false);
    }
  }

  async function applyBackendResponse(data) {
    if (!data) return;
    // WEATHER_CARD_FOLLOWS_LOCATION (default on, 27 Oct 2026, Biz's QA): "I'm home",
    // "I'm in Lisbon", "use my real location" change the person's place, so the
    // WeatherBOT card is reloaded now (it kept showing Lisbon until a reload).
    if (data.intentOutcome === "city-override" && process.env.REACT_APP_WEATHER_CARD_FOLLOWS_LOCATION !== "false") loadWeather(true);
    // ASK (Round 10): an answer that comes with sources or files.
    if (!data.askJobId && ((data.askSources && data.askSources.length) || (data.askFiles && data.askFiles.length))) {
      setSrx({});
      await replyWithAskExtras(data.reply || "", data.emotion || "neutral", data);
      return;
    }
    // SEARCH requests come back as a hand-off: run the search here, or ask for
    // the query and treat the next message as it.
    if (data.type === "search" && data.query) {
      setSrx({});
      await runWebSearch(data.query);
      return;
    }
    // A slow ASK answer (ASK_ASYNC_ENABLED on the back end): show "Working on it…"
    // now, then the answer when the back end has it.
    if (data.askJobId) {
      setSrx({});
      await botReply(data.reply || "Working on it…", "neutral");
      pollAskJob(data.askJobId);
      return;
    }
    if (data.type === "searchPrompt") {
      setSrx({});
      pendingSearchRef.current = true;
      await botReply(data.reply || "What would you like me to search for?", "neutral");
      return;
    }
    // Security questions: the form, not the numbered list in the message.
    if (data.modalType === "securityQuestions") {
      const sn = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
      setSrx(enrichSrxState({ ...(data.srxState || data), type: "input" }, currentUser, sn));
      const reopen = !!secqForm;
      setSecqForm(data.data || {});
      if (!reopen) await botReply(data.data?.title || "Choose 2 security questions, then answer them.", "neutral");
      return;
    }

    const sessionName = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
    const emptyShopCategory = !data.srxState && data.reply && /no items found in this category/i.test(data.reply);

    if (emptyShopCategory) {
      const prev = enrichSrxState(srxRef.current, currentUser, sessionName);
      const choice = prev.inputList?.[0] || "2";
      setSrx(enrichSrxState({
        type: "modal",
        modalType: "viewCategory",
        selectedPackage: "SHOPSAVVY",
        intent: prev.intent || "i want to add items to my shopping list",
        inputList: [choice],
        firstName: prev.firstName,
      }, currentUser, sessionName));
    } else if (data.srxState && Object.keys(data.srxState).length) {
      setSrx(enrichSrxState(data.srxState, currentUser, sessionName));
    } else if (data.type === "result" || data.type === "unknown" || data.type === "upsell") {
      setSrx({});
      setShowCategoryModal(false);
      setShowUnitModal(false);
      setShowShopSavvy(false);
      setShowPostNeedModal(false);
    } else if (data.type === "input") {
      setSrx(enrichSrxState(data, currentUser, sessionName));
    } else if (data.type === "modal" && ["viewCategory", "viewUnit", "scheduleModal"].includes(data.modalType)) {
      setSrx(enrichSrxState(data, currentUser, sessionName));
    } else if (data.type === "modal") {
      setSrx({});
    } else {
      setSrx({});
    }

    if (data.type === "activityLedger" || data.action === "startActivity") {
      startActivityLedgerFlow({ entryDate: data.entryDate, promptMessage: data.reply || data.message });
    } else if (data.action === "startFood") {
      startFoodLedgerFlow({ entryDate: data.entryDate, promptMessage: data.reply || data.message });
    }

    if (data.type === "screen") {
      if (data.screenType === "activeNeeds") {
        setSrx(enrichSrxState(data, currentUser, sessionName));
        setNeedsScreenMode("activeNeeds");
        setNeedsTypeFilter(data.needType || "");
        setTimeout(() => setShowNeedsScreen(true), 300);
      } else if (data.screenType === "myNeeds") {
        setSrx(enrichSrxState(data, currentUser, sessionName));
        setNeedsScreenMode("myNeeds");
        setNeedsTypeFilter("");
        setTimeout(() => setShowNeedsScreen(true), 300);
      }
    }

    if (emptyShopCategory) {
      setTimeout(() => setShowCategoryModal(true), 400);
    }
    if (data.modalType === "viewTL") { setTodoModalData(data.data || []); setShowViewTodoModal(true); }
    else if (data.modalType === "updateTL" || data.modalType === "fulfillTL") { setTodoModalData(data.data || []); setShowUpdateTodoModal(true); }
    else if (data.modalType === "viewSA") {
      setSaModalMode("view");
      setSaModalData((data.data || []).map(item => ({ ...item, delete: false })));
      setShowUpdateSA(true);
    }
    else if (data.modalType === "updateSA") {
      setSaModalMode("update");
      setSaModalData((data.data || []).map(item => ({ ...item, delete: false })));
      setShowUpdateSA(true);
    }
    else if (data.modalType === "purchaseSA") {
      setPurchaseItems((data.data || []).map(item => ({
        ...item,
        purchased: false,
        fulfilledStore: "",
        fulfilledDate: new Date().toISOString().slice(0, 16),
      })));
      setShowPurchaseSA(true);
    }
    else if (data.modalType === "viewCategory" || data.modalType === "categoryList") {
      setTimeout(() => setShowCategoryModal(true), 300);
    } else if (data.modalType === "viewUnit") {
      setTimeout(() => setShowUnitModal(true), 300);
    } else if (data.modalType === "addSA") {
      setSaModalData((data.data || []).map(item => ({ ...item, quantity: item.quantity || 0 })));
      setTimeout(() => setShowShopSavvy(true), 300);
    } else if (data.modalType === "postNeedForm") {
      setPostNeedPreset(data.needType || "");
      setTimeout(() => setShowPostNeedModal(true), 300);
    } else if (data.modalType === "scheduleModal") {
      setSrx(enrichSrxState(data, currentUser, sessionName));
      setTimeout(() => setShowScheduleModal(true), 300);
    } else if (["viewChecklist", "educationResources", "safetyContacts"].includes(data.modalType)) {
      setSrx({});
      setTimeout(() => setInfoModal({ kind: data.modalType, message: data.reply || data.message || "", data: data.data }), 300);
    } else if (data.modalType === "checklistPicker" || data.modalType === "checklistView") {
      // CheckLister: a picker keeps the conversation as an input turn (the chosen
      // option's id is the next message); a list view ends it.
      if (data.modalType === "checklistPicker") setSrx(enrichSrxState({ ...(data.srxState || data), type: "input" }, currentUser, sessionName));
      else setSrx({});
      const cd = data.data || (data.srxState && data.srxState.data) || {};
      setTimeout(() => setChecklistModal({ kind: data.modalType, data: cd }), 300);
    } else if (data.modalType === "goalKeeperModal") {
      // Harv's GoalKeeper: the backend hands us the master goal list + the user's
      // saved selection. Keep the srx as a continuable input turn (override the
      // "modal" type) so the save posts the ordered ids back as inputList, the
      // same mechanism the number-entry flow used before the modal existed.
      const gd = data.data || (data.srxState && data.srxState.data) || {};
      // TEMP [GOAL-DIAG]: confirms the idOf/nameOf field guess in
      // GoalKeeperModalWeb against the real payload, as the frontend sees it
      // after the proxy. Logs the whole response plus the key shapes. Remove
      // once the goal id field is confirmed.
      console.log("[GOAL-DIAG] goalKeeperModal response:", JSON.stringify(data));
      console.log("[GOAL-DIAG] goals[0] shape:", JSON.stringify(Array.isArray(gd.goals) ? gd.goals[0] : gd.goals),
        "| goals count:", Array.isArray(gd.goals) ? gd.goals.length : 0,
        "| selectedGoals:", JSON.stringify(gd.selectedGoals));
      setGoalModalData({
        goals: Array.isArray(gd.goals) ? gd.goals : [],
        selectedGoals: Array.isArray(gd.selectedGoals) ? gd.selectedGoals : [],
      });
      setSrx(enrichSrxState({ ...(data.srxState || data), type: "input" }, currentUser, sessionName));
      setTimeout(() => setShowGoalModal(true), 300);
    } else if (data.modalType && data.modalType !== "packages") {
      // Unknown modalType must never freeze the chat: clear any continuation
      // state so input stays alive, log it, and let data.message fall through to
      // a plain bubble below. ("packages" is handled by its own block later.)
      console.warn("[Modal] unhandled modalType:", data.modalType);
      setSrx({});
    }
    if (data.type === "packages" || data.modalType === "packages") {
      const pkgList = data.allPackages || data.data || data.packages || [];
      const userPkgs = currentUser?.package || [];
      const allPkgs = pkgList.length > 0
        ? pkgList.map(p => ({
            ...p,
            price: ZONE_A_SERVICE_PRICE[p.name] ?? p.price ?? 0,
            selected: p.selected ?? userPkgs.includes(p.name),
          }))
        : buildPackageListFromIcons(userPkgs);
      // PURCHASE_INTENT_ENABLED: "buy goalkeeper package" opens the store with
      // that package ticked; saving adds it.
      if (data.focusPackage) {
        const fk = String(data.focusPackage).toLowerCase().replace(/[^a-z0-9]/g, "");
        const strip = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
        const hit = allPkgs.find(p => strip(p.name) === fk || strip(p.name).replace(/ai$/, "") === fk.replace(/ai$/, ""));
        if (hit) hit.selected = true;
      }
      setTimeout(() => {
        botStoreBaselineRef.current = [...userPkgs];
        setPackages(allPkgs);
        setSelectedCategory(null);
        setBotStoreView("browse");
        setBotStoreSuccessMsg("");
        setShowBotStore(true);
      }, 800);
    }
    // ArtBOT (IMAGE_GEN_ENABLED in MBTS): the picture, with its caption spoken.
    if (data.imageUrl && /^https:\/\//.test(String(data.imageUrl))) {
      await botReply(data.message || "Here's your picture.", data.emotion || "happy", { isImage: true, imageSrc: data.imageUrl });
      return;
    }
    if (data.isImage && (data.reply || data.message)) {
      await botReplyMemoryImage(data.reply || data.message, data.emotion || "happy");
      return;
    }
    // Active Goals card: view_goal is text-only, so extractActiveGoals recovers
    // the goals by matching known master tokens. Placed before the AskMyLLM
    // gates so a goals reply is never badged. Returns null on anything it does
    // not recognise, which falls through to the plain bubble unchanged.
    if (data.type === "result" && !data.modalType) {
      const activeGoals = extractActiveGoals(data);
      if (activeGoals) {
        setSrx({});
        await botReplyGoals(data.reply || data.message || "", activeGoals, data.emotion || "happy");
        return;
      }
    }
    // AskMyLLM gates (TASK 2). Gate A: no LLM surface when logged out — never
    // mount the wide view or badge a reply for an unauthenticated user, whatever
    // the backend or askLlmActiveRef say. Gate B: the "a bit beyond" capability
    // upsell is never an LLM answer, logged in or out. When either gate trips on
    // a reply the flag expected to badge, clear the flag (the turn is over) and
    // fall through to the plain-bubble path — the message is shown, never hidden.
    if (askLlmActiveRef.current && (!currentUser || CAPABILITY_UPSELL_RE.test(data.reply || data.message || ""))) {
      askLlmActiveRef.current = false;
    }
    if (isWideViewResponse(data) && currentUser) {
      const text = data.reply || data.message || "";
      await botReplySearch(text, data.results || [], data.emotion || "neutral", data.query || data.searchQuery || "");
      return;
    }
    // The final LLM answer arrives as type:"result"; intermediate steps carry srxState
    // and stay in the normal bubble flow until the exchange resolves.
    if (askLlmActiveRef.current && (data.reply || data.message) && data.type !== "input"
        && !(data.srxState && Object.keys(data.srxState).length)) {
      askLlmActiveRef.current = false;
      await botReplyLlm(data.reply || data.message, data.emotion || "neutral", { llmLabel: llmLabelFor(data, data.reply || data.message) });
      return;
    }
    if (data.reply || data.message) {
      const skipReply = data.type === "activityLedger" || data.action === "startActivity" || data.action === "startFood";
      if (!skipReply) {
        const text = data.reply || data.message;
        // TASK 2: fire the wink on the upsell whether or not the backend tags type:"upsell".
        const isUpsell = data.type === "upsell" || UPSELL_RE.test(text);
        // TASK 4: on an upsell the wink must land AFTER the spoken word "Purchase", so it is
        // NOT passed to botReply (that fires at message start). It is scheduled from the real
        // Kokoro word timings in ThreeDAvatar's onmessage; 2D has no word timings (see below).
        const emotion = isUpsell ? null : (data.emotion || null);
        if (isUpsell) scheduleUpsellWink(text);
        await botReply(text, emotion);
      }
    }
  }

  function handleExitApp() {
    // Biz: Exit = get OUT of the app, back to "Tap to continue" to start a fresh
    // session. It is not a logout and not a menu-close — it's the escape hatch
    // for when the control loop gets hung inside a handler loop.
    try { killAllAudio(); } catch (_) {}
    try { sessionStorage.removeItem("btcg_session_name"); } catch (_) {}
    window.location.reload();
  }

  async function handleSaveSchedule(wakeTime, sleepTime, schedule) {
    if (!currentUser?._id) {
      setShowScheduleModal(false);
      await botReply("Please log in to save your schedule.", "neutral");
      return;
    }
    const sessionName = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
    const activeSrx = enrichSrxState(srxRef.current, currentUser, sessionName);
    // The day the backend asked about (FlowPlanner / ReadyScheduler put it in
    // targetDate and data.date). Without it the backend saves the schedule under
    // today, so a flow plan for tomorrow never finds it and asks again forever.
    // No day named: the person's own today, not the server's (UTC) date.
    const scheduleDate = activeSrx.targetDate || activeSrx.data?.date || localDateString();
    try {
      const res = await fetch("/api/updateSchedule", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(currentUser.token ? { Authorization: `Bearer ${currentUser.token}` } : {}),
        },
        body: JSON.stringify({ userId: currentUser._id, wakeTime, sleepTime, schedule, ...(scheduleDate ? { date: scheduleDate } : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      // Only a real save counts: every error reply also carries a message.
      if (res.ok && data.status === "OK") {
        setShowScheduleModal(false);
        const { type, inputList, modalType, message, reply, ...filteredSrx } = activeSrx;
        // FlowPlanner opened the modal mid-conversation: resume it as a
        // continuation turn so the proxy forwards selectedPackage and targetDate.
        // (Sent as a new message, the proxy dropped them and the backend saw an
        // empty request.) Any other schedule edit is finished once it is saved.
        if (filteredSrx.selectedPackage === "FLOWPLANNER") {
          const cont = await sendToBackend({
            message: "",
            avatarName: activeAvatar,
            user: currentUser,
            sessionName,
            packages: currentUser?.package || [],
            srxState: { ...filteredSrx, type: "input", inputList: [] },
          });
          await applyBackendResponse(cont);
        } else {
          setSrx({});
          await botReply(scheduleDate ? `Your schedule for ${scheduleDate} is saved.` : "Your schedule is saved.", "happy");
        }
      } else {
        // The back end's own reason (e.g. "Your sign-in has expired. Please sign in again.").
        await botReply(data.message ? `Your schedule wasn't saved. ${data.message}` : "Could not save your schedule.", "sad");
      }
    } catch (_) {
      await botReply("Could not save your schedule right now.", "sad");
    }
  }

  async function openGoalEditor() {
    // The card is built from a text-only view_goal reply, so we do not hold the
    // master goal list it would need. If a goalKeeperModal payload is already in
    // hand, reopen it; otherwise ask the backend for the editor. This issues a
    // known intent rather than detecting one — the backend owns the routing, and
    // it confirmed "set goals" / "edit goals" / "update goals" are one use case.
    // Reuse the list only while the GoalKeeper step is still open; otherwise ask
    // again, so the save goes back to GoalKeeper and the ticks are current.
    if (goalModalData.goals.length && srxRef.current?.selectedPackage === "GOALKEEPER") { setShowGoalModal(true); return; }
    setShowTypingIndicator(true);
    const sessionName = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
    try {
      const data = await sendToBackend({
        message: "set goals",
        avatarName: activeAvatar,
        user: currentUser,
        sessionName,
        packages: currentUser?.package || [],
        srxState: null,
      });
      await applyBackendResponse(data);
    } catch (err) {
      console.warn("[Goals] could not open editor:", err.message);
      await botReply("I couldn't open your goals right now. Please try again.", "sad");
    } finally {
      setShowTypingIndicator(false);
    }
  }

  // The security-questions form goes back as one object; the answers never
  // appear in the chat and are hashed on the back end.
  async function submitSecurityQuestions(form) {
    const sessionName = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
    const activeSrx = enrichSrxState(srxRef.current, currentUser, sessionName);
    setSecqBusy(true);
    try {
      const data = await sendToBackend({ message: "", avatarName: activeAvatar, user: currentUser, sessionName, packages: currentUser?.package || [], srxState: activeSrx, secqForm: form });
      if (data && data.modalType === "securityQuestions") { await applyBackendResponse(data); return; }
      setSecqForm(null);
      await applyBackendResponse(data);
    } catch (_) {
      setSecqForm(null);
      setSrx({});
      await botReply("Your security questions weren't saved. Please try again.", "sad");
    } finally {
      setSecqBusy(false);
    }
  }

  async function handleSaveGoals(orderedIds) {
    // Close first so the chat is never blocked, then post the selection back as
    // the inputList the backend splits on /[\s,.]+/ — space-joined ids in
    // priority order. continueSrxWithValue carries the goalKeeper srx context and
    // renders the backend's type:"result" confirmation as a normal bubble.
    setShowGoalModal(false);
    const payload = (orderedIds || []).join(" ");
    if (!payload) { setSrx({}); return; }
    // The save must continue the GoalKeeper step; without it the ids went out as
    // a new chat message ("4 11 12") and came back "not recognized".
    if (srxRef.current?.selectedPackage !== "GOALKEEPER") {
      const goalSrx = { selectedPackage:"GOALKEEPER", intent:"edit my goals", type:"input", modalType:"goalKeeperModal", inputList:[] };
      srxRef.current = goalSrx; setSrx(goalSrx);
    }
    await continueSrxWithValue(payload);
  }

  async function continueSrxWithValue(value) {
    setShowTypingIndicator(true);
    const sessionName = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
    const activeSrx = enrichSrxState(srxRef.current, currentUser, sessionName);
    try {
      const data = await sendToBackend({
        message: value,
        avatarName: activeAvatar,
        user: currentUser,
        sessionName,
        packages: currentUser?.package || [],
        srxState: activeSrx,
      });
      await applyBackendResponse(data);
    } catch (err) {
      console.warn("[SRX] modal continuation failed:", err.message);
      setSrx({});
    } finally {
      setShowTypingIndicator(false);
    }
  }

  async function handlePostNeed(payload) {
    if (!currentUser?._id) {
      setShowPostNeedModal(false);
      await botReply("Please log in to post a need.", "neutral");
      return;
    }
    try {
      const res = await fetch("/api/xshares/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentUser?.token || ""}`,
        },
        body: JSON.stringify({
          type: payload.needType,
          userId: currentUser._id,
          address: payload.address,
          packageName: payload.packageName,
          byWhen: payload.byWhen,
          bloodType: payload.bloodType,
          description: payload.description,
          supportType: payload.supportType,
          location: payload.location,
        }),
      });
      await res.json().catch(() => ({}));
      setShowPostNeedModal(false);
      setSrx({});
      // Mobile parity: exact success/failure wording from Home.js handlePostNeed.
      const ok = res.status === 201;
      await botReply(ok ? "Posted your need successfully" : "Unable to post your need", ok ? "happy" : "sad");
    } catch (_) {
      setShowPostNeedModal(false);
      await botReply("Unable to post your need", "sad");
    }
  }

  function requireLoginForStore() {
    if (!currentUser) {
      botReply("Please log in to purchase BOT services.", "neutral");
      return false;
    }
    return true;
  }

  function toggleZoneAService(serviceKey, forceSelected) {
    if (!requireLoginForStore()) return;
    const price = ZONE_A_SERVICE_PRICE[serviceKey] ?? catalogBot(STORE_CATALOG, serviceKey)?.price ?? 0;
    setPackages(prev => {
      const idx = prev.findIndex(p => p.name === serviceKey);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], selected: forceSelected ?? !updated[idx].selected };
        return updated;
      }
      return [...prev, { name: serviceKey, selected: forceSelected ?? true, price }];
    });
  }

  function handleZoneAGetService(serviceKey) {
    toggleZoneAService(serviceKey, true);
  }

  function resetBotStoreNav() {
    setSelectedCategory(null);
    setBotStoreView("browse");
    setBotStoreSuccessMsg("");
  }

  async function submitBotStorePackages() {
    if (!requireLoginForStore()) return;
    try {
      const baseline = botStoreBaselineRef.current || [];
      const { subscribe, unsubscribe, selected } = computePackageChanges(packages, baseline);
      // Data-loss guard: the cart (buildPackageListFromIcons) only lists PKG_ICONS
      // keys and hides FREEMIUM, so any package the user already owns that the cart
      // never displayed is absent from `selected`. Sending only ["FREEMIUM",
      // ...selected] would drop it and the backend would strip it. Preserve every
      // owned package the cart did not manage, so a save can only ever change what
      // the user could actually see and toggle.
      const shownInCart = new Set(packages.map(p => p.name));
      const preserved = baseline.filter(n => n !== "FREEMIUM" && !shownInCart.has(n));
      const pkgList = [...new Set(["FREEMIUM", ...selected, ...preserved])];
      const price = calcMonthlyPriceFromNames(pkgList);
      const res = await fetch("/api/updatePackages", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${currentUser?.token || ""}` },
        body: JSON.stringify({
          _id: currentUser?._id,
          firstName: currentUser?.name?.split(" ")[0] || "",
          price: price.toFixed(2),
          packages: pkgList,
          subscribePackageIds: subscribe,
          unsubscribePackageIds: unsubscribe,
          currentPackages: baseline,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false || data.status === "FAILED") {
        throw new Error(data.message || "Package update failed");
      }
      // C1: People.user.packages from backend ONLY — never fall back to local pkgList.
      let updatedPkgs = Array.isArray(data.packages) ? data.packages : null;
      if (!updatedPkgs) {
        const verify = await fetch("/api/user/packages", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ _id: currentUser?._id }),
        });
        if (!verify.ok) {
          throw new Error(data.message || "Package update failed — could not re-read packages");
        }
        const v = await verify.json();
        if (!Array.isArray(v.packages)) {
          throw new Error(data.message || "Package update failed — backend did not return packages");
        }
        updatedPkgs = v.packages;
      }
      // Icons / session user.package: only from confirmed backend array
      setCurrentUser(u => ({ ...u, package: updatedPkgs }));
      botStoreBaselineRef.current = [...updatedPkgs];
      // Cart checkbox draft reset FROM Mongo result (not a surviving shadow)
      setPackages(buildPackageListFromIcons(updatedPkgs));
      // Success copy only if MBTS sent a message — no invented success string
      if (!data.message) {
        throw new Error("Package update failed — backend did not confirm with a message");
      }
      setBotStoreSuccessMsg(data.message);
      setBotStoreView("success");
    } catch (err) {
      setShowBotStore(false);
      await botReply(err?.message || "Sorry, I couldn't update your packages. Please try again.", "sad");
    }
  }

  // OTP_SERVER_VERIFY_ENABLED is off on the server: ask WordPress directly.
  const otpServerOffRef=useRef(false);
  async function postVerify(path, payload) {
    try {
      const res=await fetch(`/api/auth/${path}`,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify(payload),
      });
      return { status:res.status, data:await res.json().catch(()=>({})) };
    } catch(_) {
      // A network failure is not an answer from the gate. Returning it as an
      // ordinary non-OK step means every caller still clears its spinner and
      // lands the user on something they can act on, instead of the await
      // rejecting and leaving the chat sitting on a typing indicator.
      return { status:0, data:{ status:"ERROR", message:"We could not reach the server. Please try again." } };
    }
  }
  function endVerification() {
    verifyRef.current=null; setStep(null); setShowYesNo(false); setCatchphrasePopup(null);
  }
  async function startVerification(firstName) {
    const name=firstName||sessionFirstName||sessionStorage.getItem("btcg_session_name")||""; if(!name) return;
    setShowTypingIndicator(true);
    try {
      const { status, data }=await postVerify("verify/start",{ firstName:name });
      setShowTypingIndicator(false);
      verifyRef.current={ firstName:name };
      await applyVerifyStep(status, data);
    } catch(_) {
      setShowTypingIndicator(false); endVerification();
      await botReply("We could not reach the server. Please try again.","sad");
    }
  }
  // The gate replies with the step to take next; every branch it can return is
  // handled here so an abandoned session can never leave the chat waiting.
  async function applyVerifyStep(status, data) {
    if (status===429) { endVerification(); await botReply(data?.message||"Too many attempts. Please try again in a few minutes.","sad"); return; }
    if (status===401 || data?.next==="expired") { endVerification(); await botReply('That took a little too long, so I closed the session. Say "login" to start again.',"neutral"); return; }
    if (data?.status!=="OK") { endVerification(); await botReply(data?.message||"Something went wrong. Please try again.","sad"); return; }
    if (data.registered===false) { endVerification(); await botReply("You're not registered with BOTCIERGE®. Please register at BOTCIERGE.com","sad"); return; }
    if (data.sessionToken) verifyRef.current={ ...verifyRef.current, sessionToken:data.sessionToken };

    if (data.next==="identify") {
      setShowYesNo(false); setStep("awaiting_identify");
      await botReply(data.message||"What's your first name, or the email address on your account?","neutral");
      return;
    }
    if (data.next==="reset_offer") {
      verifyRef.current={ ...verifyRef.current, resetOffer:true };
      setStep(null); setShowYesNo(true);
      await botReply(data.message||"That didn't match. Shall I email you a code instead?","neutral");
      return;
    }
    if (verifyRef.current?.resetOffer) verifyRef.current={ ...verifyRef.current, resetOffer:false };
    if (data.next==="confirm") {
      setStep(null); setShowYesNo(true);
      // The gate sends the identity as fields rather than a finished sentence, so
      // the wording is ours — and it has no state field to offer.
      await botReply(`I have a ${verifyRef.current?.firstName} ${data.confirm?.lastName} in ${data.confirm?.homeCity}. Is that you?`,"neutral");
      return;
    }
    if (data.next==="question") {
      // Confirmed from the live database: Security_Question_Registry documents
      // are shaped { _id, id, question: "..." }, and the field is `question`.
      // The gate may pass the string straight through at data.question, or the
      // whole document with the string on its `.question` sub-field — either
      // shape resolves here. The older guesses stay in the list beneath in the
      // stated order, so an unfamiliar shape still renders a real prompt rather
      // than dropping us into an unanswerable question step. Security questions
      // are optional at registration, so an empty payload is a real case; when
      // no candidate yields text we fall through instead.
      const q=data.question;
      const label=
        (typeof q==="string" && q.trim()) ? q :
        (q && typeof q==="object")
          ? [q.question,q.label,q.questionPhrase,q.text,q.prompt].find(v=>typeof v==="string" && v.trim())
          : null;
      if (label) {
        setShowYesNo(false); setStep("awaiting_verify_answer");
        await botReply(label,"neutral");
        return;
      }
    }
    if (data.next==="catchphrase_required") {
      // Also capture email/maskedEmail if the gate supplied them here — three
      // failed catchphrase attempts falls back to the OTP terminal and needs
      // both to open the modal without another round trip.
      verifyRef.current={
        ...verifyRef.current,
        verificationToken:data.verificationToken,
        catchphraseAttempts:0,
        email:data.email||verifyRef.current?.email||"",
        maskedEmail:data.maskedEmail||verifyRef.current?.maskedEmail||"",
      };
      setShowYesNo(false); setStep("awaiting_catchphrase");
      if (CATCHPHRASE_POPUP_ENABLED) setCatchphrasePopup({ busy:false, error:"" });
      await botReply("Enter your BOTCIERGE Catchphrase","neutral");
      return;
    }
    if (data.next==="otp_required") {
      // Keep the verification token: the email step below is what redeems it.
      verifyRef.current={ ...verifyRef.current, verificationToken:data.verificationToken };
      setShowYesNo(false); setStep(null); setOtpCode("");
      setOtpModal({ stage:"email", email:data.email, maskedEmail:data.maskedEmail });
      await botReply(`One more step — let's confirm ${data.maskedEmail}.`,"neutral");
      return;
    }
    // Split deliberately. The gate's own words win when it supplies them, and a
    // signal we can read as a rejection keeps the verbatim rejection line. Every
    // other case — an unrecognised step, a missing `next`, a question we could
    // not render — is our failure to understand the reply, not a finding about
    // the user, so it must never tell someone verified that they are unregistered.
    endVerification();
    if (data.message) { await botReply(data.message,"sad"); return; }
    if (data.verified===false) {
      await botReply("Based on your responses I cannot verify your identity. Please register at BOTCIERGE.com","sad");
      return;
    }
    await botReply("Something went wrong. Please try again.","sad");
  }
  async function openSecurityQuestionsFor(user) {
    await botReply("Your old security questions are cleared. Please choose new ones.","neutral");
    const sessionName = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
    try {
      const data = await sendToBackend({ message:"change my security questions", avatarName:activeAvatar, user, sessionName, packages:user?.package||[], srxState:null });
      await applyBackendResponse(data);
    } catch(_) {
      await botReply('Say "change my security questions" to choose new ones.',"neutral");
    }
  }
  async function finishVerifiedLogin(userId, loadFailMsg) {
    endVerification();
    let user;
    try { user=await loadProfile(userId); }
    catch(_) { await botReply(loadFailMsg||"Could not identify your account. Please log in again.","sad"); return null; }
    setCurrentUser(user);
    if (user.backgroundId) { const savedBg=BG_GALLERY.find(b=>b.id===user.backgroundId); if(savedBg) setGalleryBg(savedBg); }
    // Client asked for the Activity Ledger check to be removed from login. The
    // ledger still runs mid-conversation when the backend asks for it — see the
    // data.type==="activityLedger" branch in handleBackendResponse — this only
    // stops the unprompted check that fired on every successful sign-in.
    const { avatarName:registeredAvatar, isWebAvatar, avatarType:savedType }=savedAvatarPrefs(user);
    // Show the host in the mode the person saved (2D or 3D), not whatever this
    // page was left on. Applied before the host changes, so the takeover loads
    // the saved mode directly.
    if (LOGIN_AVATAR_TYPE_ENABLED && savedType && savedType!==botState.avatarType) {
      controlBotcierge({ avatarType:savedType, emotion:"happy" });
      // Say "verified" in the voice of the mode they saved, once it is on screen:
      // sent straight away it went to the old mode's voice, which the switch cuts off.
      avatarTypeRef.current = savedType;
      await waitForHostVoice(savedType);
    }
    if (isWebAvatar && registeredAvatar!==activeAvatar) {
      const authMsg   =`I have successfully verified your identity.`;
      const bowOutMsg1=`I see you've chosen ${registeredAvatar} to be your host`;
      const bowOutMsg2=`Bye ${sessionFirstName||user.name}. ${registeredAvatar} will take care of you from here :)`;
      await botReply(`${authMsg} ${bowOutMsg1}`,"happy");
      setTimeout(()=>{
        setMessages(p=>[...p,{text:bowOutMsg2,time:formatTime()}]);
        speakAvatar(bowOutMsg2);
        // FIX (Biz bow-out): pass the freshly logged-in `user` — the `currentUser`
        // closure here is stale (still null right after login), which wiped auth
        // and left the takeover greeting blank.
        setTimeout(()=>{ window._bowOutInProgress=true; window._bowOutUser=user; setMessages([]); window._pendingGreeting=null; requestAnimationFrame(()=>setActiveAvatar(registeredAvatar)); },15000);
      },7000);
    } else {
      // Client asked for the post-login backend round trip to be removed — the
      // srxType:"auth_success" call was what surfaced the intentRoutes "bit
      // beyond" message. Our own greeting stands alone so login is never silent.
      await botReply("I have successfully verified your identity. How may I assist you?","happy");
    }
    return user;
  }
  async function closeOtpModal() {
    setOtpModal(null); setOtpCode(""); setOtpResendIn(0); endVerification();
    await botReply('Okay, sign-in cancelled. Say "login" whenever you are ready.',"neutral");
  }
  // Redeem the verification token now that the account's email has been proven,
  // either by the emailed code or by a Google token the RP validated.
  async function completeAfterEmailProof() {
    // Read before the call: finishVerifiedLogin clears verifyRef on its way in,
    // and catchphrase/set needs this same token.
    const verificationToken=verifyRef.current?.verificationToken;
    const fromCatchphrase=verifyRef.current?.catchphraseAttempts!==undefined;
    const { status, data }=await postVerify("verify/complete",{ verificationToken });
    // After a catchphrase, an emailed code cannot finish sign-in until the server
    // can check the code itself (step binding); Google can, so point there.
    if (status===401 && fromCatchphrase) {
      setOtpModal(m=>m?{ ...m, busy:false, error:'For your security, an account with a catchphrase cannot finish with an emailed code yet. Use "Continue with Google", or try your catchphrase again.' }:m);
      return;
    }
    if (status===200 && data?.status==="OK") {
      setOtpModal(null); setOtpCode("");
      const user = await finishVerifiedLogin(data.data?._id);
      // Recovery after 3 wrong security answers: the old ones are cleared, so
      // open the security questions form straight away.
      if (data.resetSecurityQuestions && user) { await openSecurityQuestionsFor(user); return; }
      setCatchPhraseOffer({ verificationToken, value:"", confirm:"", busy:false, error:"" });
      return;
    }
    setOtpModal(m=>m?{ ...m, busy:false, error:data?.message||"Could not finish signing you in. Please try again." }:m);
  }
  // Purely an offer: the user is already signed in by the time it appears, so
  // declining costs nothing and holds nothing up.
  async function saveCatchPhrase() {
    const catchphrase=(catchPhraseOffer?.value||"").trim(); if(!catchphrase) return;
    // Same rule the gate enforces, run before the request so the answer is
    // immediate. Trimmed first, so leading and trailing whitespace never counts
    // toward the length and never reaches the gate; internal spaces are kept.
    if (/[\t\r\n]/.test(catchphrase)) {
      setCatchPhraseOffer(o=>o?{ ...o, error:"Your catchphrase cannot contain tabs or line breaks." }:o);
      return;
    }
    if (catchphrase.length<CATCHPHRASE_MIN) {
      setCatchPhraseOffer(o=>o?{ ...o, error:`Your catchphrase must be at least ${CATCHPHRASE_MIN} characters.` }:o);
      return;
    }
    if (catchphrase.length>CATCHPHRASE_MAX) {
      setCatchPhraseOffer(o=>o?{ ...o, error:`Your catchphrase must be ${CATCHPHRASE_MAX} characters or fewer.` }:o);
      return;
    }
    // Re-entry has to agree before anything is sent — the shipped app confirms a
    // new catchphrase rather than saving whatever was typed once.
    if (catchphrase!==(catchPhraseOffer?.confirm||"").trim()) {
      setCatchPhraseOffer(o=>o?{ ...o, error:"Your catchphrases do not match." }:o);
      return;
    }
    setCatchPhraseOffer(o=>o?{ ...o, busy:true, error:"" }:o);
    try {
      const { status, data }=await postVerify("catchphrase/set",{ verificationToken:catchPhraseOffer.verificationToken, catchphrase });
      if (status===200 && data?.status==="OK") {
        setCatchPhraseOffer(null);
        await botReply("Your BOTCIERGE Catchphrase is set! Next time, just enter it to log in.","happy");
        return;
      }
      setCatchPhraseOffer(o=>o?{ ...o, busy:false, error:data?.message||"Could not save your catchphrase. Try again." }:o);
    } catch(_) {
      setCatchPhraseOffer(o=>o?{ ...o, busy:false, error:"We could not reach the server. Please try again." }:o);
    }
  }
  async function sendOtpCode() {
    // Email is the default security question: on the email stage the user must
    // produce the address, we never present it. Compare typed input to the
    // account email (trim + lowercase, exact). A mismatch stays on the modal
    // with the masked hint and sends nothing — the verification token lives on,
    // so this is retryable, not a lockout. Resend (code stage) has already
    // cleared this challenge, so it skips the check.
    if (otpModal.stage==="email") {
      // Read the field itself first. A browser autofill can populate the input
      // without firing React's onChange, which leaves emailInput empty while the
      // user plainly sees their address on screen — their first press then fails
      // and the second succeeds once a keystroke syncs state. The DOM value is
      // the source of truth at submit time; state is the fallback.
      const typed=(otpEmailRef.current?.value ?? otpModal.emailInput ?? "").trim().toLowerCase();
      const account=(otpModal.email||"").trim().toLowerCase();
      if (!typed) {
        setOtpModal(m=>m?{ ...m, busy:false, error:"Please enter the email address on your account." }:m);
        return;
      }
      if (typed!==account) {
        setOtpModal(m=>m?{ ...m, busy:false, error:`That doesn't match the email on your account. Hint: ${m.maskedEmail||"the address on file"}` }:m);
        return;
      }
    }
    setOtpModal(m=>({ ...m, busy:true, error:"" }));
    try {
      // OTP_SERVER_VERIFY_ENABLED: MBTS sends and checks the code (it asks
      // helperbots.ai itself); WordPress directly only if that check is off.
      let res=await fetch("/api/auth/otp/request",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ verificationToken:verifyRef.current?.verificationToken }),
      });
      if (res.status===404) {
        otpServerOffRef.current=true;
        res=await fetch(`${AUTH_RP_BASE}otp/request`,{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ email:otpModal.email }),
        });
      }
      // fetch only rejects on a transport error, so a 4xx/5xx lands here looking
      // like success. Advancing on one would ask the user for a code that was
      // never sent, and every attempt would then read as a wrong code.
      if (!res.ok) {
        const body=await res.json().catch(()=>({}));
        const reason={
          no_user:"We could not find an account with that email. Please register first or try another email.",
          too_many_attempts:'Too many attempts. Tap "Resend code" to start over.',
        }[body?.error];
        setOtpModal(m=>({ ...m, busy:false, error:reason||"Could not send a code right now. Please try again." }));
        return;
      }
      setOtpCode("");
      setOtpResendIn(OTP_RESEND_COOLDOWN_SECONDS);
      setOtpModal(m=>({ ...m, stage:"code", busy:false, error:"" }));
    } catch(_) {
      setOtpModal(m=>({ ...m, busy:false, error:"Could not send a code right now. Please try again." }));
    }
  }
  async function submitOtpCode() {
    const code=otpCode.trim(); if(!code) return;
    setOtpModal(m=>({ ...m, busy:true, error:"" }));
    let result=null;
    try {
      if (!otpServerOffRef.current) {
        const res=await fetch("/api/auth/otp/verify",{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ verificationToken:verifyRef.current?.verificationToken, code }),
        });
        const data=await res.json().catch(()=>({}));
        if (res.status===404) otpServerOffRef.current=true;
        else if (res.ok && data?.verificationToken) {
          // The one-use token that finishes this sign-in (and sets the catchphrase).
          if (verifyRef.current) verifyRef.current={ ...verifyRef.current, verificationToken:data.verificationToken };
          result={ ok:true };
        } else result={ ok:false, error:data?.error };
      }
      if (otpServerOffRef.current) {
        const res=await fetch(`${AUTH_RP_BASE}otp/verify`,{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ email:otpModal.email, code }),
        });
        result=await res.json().catch(()=>({}));
      }
    } catch(_) {}
    if (!result?.ok) {
      const reason={
        invalid_code:"That code is incorrect. Please check and try again.",
        expired:'That code has expired. Tap "Resend code" to get a new one.',
        too_many_attempts:'Too many attempts. Tap "Resend code" to start over.',
      }[result?.error];
      setOtpModal(m=>({ ...m, busy:false, error:reason||"That code did not work. Please try again." }));
      return;
    }
    await completeAfterEmailProof();
  }
  // Google hands back a signed credential; the RP is what validates it, and the
  // email it returns has to be the account the gate already recognised.
  async function handleGoogleCredential(response) {
    setOtpModal(m=>({ ...m, busy:true, error:"" }));
    const account=otpModal?.email||"";
    const masked=otpModal?.maskedEmail||"the address on your account";
    let verifiedEmail=""; let failure="";
    try {
      const res=await fetch(`${AUTH_RP_BASE}google/login`,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ id_token:response?.credential }),
      });
      const data=await res.json().catch(()=>({}));
      verifiedEmail=(data?.email||"").trim();
      failure=data?.code||"";
    } catch(_) {}
    if (!verifiedEmail) {
      const reason={
        no_user:"We could not find a matching account for this Google email. Please register first.",
        email_not_verified:"Your Google email address is not verified. Please verify it in Google and try again.",
        invalid_google_audience:"This Google sign-in configuration does not match the app yet. Please try again later.",
        google_client_ids_missing:"Google sign-in is not fully configured yet. Please try again later.",
      }[failure];
      setOtpModal(m=>({ ...m, busy:false, error:reason||"We could not verify your Google account right now. Please try again." }));
      return;
    }
    // DELIBERATE DIVERGENCE from the shipped app, which stops at "did not match".
    // Someone signed in to two Google accounts cannot act on that — they have no
    // way to know which one this record wants. We name it masked, never in full,
    // and point at the emailed code as the way through.
    if (verifiedEmail.toLowerCase()!==account.toLowerCase()) {
      // Never signs in a different person: the Google account must be the
      // identified person's (24 Oct: named, as Biz asked).
      const who=verifyRef.current?.firstName ? `${verifyRef.current.firstName}'s account` : `this account (${masked})`;
      setOtpModal(m=>({ ...m, busy:false,
        error:`That Google account doesn't match ${who}. Choose the Google account for ${masked}, or use "Email me a code" instead.` }));
      return;
    }
    if (GOOGLE_SERVER_VERIFY_ENABLED) { await completeWithGoogle(response?.credential); return; }
    await completeAfterEmailProof();
  }
  // GOOGLE_SERVER_VERIFY_ENABLED: MBTS verifies the Google ID token itself
  // (/auth/google/complete). It also accepts the catchphrase-step token, so the
  // "3 wrong catchphrases -> Google" fallback finishes with step binding on.
  async function completeWithGoogle(idToken) {
    const verificationToken=verifyRef.current?.verificationToken;
    const fromCatchphrase=verifyRef.current?.catchphraseAttempts!==undefined;
    const { status, data }=await postVerify("google/complete",{ verificationToken, idToken });
    if (status===404 && !data?.data) { await completeAfterEmailProof(); return; } // switched off on the server
    if (status===200 && data?.status==="OK") {
      setOtpModal(null); setOtpCode("");
      await finishVerifiedLogin(data.data?._id);
      if (!fromCatchphrase) setCatchPhraseOffer({ verificationToken, value:"", confirm:"", busy:false, error:"" });
      return;
    }
    setOtpModal(m=>m?{ ...m, busy:false, error:data?.message||"We could not verify your Google account right now. Please try again." }:m);
  }
  useEffect(() => {
    if (otpResendIn<=0) return;
    const t=setTimeout(()=>setOtpResendIn(s=>s-1),1000);
    return ()=>clearTimeout(t);
  }, [otpResendIn]);
  useEffect(() => {
    if (otpModal?.stage!=="email" || !GOOGLE_WEB_CLIENT_ID) return;
    let cancelled=false;
    // The GIS script is async/defer, so it can still be in flight when the modal
    // opens. Returning early on that race would leave the button permanently
    // missing for the rest of the session, with nothing to re-trigger this.
    const render=() => {
      if (cancelled) return true;
      const gsi=window.google?.accounts?.id;
      if (!gsi || !googleBtnRef.current) return false;
      gsi.initialize({ client_id:GOOGLE_WEB_CLIENT_ID, callback:handleGoogleCredential });
      // size "medium": Google never personalizes it ("Sign in as <browser account>"
      // confused people when that account was not the one being confirmed).
      gsi.renderButton(googleBtnRef.current,{ theme:"filled_blue", size:"medium", text:"signin_with" });
      return true;
    };
    if (render()) return;
    const poll=setInterval(()=>{ if(render()) clearInterval(poll); },200);
    // The emailed code is always available, so give up quietly rather than
    // polling for the rest of the session.
    const giveUp=setTimeout(()=>clearInterval(poll),10000);
    return ()=>{ cancelled=true; clearInterval(poll); clearTimeout(giveUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpModal?.stage, otpModal?.email]);

  // After 3 wrong security answers the yes/no is "email me a code?" (verify/reset).
  const yesNoStep = () => (verifyRef.current?.resetOffer ? "verify/reset" : "verify/confirm");
  async function handleYes() {
    setShowYesNo(false); setMessages(p=>[...p,{text:"Yes",time:formatTime(),isUser:true}]);
    setShowTypingIndicator(true);
    const { status, data }=await postVerify(yesNoStep(),{ sessionToken:verifyRef.current?.sessionToken, answer:"yes" });
    setShowTypingIndicator(false);
    await applyVerifyStep(status, data);
  }
  async function handleNo() {
    setShowYesNo(false); setMessages(p=>[...p,{text:"No",time:formatTime(),isUser:true}]);
    setShowTypingIndicator(true);
    // "no" moves to the next account sharing this first name, so the remembered
    // name stays put — the gate decides when there is nobody left to offer.
    const { status, data }=await postVerify(yesNoStep(),{ sessionToken:verifyRef.current?.sessionToken, answer:"no" });
    setShowTypingIndicator(false);
    await applyVerifyStep(status, data);
  }
  function handleInputChange(e) {
    const val=e.target.value; setInputText(val);
    setIsTyping(true); clearTimeout(typingRef.current);
    typingRef.current=setTimeout(()=>setIsTyping(false),1500);
    const d=detectEmotion(val);
    if(d){setActiveEmotion(d);controlBotcierge({avatarType:botState.avatarType,emotion:d});}
  }

  const logoSize      = isSmall?"36px":"52px";
  const headerPad     = isSmall?"0 12px":"4px 20px";
  const headerSpacerH = isSmall?(currentUser?"calc(64px + env(safe-area-inset-top, 0px))":"calc(52px + env(safe-area-inset-top, 0px))"):"64px";
  const titleSize     = isSmall?"14px":"22px";
  const pillFontSize  = isSmall?"11px":"13px";
  const avatarHeight  = isMobile?"180px":isTablet?"240px":"320px";
  const sceneBtnSize  = isSmall?"28px":"36px";
  const scenePad      = isSmall?"5px 10px":"7px 16px";
  const inputPad      = isSmall?"6px 8px":"10px 16px";
  const menuWidth     = isSmall?"min(260px,85vw)":"270px";
  const avatarBgColor = theme.bgStyle||"transparent";
  const inputPlaceholder =
    ledgerPrompt                                   ? "type yes or no" :
    (greetingAskedName&&!currentUser)            ? "share your name" :
    showYesNo                                     ? "type yes or no" :
    loginStepRef.current==="awaiting_verify_name"     ? "share your first name" :
    loginStepRef.current==="awaiting_verify_answer"   ? "answer the question" :
    loginStepRef.current==="awaiting_identify"        ? "type your first name or email" :
    loginStepRef.current==="awaiting_catchphrase"     ? "enter your BOTCIERGE Catchphrase" :
    (srx?.type === "input" && srx?.inputType === "any") ? "describe it or tap the photo icon" :
    "share a need or intent";
  const showRememBotPhoto = srx?.type === "input" && srx?.inputType === "any";
  // The chat input holds a catchphrase or a security answer: hidden, with an eye to show it.
  const maskedEntry = loginStepRef.current==="awaiting_catchphrase" || loginStepRef.current==="awaiting_verify_answer" || srx?.pendingAction==="SECQ_ANSWER";

  // Stable, so the panel's history listener is not re-added on every render.
  const closeLinkPanel = useCallback(() => setShowLinkModal(false), []);
  function openSearchLink(url, title, external) {
    if (!url) return;
    if (external) { window.open(url, "_blank", "noopener"); return; }
    setLinkModalUrl(url);
    setLinkModalTitle(title || url);
    setShowLinkModal(true);
  }

  return (
    <>
      {/* ── SPLASH SCREEN ── */}
      {!avatarReady && (
        <div style={{ position:"fixed",inset:0,zIndex:99999,background:"linear-gradient(135deg,#3B1F6E 0%,#6B4EAA 50%,#3B1F6E 100%)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"24px",cursor:engineReady?"pointer":"default" }}
          onClick={()=>{
            if(!engineReady) return;
            if(window.headEngineRef?.current?.head?.audioCtx?.state==="suspended") { window.headEngineRef.current.head.audioCtx.resume(); }
            if(headEngineRef.current?.head?.audioCtx?.state==="suspended") { headEngineRef.current.head.audioCtx.resume(); }
            setAvatarReady(true);
            window._greetingDone = false;
            if(window._pendingGreeting){
              const greetingText = window._pendingGreeting;
              setMessages([{text:greetingText, time:formatTime()}]);
              window._pendingGreeting = null;
              setTimeout(() => {
                if(window._greetingDone) return;
                window._greetingDone = true;
                const tts = window.currentAvatarTTS;
                if(tts) {
                  const avatarObj = window.currentAvatarObject;
                  const doSetupAndSpeak = () => {
                    try {
                      if(tts.setup) {
                        tts.setup({ voice:avatarObj?.voice||"af_bella", language:avatarObj?.language||"en-us", speed:avatarObj?.speed||1, audioEncoding:avatarObj?.encoding||"wav" });
                      }
                      tts.synthesize({ input: greetingText });
                      console.log("[Greeting] ✅ Splash tap spoke");
                    } catch(e) { console.warn("[Greeting] TTS failed:", e.message); }
                  };
                  // GREETING_PREFETCH: the avatar fetched this greeting while it loaded;
                  // play it with the wave now instead of synthesizing it again.
                  const pre = window._greetingPrefetch;
                  const head = window.currentAvatarHead;
                  if (pre && head && pre.instance === window._avatarInstanceId) {
                    const ready = Promise.race([pre.ready, new Promise((r) => setTimeout(r, 3000))]);
                    ready.then(() => {
                      if (pre.fetch.text === greetingText && pre.fetch.audio) {
                        try { head.playGesture("👋", 1.5); } catch(_) {}
                        try { pre.fetch.audio.forEach((d) => head.speakAudio(d, { mood:"happy" }, () => {})); console.log("[Greeting] ✅ Splash tap spoke (pre-fetched)"); return; } catch(_) {}
                      }
                      tts.connect(null, () => {}).then(doSetupAndSpeak).catch(doSetupAndSpeak);
                    });
                    return;
                  }
                  // FIX 12: tts.ws is undefined on deployed HeadTTS — ALWAYS reconnect before speaking.
                  // GLB takes 60s+ on Heroku, original WS dies (code 1006). connect() is safe if already open.
                  console.log("[Greeting] Reconnecting WS before splash speak...");
                  tts.connect(null, () => {})
                    .then(() => { console.log("[Greeting] ✅ WS ready"); doSetupAndSpeak(); })
                    .catch(() => { doSetupAndSpeak(); });
                }
              }, 400);
            }
          }}>
          <div style={{ position:"relative",width:"120px",height:"120px",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <div style={{ position:"absolute",inset:0,borderRadius:"50%",border:"3px solid transparent",borderTopColor:"#D4ADFF",borderRightColor:"#D4ADFF",animation:"splashSpin 1s linear infinite" }} />
            <div style={{ position:"absolute",inset:"12px",borderRadius:"50%",border:"2px solid transparent",borderBottomColor:"rgba(255,255,255,0.4)",borderLeftColor:"rgba(255,255,255,0.4)",animation:"splashSpinReverse 1.5s linear infinite" }} />
            <img src="/boticon.png" alt="BOTCIERGE" style={{ width:"64px",height:"64px",objectFit:"contain",filter:"drop-shadow(0 0 20px rgba(180,130,255,0.8))",animation:"splashPulse 2s ease-in-out infinite" }} />
          </div>
          <div style={{ color:"#fff",fontSize:"28px",fontWeight:"800",letterSpacing:"4px",textTransform:"uppercase",textShadow:"0 0 30px rgba(180,130,255,0.6)" }}>
            BOTCIERGE<span style={{color:"#D4ADFF"}}>®</span>
          </div>
          {!engineReady ? (
            <div style={{display:"flex",gap:"8px",marginTop:"8px"}}>
              {[0,1,2].map(i=><div key={i} style={{width:"8px",height:"8px",borderRadius:"50%",background:"rgba(255,255,255,0.6)",animation:`splashDot 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}
            </div>
          ) : (
            <div style={{marginTop:"8px",padding:"14px 40px",borderRadius:"32px",background:"rgba(255,255,255,0.15)",border:"2px solid rgba(255,255,255,0.5)",color:"#fff",fontSize:"16px",fontWeight:"700",letterSpacing:"2px",animation:"splashPulse 1.5s ease-in-out infinite",backdropFilter:"blur(10px)"}}>
              ▶ TAP TO START
            </div>
          )}
        </div>
      )}

    <div style={{display:"flex",flexDirection:"column",width:"100%",height:"100dvh",minHeight:"-webkit-fill-available",background:theme.chatBg,fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",overflow:"hidden",transition:"all 0.4s ease",opacity:pageVisible?1:0,transform:pageVisible?"translateY(0)":"translateY(12px)",position:"relative"}}>
      {flash && <div style={{position:"fixed",inset:0,background:theme.primary,zIndex:9999,animation:"flashIn 0.22s ease forwards",pointerEvents:"none"}}/>}
      {/* ── In-app link viewer — search results stay on chatcamille ── */}
      <LinkPanel open={showLinkModal} url={linkModalUrl} title={linkModalTitle} onClose={closeLinkPanel} />

      {otpModal && (
        <div style={{position:"fixed",inset:0,zIndex:9800,background:"rgba(0,0,0,0.9)",display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}>
          <div style={{width:"min(360px,100%)",background:"rgba(28,18,52,0.98)",border:"1px solid rgba(155,127,212,0.45)",borderRadius:"16px",padding:"20px",display:"flex",flexDirection:"column",gap:"12px"}}>
            <div style={{fontSize:"15px",fontWeight:"700",color:"#D4ADFF"}}>Confirm it's you</div>
            {otpModal.stage==="email" ? (
              <>
                <div style={{fontSize:"12px",color:"rgba(255,255,255,0.65)"}}>Enter the email address on your account. We'll send a one-time code once it matches.</div>
                <input ref={otpEmailRef} value={otpModal.emailInput||""} onChange={e=>setOtpModal(m=>m?{ ...m, emailInput:e.target.value, error:"" }:m)}
                  type="email" inputMode="email" autoComplete="off" autoCapitalize="none" spellCheck={false} autoFocus
                  onKeyDown={e=>{ if(e.key==="Enter") sendOtpCode(); }} placeholder="Your account email"
                  style={{padding:"10px 12px",borderRadius:"10px",border:"1px solid rgba(155,127,212,0.35)",background:"rgba(255,255,255,0.06)",color:"#fff",fontSize:"13px"}}/>
                <button onClick={sendOtpCode} disabled={otpModal.busy}
                  style={{padding:"11px 0",borderRadius:"10px",border:"none",background:otpModal.busy?"rgba(107,78,170,0.4)":"linear-gradient(135deg,#6B4EAA,#9B7FD4)",color:"#fff",fontSize:"13px",fontWeight:"700",cursor:otpModal.busy?"default":"pointer"}}>
                  {otpModal.busy?"Sending…":"Email me a code"}
                </button>
                {GOOGLE_WEB_CLIENT_ID && (
                  <>
                    <div style={{display:"flex",alignItems:"center",gap:"8px",color:"rgba(255,255,255,0.35)",fontSize:"11px"}}>
                      <span style={{flex:1,height:"1px",background:"rgba(255,255,255,0.15)"}}/>or<span style={{flex:1,height:"1px",background:"rgba(255,255,255,0.15)"}}/>
                    </div>
                    <div ref={googleBtnRef} style={{display:"flex",justifyContent:"center"}}/>
                  </>
                )}
              </>
            ) : (
              <>
                <div style={{fontSize:"12px",color:"rgba(255,255,255,0.65)"}}>Enter the code we sent to {otpModal.maskedEmail}.</div>
                <input value={otpCode} onChange={e=>setOtpCode(e.target.value)} inputMode="numeric" autoFocus
                  onKeyDown={e=>{ if(e.key==="Enter") submitOtpCode(); }} placeholder="6-digit code"
                  style={{padding:"10px 12px",borderRadius:"10px",border:"1px solid rgba(155,127,212,0.35)",background:"rgba(255,255,255,0.06)",color:"#fff",fontSize:"15px",letterSpacing:"3px",textAlign:"center"}}/>
                <button onClick={submitOtpCode} disabled={otpModal.busy||!otpCode.trim()}
                  style={{padding:"11px 0",borderRadius:"10px",border:"none",background:(otpModal.busy||!otpCode.trim())?"rgba(107,78,170,0.4)":"linear-gradient(135deg,#6B4EAA,#9B7FD4)",color:"#fff",fontSize:"13px",fontWeight:"700",cursor:(otpModal.busy||!otpCode.trim())?"default":"pointer"}}>
                  {otpModal.busy?"Checking…":"Verify"}
                </button>
                <button onClick={sendOtpCode} disabled={otpModal.busy||otpResendIn>0}
                  style={{border:"none",background:"transparent",color:(otpModal.busy||otpResendIn>0)?"rgba(255,255,255,0.35)":"#9B7FD4",fontSize:"12px",cursor:(otpModal.busy||otpResendIn>0)?"default":"pointer"}}>
                  {otpResendIn>0?`Resend code in ${otpResendIn}s`:"Resend code"}
                </button>
              </>
            )}
            {otpModal.error && <div style={{fontSize:"12px",color:"#ef4444"}}>{otpModal.error}</div>}
            <button onClick={closeOtpModal}
              style={{border:"none",background:"transparent",color:"rgba(255,255,255,0.45)",fontSize:"12px",cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      {catchPhraseOffer && (
        <div style={{position:"fixed",inset:0,zIndex:9800,background:"rgba(0,0,0,0.9)",display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}>
          <div style={{width:"min(360px,100%)",background:"rgba(28,18,52,0.98)",border:"1px solid rgba(155,127,212,0.45)",borderRadius:"16px",padding:"20px",display:"flex",flexDirection:"column",gap:"12px"}}>
            <div style={{fontSize:"15px",fontWeight:"700",color:"#D4ADFF"}}>Create your BOTCIERGE Catchphrase</div>
            <div style={{fontSize:"12px",color:"rgba(255,255,255,0.65)"}}>Catchphrases can contain letters, numbers, symbols, and spaces. You'll use it to log in next time.</div>
            <input value={catchPhraseOffer.value} onChange={e=>setCatchPhraseOffer(o=>o?{ ...o, value:e.target.value, error:"" }:o)} autoFocus
              type="password" autoComplete="new-password" placeholder="Enter your BOTCIERGE Catchphrase"
              style={{padding:"10px 12px",borderRadius:"10px",border:"1px solid rgba(155,127,212,0.35)",background:"rgba(255,255,255,0.06)",color:"#fff",fontSize:"14px"}}/>
            <input value={catchPhraseOffer.confirm} onChange={e=>setCatchPhraseOffer(o=>o?{ ...o, confirm:e.target.value, error:"" }:o)}
              type="password" autoComplete="new-password"
              onKeyDown={e=>{ if(e.key==="Enter") saveCatchPhrase(); }} placeholder="Re-enter your BOTCIERGE Catchphrase"
              style={{padding:"10px 12px",borderRadius:"10px",border:"1px solid rgba(155,127,212,0.35)",background:"rgba(255,255,255,0.06)",color:"#fff",fontSize:"14px"}}/>
            <button onClick={saveCatchPhrase} disabled={catchPhraseOffer.busy||!catchPhraseOffer.value.trim()||!catchPhraseOffer.confirm.trim()}
              style={{padding:"11px 0",borderRadius:"10px",border:"none",background:(catchPhraseOffer.busy||!catchPhraseOffer.value.trim()||!catchPhraseOffer.confirm.trim())?"rgba(107,78,170,0.4)":"linear-gradient(135deg,#6B4EAA,#9B7FD4)",color:"#fff",fontSize:"13px",fontWeight:"700",cursor:(catchPhraseOffer.busy||!catchPhraseOffer.value.trim()||!catchPhraseOffer.confirm.trim())?"default":"pointer"}}>
              {catchPhraseOffer.busy?"Saving…":"Create Catchphrase"}
            </button>
            {catchPhraseOffer.error && <div style={{fontSize:"12px",color:"#ef4444"}}>{catchPhraseOffer.error}</div>}
            <button onClick={()=>setCatchPhraseOffer(null)}
              style={{border:"none",background:"transparent",color:"rgba(255,255,255,0.45)",fontSize:"12px",cursor:"pointer"}}>Not now</button>
          </div>
        </div>
      )}

      {/* Avatar loading overlay */}
      {avatarLoading && avatarReady && botState.avatarType === "3D" && (
        <div style={{position:"fixed",inset:0,zIndex:9000,background:"rgba(30,10,60,0.85)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"16px",backdropFilter:"blur(8px)"}}>
          <img src="/boticon.png" alt="loading" style={{width:"56px",height:"56px",objectFit:"contain",animation:"splashPulse 1.2s ease-in-out infinite",filter:"drop-shadow(0 0 16px rgba(180,130,255,0.9))"}}/>
          <div style={{color:"#D4ADFF",fontSize:"14px",fontWeight:"700",letterSpacing:"2px"}}>Loading {activeAvatar}...</div>
          <div style={{display:"flex",gap:"6px"}}>{[0,1,2].map(i=><div key={i} style={{width:"7px",height:"7px",borderRadius:"50%",background:"rgba(255,255,255,0.6)",animation:`splashDot 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}</div>
        </div>
      )}

      {/* ── Sprint 5: SCENES Gallery Modal ── */}
      {showScenesGallery && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,backdropFilter:"blur(12px)"}}
          onClick={()=>setShowScenesGallery(false)}>
          <div style={{background:theme.headerBg,border:`1px solid ${theme.sectionBorder}`,borderRadius:"20px",padding:"24px",width:"min(680px,96vw)",maxHeight:"85vh",display:"flex",flexDirection:"column",gap:"16px",boxShadow:"0 24px 64px rgba(0,0,0,0.6)",backdropFilter:"blur(24px)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:"18px",fontWeight:"800",color:theme.pill}}>🎬 Scenes</div>
                <div style={{fontSize:"11px",color:theme.textMeta,marginTop:"2px"}}>{currentUser?"Your selection saves to your profile":"Login to save your background preference"}</div>
              </div>
              <button onClick={()=>setShowScenesGallery(false)} style={{border:"none",background:"transparent",color:theme.textMeta,cursor:"pointer",fontSize:"22px"}}>✕</button>
            </div>
            <div style={{overflowY:"auto",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:"12px"}}>
              {BG_SCENES.map(bg=>{
                const isSelected=galleryBg?.id===bg.id;
                return (
                  <div key={bg.id} onClick={()=>{setGalleryBg(bg);setGalleryType("scene");setShowScenesGallery(false);if(currentUser)saveBackgroundToProfile(bg.id);}}
                    style={{borderRadius:"14px",overflow:"hidden",cursor:"pointer",border:isSelected?`3px solid ${bg.accent}`:"3px solid transparent",boxShadow:isSelected?`0 0 0 2px ${bg.accent}40,0 8px 24px rgba(0,0,0,0.5)`:"0 4px 16px rgba(0,0,0,0.4)",transform:isSelected?"scale(1.03)":"scale(1)",transition:"all 0.2s ease",position:"relative"}}>
                    <div style={{width:"100%",paddingBottom:"56%",backgroundImage:`url(${bg.file})`,backgroundSize:"cover",backgroundPosition:"center"}}/>
                    <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"20px 10px 8px",background:"linear-gradient(transparent,rgba(0,0,0,0.85))",fontSize:"11px",fontWeight:"700",color:"#fff",textAlign:"center"}}>{bg.label}</div>
                    {isSelected && <div style={{position:"absolute",top:"8px",right:"8px",background:bg.accent,borderRadius:"50%",width:"22px",height:"22px",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"12px"}}>✓</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Sprint 5: CITIES Gallery Modal ── */}
      {showCitiesGallery && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,backdropFilter:"blur(12px)"}}
          onClick={()=>setShowCitiesGallery(false)}>
          <div style={{background:theme.headerBg,border:`1px solid ${theme.sectionBorder}`,borderRadius:"20px",padding:"24px",width:"min(680px,96vw)",maxHeight:"85vh",display:"flex",flexDirection:"column",gap:"16px",boxShadow:"0 24px 64px rgba(0,0,0,0.6)",backdropFilter:"blur(24px)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:"18px",fontWeight:"800",color:theme.pill}}>🌆 Cities</div>
                <div style={{fontSize:"11px",color:theme.textMeta,marginTop:"2px"}}>{currentUser?"Your selection saves to your profile":"Login to save your background preference"}</div>
              </div>
              <button onClick={()=>setShowCitiesGallery(false)} style={{border:"none",background:"transparent",color:theme.textMeta,cursor:"pointer",fontSize:"22px"}}>✕</button>
            </div>
            <div style={{overflowY:"auto",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:"12px"}}>
              {BG_CITIES.map(bg=>{
                const isSelected=galleryBg?.id===bg.id;
                return (
                  <div key={bg.id} onClick={()=>{setGalleryBg(bg);setGalleryType("city");setShowCitiesGallery(false);if(currentUser)saveBackgroundToProfile(bg.id);}}
                    style={{borderRadius:"14px",overflow:"hidden",cursor:"pointer",border:isSelected?`3px solid ${bg.accent}`:"3px solid transparent",boxShadow:isSelected?`0 0 0 2px ${bg.accent}40,0 8px 24px rgba(0,0,0,0.5)`:"0 4px 16px rgba(0,0,0,0.4)",transform:isSelected?"scale(1.03)":"scale(1)",transition:"all 0.2s ease",position:"relative"}}>
                    <div style={{width:"100%",paddingBottom:"56%",backgroundImage:`url(${bg.file})`,backgroundSize:"cover",backgroundPosition:"center"}}/>
                    <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"20px 10px 8px",background:"linear-gradient(transparent,rgba(0,0,0,0.85))",fontSize:"11px",fontWeight:"700",color:"#fff",textAlign:"center"}}>{bg.label}</div>
                    {isSelected && <div style={{position:"absolute",top:"8px",right:"8px",background:bg.accent,borderRadius:"50%",width:"22px",height:"22px",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"12px"}}>✓</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── BOT SERVICES PANEL ── */}
      {showBotsPanel && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,backdropFilter:"blur(8px)"}} onClick={()=>setShowBotsPanel(false)}>
          <div style={{background:theme.headerBg,border:`1px solid ${theme.sectionBorder}`,borderRadius:"20px",padding:"28px 24px",width:"min(380px,94vw)",maxHeight:"70vh",display:"flex",flexDirection:"column",boxShadow:`0 12px 48px ${theme.glowColor}`,backdropFilter:"blur(24px)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"18px"}}>
              <span style={{fontSize:"16px",fontWeight:"800",color:theme.pill}}>🤖 My BOT Services</span>
              <button onClick={()=>setShowBotsPanel(false)} style={{border:"none",background:"transparent",color:theme.textMeta,cursor:"pointer",fontSize:"20px"}}>✕</button>
            </div>
            {(currentUser?.package||[]).length===0 ? (
              <div style={{textAlign:"center",color:theme.textMeta,fontSize:"13px",padding:"24px 0"}}>No BOT services yet. Say "I want to buy a package" to get started!</div>
            ) : (
              <div style={{overflowY:"auto",display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
                {(currentUser.package||[]).filter(pkg=>pkg!=="FREEMIUM").map((pkg,i)=>(
                  <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"8px",padding:"14px 10px",background:theme.inputBg,borderRadius:"14px",border:`1px solid ${theme.sectionBorder}`}}>
                    {PKG_ICONS[pkg] ? <img src={PKG_ICONS[pkg]} alt={pkg} style={{width:"52px",height:"52px",borderRadius:"12px",objectFit:"cover",boxShadow:`0 4px 12px ${theme.glowColor}`}}/> : <div style={{width:"52px",height:"52px",borderRadius:"12px",background:`linear-gradient(135deg,${theme.primary},${theme.primaryLight})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"24px"}}>🤖</div>}
                    <span style={{fontSize:"11px",fontWeight:"700",color:theme.textPrimary,textAlign:"center",lineHeight:"1.3"}}>{pkg.replace(/([A-Z])/g,' $1').trim()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── FIX 11: BOT STORE with Zone A / B / C tabs ── */}
      {showBotStore && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,backdropFilter:"blur(8px)"}}>
          <div style={{background:"#fff",borderRadius:"16px",padding:"0",width:"min(520px,96vw)",maxHeight:"88vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 40px rgba(0,0,0,0.5)",overflow:"hidden"}}>
            {/* Header + Zone tabs */}
            <div style={{padding:"16px 20px 0",borderBottom:"1px solid #f0f0f0"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
                <span style={{fontSize:"17px",fontWeight:"800",color:"#111"}}>🤖 BOT Store</span>
                <button onClick={()=>{ resetBotStoreNav(); setShowBotStore(false); }} style={{border:"none",background:"transparent",fontSize:"20px",cursor:"pointer",color:"#999"}}>✕</button>
              </div>
              <div style={{display:"flex",gap:"6px",overflowX:"auto"}}>
                {Object.entries(BOT_STORE_ZONES).map(([key, zone]) => {
                  const isActive = botStoreZone === key;
                  return (
                    <button key={key} onClick={() => { setBotStoreZone(key); setSelectedCategory(null); setBotStoreView("browse"); }}
                      style={{ padding:"7px 16px", borderRadius:"20px 20px 0 0", border:"none", cursor:"pointer", fontSize:"13px", fontWeight:isActive?"700":"500", background:isActive?zone.color:"#f5f5f5", color:isActive?"#fff":"#555", transition:"all 0.15s ease", flexShrink:0, whiteSpace:"nowrap" }}>
                      {zone.icon} {zone.label} <span style={{opacity:0.75,fontSize:"11px"}}>· {zone.sublabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Zone content */}
            <div style={{flex:1,overflowY:"auto",padding:"12px 0"}}>
              {botStoreView === "success" ? (
                <div style={{textAlign:"center",padding:"40px 24px"}}>
                  <div style={{fontSize:"48px",marginBottom:"16px"}}>✅</div>
                  <div style={{fontSize:"16px",fontWeight:"700",color:"#111",marginBottom:"10px"}}>Subscription Updated</div>
                  <div style={{fontSize:"14px",color:"#555",lineHeight:"1.5"}}>{botStoreSuccessMsg}</div>
                </div>
              ) : botStoreView === "cart" ? (
                <div style={{display:"flex",flexDirection:"column",padding:"0 0 8px"}}>
                  <button onClick={() => setBotStoreView("browse")}
                    style={{ display:"flex", alignItems:"center", gap:"6px", padding:"10px 20px", border:"none", background:"transparent", cursor:"pointer", fontSize:"14px", fontWeight:"600", color:"#2563EB", borderBottom:"1px solid #f0f0f0" }}>
                    ← Back
                  </button>
                  <div style={{padding:"12px 20px 6px", fontSize:"15px", fontWeight:"700", color:"#111"}}>Your Cart</div>
                  <div style={{padding:"4px 20px 10px", fontSize:"13px", color:"#666"}}>New monthly total: <strong style={{color:"#2563EB"}}>${botStoreMonthlyPrice}/mo</strong></div>
                  {botStoreChanges.subscribe.length === 0 && botStoreChanges.unsubscribe.length === 0 ? (
                    <div style={{textAlign:"center",padding:"24px 20px",color:"#999",fontSize:"14px"}}>No changes yet. Select services to subscribe or uncheck owned services to remove.</div>
                  ) : (
                    <>
                      {botStoreChanges.subscribe.length > 0 && (
                        <div style={{padding:"8px 20px 4px", fontSize:"12px", fontWeight:"700", color:"#2E7D32", letterSpacing:"0.4px"}}>TO SUBSCRIBE</div>
                      )}
                      {botStoreChanges.subscribe.map(name => (
                        <div key={`sub-${name}`} style={{display:"flex",alignItems:"center",gap:"10px",padding:"10px 20px",borderBottom:"1px solid #f5f5f5"}}>
                          {PKG_ICONS[name] ? <img src={PKG_ICONS[name]} alt={name} style={{width:"36px",height:"36px",borderRadius:"8px",objectFit:"cover"}}/> : <span>🤖</span>}
                          <span style={{flex:1,fontSize:"14px",fontWeight:"600"}}>{ZONE_A_SERVICE_DISPLAY[name] || catalogBot(storeCatalog, name)?.name || name}</span>
                          <span style={{fontSize:"12px",color:"#2563EB"}}>+${(ZONE_A_SERVICE_PRICE[name] ?? 0).toFixed(2)}</span>
                        </div>
                      ))}
                      {botStoreChanges.unsubscribe.length > 0 && (
                        <div style={{padding:"12px 20px 4px", fontSize:"12px", fontWeight:"700", color:"#C62828", letterSpacing:"0.4px"}}>TO UNSUBSCRIBE</div>
                      )}
                      {botStoreChanges.unsubscribe.map(name => (
                        <div key={`unsub-${name}`} style={{display:"flex",alignItems:"center",gap:"10px",padding:"10px 20px",borderBottom:"1px solid #f5f5f5",opacity:0.75}}>
                          {PKG_ICONS[name] ? <img src={PKG_ICONS[name]} alt={name} style={{width:"36px",height:"36px",borderRadius:"8px",objectFit:"cover",filter:"grayscale(1)"}}/> : <span>🤖</span>}
                          <span style={{flex:1,fontSize:"14px",fontWeight:"600",textDecoration:"line-through",color:"#888"}}>{ZONE_A_SERVICE_DISPLAY[name] || catalogBot(storeCatalog, name)?.name || name}</span>
                          <span style={{fontSize:"12px",color:"#C62828"}}>remove</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : getBotStoreCategories(botStoreZone).length === 0 ? (
                <div style={{textAlign:"center",padding:"40px 20px",color:"#aaa"}}>
                  <div style={{fontSize:"14px",fontWeight:"600",color:"#555"}}>{STORE_FROM_DB_ENABLED && !storeCatalog ? `Loading ${BOT_STORE_ZONES[botStoreZone].label}…` : `No ${BOT_STORE_ZONES[botStoreZone].label} services yet`}</div>
                </div>
              ) : botStoreZone !== "A" && selectedCategory ? (
                <div style={{display:"flex",flexDirection:"column"}}>
                  <button onClick={() => setSelectedCategory(null)}
                    style={{ display:"flex", alignItems:"center", gap:"6px", padding:"10px 20px", border:"none", background:"transparent", cursor:"pointer", fontSize:"14px", fontWeight:"600", color:"#2563EB", borderBottom:"1px solid #f0f0f0" }}>
                    ← Back
                  </button>
                  <div style={{padding:"8px 20px 4px", fontSize:"15px", fontWeight:"700", color:"#111"}}>{selectedCategory}</div>
                  {botsInCategory(storeCatalog, botStoreZone, selectedCategory).map(bot => {
                    const owned = (currentUser?.package || []).includes(bot.key);
                    const isSelected = packages.find(p => p.name === bot.key)?.selected ?? owned;
                    const priceLabel = bot.price === 0 ? "FREE" : `$${bot.price.toFixed(2)}/mo`;
                    return (
                      <div key={bot.key} style={{ display:"flex", alignItems:"center", gap:"12px", padding:"12px 20px", borderBottom:"1px solid #f5f5f5", cursor:"pointer" }}
                        onClick={() => toggleZoneAService(bot.key)}>
                        <div onClick={e => e.stopPropagation()} style={{flexShrink:0}}>
                          <input type="checkbox" checked={!!isSelected} onChange={() => toggleZoneAService(bot.key)}
                            style={{ width:"18px", height:"18px", accentColor:"#2563EB", cursor:"pointer" }} />
                        </div>
                        {PKG_ICONS[bot.key] ? <img src={PKG_ICONS[bot.key]} alt={bot.name} style={{ width:"48px", height:"48px", borderRadius:"10px", objectFit:"contain", background:"#fff", flexShrink:0 }} /> : <div style={{ width:"48px", height:"48px", borderRadius:"10px", background:"#f0f0f0", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"22px", flexShrink:0 }}>{BOT_STORE_ZONES[botStoreZone].icon}</div>}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:"14px", fontWeight:"700", color:"#111" }}>{bot.name}</div>
                          <div style={{ fontSize:"12px", color:"#666", marginTop:"2px", lineHeight:"1.35" }}>{bot.description}</div>
                          <div style={{ fontSize:"12px", fontWeight:"600", color:"#2563EB", marginTop:"4px" }}>{priceLabel}</div>
                        </div>
                        {isSelected ? (
                          <span style={{ padding:"6px 12px", borderRadius:"16px", background:"#E8F5E9", color:"#2E7D32", fontSize:"12px", fontWeight:"700", flexShrink:0 }}>✓ Added</span>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); handleZoneAGetService(bot.key); }}
                            style={{ padding:"7px 16px", borderRadius:"16px", border:"none", background:"#2563EB", color:"#fff", fontSize:"13px", fontWeight:"700", cursor:"pointer", flexShrink:0 }}>
                            Get
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : botStoreZone === "A" && selectedCategory ? (
                <div style={{display:"flex",flexDirection:"column"}}>
                  <button onClick={() => setSelectedCategory(null)}
                    style={{ display:"flex", alignItems:"center", gap:"6px", padding:"10px 20px", border:"none", background:"transparent", cursor:"pointer", fontSize:"14px", fontWeight:"600", color:"#2563EB", borderBottom:"1px solid #f0f0f0" }}>
                    ← Back
                  </button>
                  <div style={{padding:"8px 20px 4px", fontSize:"15px", fontWeight:"700", color:"#111"}}>{selectedCategory}</div>
                  {getZoneAServicesForCategory(selectedCategory).length === 0 ? (
                    <div style={{textAlign:"center",padding:"32px 20px",color:"#999",fontSize:"14px"}}>
                      No services available in this category yet.
                    </div>
                  ) : (
                    getZoneAServicesForCategory(selectedCategory).map(svcKey => {
                      const owned = (currentUser?.package || []).includes(svcKey);
                      const isSelected = packages.find(p => p.name === svcKey)?.selected ?? owned;
                      const price = ZONE_A_SERVICE_PRICE[svcKey];
                      const priceLabel = price === 0 ? "FREE" : `$${price.toFixed(2)}/mo`;
                      return (
                        <div key={svcKey} style={{ display:"flex", alignItems:"center", gap:"12px", padding:"12px 20px", borderBottom:"1px solid #f5f5f5", cursor:"pointer" }}
                          onClick={() => toggleZoneAService(svcKey)}>
                          <div onClick={e => e.stopPropagation()} style={{flexShrink:0}}>
                            <input type="checkbox" checked={!!isSelected} onChange={() => toggleZoneAService(svcKey)}
                              style={{ width:"18px", height:"18px", accentColor:"#2563EB", cursor:"pointer" }} />
                          </div>
                          {PKG_ICONS[svcKey] ? (
                            <img src={resolvePackageIcon(svcKey, currentUser?.botVariants)} alt={svcKey} style={{ width:"48px", height:"48px", borderRadius:"10px", objectFit:"cover", flexShrink:0 }} />
                          ) : (
                            <div style={{ width:"48px", height:"48px", borderRadius:"10px", background:"#f0f0f0", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"22px", flexShrink:0 }}>🤖</div>
                          )}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:"14px", fontWeight:"700", color:"#111" }}>{ZONE_A_SERVICE_DISPLAY[svcKey] || svcKey}</div>
                            <div style={{ fontSize:"12px", color:"#666", marginTop:"2px", lineHeight:"1.35" }}>{ZONE_A_SERVICE_DESC[svcKey]}</div>
                            <div style={{ fontSize:"12px", fontWeight:"600", color:"#2563EB", marginTop:"4px" }}>{priceLabel}</div>
                            {owned && botVariants(storeCatalog, svcKey).length > 0 && (
                              <div role="radiogroup" aria-label={`${ZONE_A_SERVICE_DISPLAY[svcKey] || svcKey} icon`} onClick={e => e.stopPropagation()} style={{ display:"flex", flexWrap:"wrap", gap:"6px", marginTop:"8px" }}>
                                {botVariants(storeCatalog, svcKey).map(v => {
                                  const on = chosenVariantId(storeCatalog, svcKey, currentUser?.botVariants) === v.variantId;
                                  return (
                                    <button key={v.variantId} type="button" role="radio" aria-checked={on} aria-label={v.name} title={v.name}
                                      onClick={async () => {
                                        const before = currentUser?.botVariants;
                                        setCurrentUser(u => ({ ...u, botVariants: { ...(u?.botVariants || {}), [svcKey]: v.variantId } }));
                                        const saved = await saveBotVariant(currentUser?._id, svcKey, v.variantId).catch(() => false);
                                        if (!saved) setCurrentUser(u => ({ ...u, botVariants: before }));
                                      }}
                                      style={{ width:"44px", height:"44px", padding:0, borderRadius:"10px", border: on ? "2px solid #2563EB" : "2px solid transparent", background:"none", cursor:"pointer" }}>
                                      <img src={v.iconUrl} alt="" loading="lazy" style={{ width:"100%", height:"100%", borderRadius:"8px", objectFit:"cover", display:"block" }} />
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          {isSelected ? (
                            <span style={{ padding:"6px 12px", borderRadius:"16px", background:"#E8F5E9", color:"#2E7D32", fontSize:"12px", fontWeight:"700", flexShrink:0 }}>✓ Added</span>
                          ) : (
                            <button onClick={(e) => { e.stopPropagation(); handleZoneAGetService(svcKey); }}
                              style={{ padding:"7px 16px", borderRadius:"16px", border:"none", background:"#2563EB", color:"#fff", fontSize:"13px", fontWeight:"700", cursor:"pointer", flexShrink:0 }}>
                              Get
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div style={{padding:"12px 16px 8px"}}>
                  {botStoreZone === "A" && (
                    <div style={{padding:"0 4px 12px", fontSize:"12px", color:"#666", lineHeight:"1.4"}}>
                      Tap a category bubble to browse BOT services.
                    </div>
                  )}
                  <div style={{display:"flex",flexWrap:"wrap",gap:"10px",justifyContent:"flex-start"}}>
                    {getBotStoreCategories(botStoreZone).map((cat, i) => {
                      const count = botStoreZone === "A" ? getZoneAServicesForCategory(cat).length : botsInCategory(storeCatalog, botStoreZone, cat).length;
                      return (
                        <button key={i} type="button"
                          onClick={botStoreZone==="A" || count > 0 ? () => setSelectedCategory(cat) : undefined}
                          style={{
                            display:"inline-flex", alignItems:"center", gap:"8px",
                            padding:"10px 16px", borderRadius:"999px",
                            border: selectedCategory===cat ? "2px solid #2563EB" : "1.5px solid #E5E7EB",
                            background: selectedCategory===cat ? "#EFF6FF" : "#F8FAFC",
                            color:"#111", cursor: botStoreZone==="A" || count > 0 ? "pointer" : "default",
                            fontSize:"13px", fontWeight:"600",
                            boxShadow:"0 1px 3px rgba(0,0,0,0.06)",
                            transition:"all 0.15s ease",
                          }}>
                          <span>{cat}</span>
                          {count > 0 && (
                            <span style={{
                              minWidth:"22px", height:"22px", borderRadius:"999px",
                              background:"#2563EB", color:"#fff", fontSize:"11px", fontWeight:"700",
                              display:"inline-flex", alignItems:"center", justifyContent:"center", padding:"0 6px",
                            }}>{count}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {/* Footer actions: Go to Cart / Submit / Success Okay */}
            <div style={{padding:"12px 20px",borderTop:"1px solid #eee",display:"flex",flexDirection:"column",gap:"8px"}}>
              {botStoreView === "success" ? (
                <button onClick={() => {
                    const msg = botStoreSuccessMsg;
                    resetBotStoreNav();
                    setShowBotStore(false);
                    // C1: speak only the backend-confirmed message — never invent success copy
                    if (msg) botReply(msg, "happy");
                  }}
                  style={{width:"100%",padding:"13px",borderRadius:"10px",border:"none",background:"#2563EB",color:"#fff",fontSize:"15px",fontWeight:"700",cursor:"pointer"}}>
                  Okay
                </button>
              ) : botStoreView === "cart" ? (
                <>
                  <button onClick={submitBotStorePackages} disabled={!hasBotStoreChanges}
                    style={{width:"100%",padding:"13px",borderRadius:"10px",border:"none",background:hasBotStoreChanges?"#2563EB":"#ccc",color:"#fff",fontSize:"15px",fontWeight:"700",cursor:hasBotStoreChanges?"pointer":"not-allowed"}}>
                    Submit
                  </button>
                  <button onClick={() => setBotStoreView("browse")}
                    style={{width:"100%",padding:"11px",borderRadius:"10px",border:"1px solid #ddd",background:"#fff",color:"#555",fontSize:"14px",fontWeight:"600",cursor:"pointer"}}>
                    Continue Shopping
                  </button>
                </>
              ) : (
                <>
                  {hasBotStoreChanges && (
                    <button onClick={() => setBotStoreView("cart")}
                      style={{width:"100%",padding:"13px",borderRadius:"10px",border:"none",background:"#34C759",color:"#fff",fontSize:"15px",fontWeight:"700",cursor:"pointer"}}>
                      Go to Cart ({botStoreChanges.subscribe.length + botStoreChanges.unsubscribe.length})
                    </button>
                  )}
                  <button onClick={submitBotStorePackages} disabled={!hasBotStoreChanges}
                    style={{width:"100%",padding:"13px",borderRadius:"10px",border:"none",background:hasBotStoreChanges?"#2563EB":"#ccc",color:"#fff",fontSize:"15px",fontWeight:"700",cursor:hasBotStoreChanges?"pointer":"not-allowed"}}>
                    Submit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── SHOPSAVVY VIEW ── */}
      {showViewSA && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,backdropFilter:"blur(8px)"}} onClick={()=>setShowViewSA(false)}>
          <div style={{background:"#fff",borderRadius:"12px",padding:"20px",width:"min(480px,94vw)",maxHeight:"85vh",display:"flex",flexDirection:"column",gap:"12px"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:"16px",fontWeight:"800",color:"#000"}}>🛍️ Shopping List</span>
              <button onClick={()=>setShowViewSA(false)} style={{border:"none",background:"transparent",fontSize:"20px",cursor:"pointer"}}>✕</button>
            </div>
            <div style={{overflowY:"auto",flex:1}}>
              {(saModalData||[]).map((item,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:"1px solid #eee"}}>
                  <span style={{fontSize:"14px",color:"#000"}}>{item.unitName ? `${item.quantity} ${item.unitName} of ${item.brand} ${item.type} ${item.thingName}` : (item.name||item.item||item)}</span>
                  {!item.unitName && <span style={{fontSize:"13px",color:"#666"}}>Qty: {item.quantity||1}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SHOPSAVVY UPDATE ── */}
      {showUpdateSA && (
        <div style={{...LIST.backdrop,zIndex:400}} onClick={()=>setShowUpdateSA(false)}>
          <div style={{...LIST.card,overflow:"hidden"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={LIST.title}>🛍️ {saModalMode === "update" ? "Edit Shopping List" : "Shopping List"}</span>
              <button onClick={()=>setShowUpdateSA(false)} style={{border:"none",background:"transparent",fontSize:"20px",cursor:"pointer"}}>✕</button>
            </div>
            <div style={{...LIST.list,overflowY:"auto",flex:1}}>
              {(saModalData||[]).map((item,i)=>(
                <div key={item.lineItemId || i} style={LIST.row}>
                  {saModalMode !== "view" && <input type="checkbox" defaultChecked={!item.delete} id={`sa-del-${i}`} style={{width:"18px",height:"18px"}} title="Keep item"/>}
                  <span style={{flex:1,fontSize:"14px",color:"#000"}}>{item.unitName ? `${item.quantity} ${item.unitName} of ${item.brand} ${item.type} ${item.thingName}` : (item.name||item.item||item.thingName)}</span>
                  {saModalMode !== "view" && <input type="number" defaultValue={item.quantity||1} min="1" id={`sa-upd-qty-${i}`} style={{width:"60px",padding:"4px 8px",border:"1px solid #ddd",borderRadius:"6px",fontSize:"13px"}}/>}
                </div>
              ))}
            </div>
            {saModalMode === "view" ? <EditListButton onClick={()=>setSaModalMode("update")} /> : (
            <button onClick={async()=>{
              const itemsToUpdate = (saModalData||[]).map((item,i)=>{
                const keep = document.getElementById(`sa-del-${i}`)?.checked !== false;
                const qty = parseInt(document.getElementById(`sa-upd-qty-${i}`)?.value || String(item.quantity||1), 10);
                return {
                  lineItemId: item.lineItemId,
                  quantity: qty > 0 ? qty : 1,
                  delete: !keep,
                };
              }).filter(x => x.lineItemId);
              if (!currentUser?._id) { setShowUpdateSA(false); await botReply("Please log in to update your shopping list.", "neutral"); return; }
              try {
                const res = await fetch("/api/updateShoppingList", {
                  method:"POST",
                  headers:{"Content-Type":"application/json"},
                  body:JSON.stringify({ _id: currentUser._id, items: itemsToUpdate }),
                });
                const data = await res.json();
                setShowUpdateSA(false);
                setSrx({});
                if (data.message) await botReply(data.message, "happy");
              } catch(_) { setShowUpdateSA(false); }
            }} style={{padding:"12px",borderRadius:"10px",border:"none",background:"#2563EB",color:"#fff",fontSize:"14px",fontWeight:"700",cursor:"pointer"}}>Save Shopping List</button>)}
          </div>
        </div>
      )}

      {/* ── SHOPSAVVY PURCHASE (fulfillment) ── */}
      {showPurchaseSA && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,backdropFilter:"blur(8px)"}} onClick={()=>setShowPurchaseSA(false)}>
          <div style={{background:"#fff",borderRadius:"12px",padding:"20px",width:"min(520px,94vw)",maxHeight:"85vh",display:"flex",flexDirection:"column",gap:"12px"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:"16px",fontWeight:"800",color:"#000"}}>🛒 Record Purchases</span>
              <button onClick={()=>setShowPurchaseSA(false)} style={{border:"none",background:"transparent",fontSize:"20px",cursor:"pointer"}}>✕</button>
            </div>
            <p style={{fontSize:"12px",color:"#666",margin:0}}>Check items you purchased, then enter store and date/time for each.</p>
            <div style={{overflowY:"auto",flex:1}}>
              {purchaseItems.map((item,i)=>(
                <div key={item.lineItemId || i} style={{padding:"12px 0",borderBottom:"1px solid #eee"}}>
                  <label style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer"}}>
                    <input type="checkbox" checked={!!item.purchased} onChange={(e)=>{
                      setPurchaseItems(prev => prev.map((p,idx)=> idx===i ? { ...p, purchased: e.target.checked } : p));
                    }} style={{width:"18px",height:"18px"}}/>
                    <span style={{fontSize:"14px",color:"#000"}}>{item.unitName ? `${item.quantity} ${item.unitName} of ${item.brand} ${item.type} ${item.thingName}` : (item.name||item.item||item.thingName)}</span>
                  </label>
                  {item.purchased && (
                    <div style={{display:"flex",flexDirection:"column",gap:"8px",marginTop:"8px",paddingLeft:"26px"}}>
                      <input type="text" placeholder="Store name" value={item.fulfilledStore||""} onChange={(e)=>{
                        setPurchaseItems(prev => prev.map((p,idx)=> idx===i ? { ...p, fulfilledStore: e.target.value } : p));
                      }} style={{padding:"8px",border:"1px solid #ddd",borderRadius:"6px",fontSize:"13px"}}/>
                      <input type="datetime-local" value={item.fulfilledDate||""} onChange={(e)=>{
                        setPurchaseItems(prev => prev.map((p,idx)=> idx===i ? { ...p, fulfilledDate: e.target.value } : p));
                      }} style={{padding:"8px",border:"1px solid #ddd",borderRadius:"6px",fontSize:"13px"}}/>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button onClick={async()=>{
              const fulfilled = purchaseItems.filter(p => p.purchased && p.lineItemId);
              if (!fulfilled.length) { await botReply("Please check at least one purchased item.", "neutral"); return; }
              if (fulfilled.some(p => !p.fulfilledStore?.trim())) { await botReply("Please enter the store name for each purchased item.", "neutral"); return; }
              if (FIELD_PREFILTER_ENABLED) {
                const check = await screenFields(fulfilled.map(p => ["store", p.fulfilledStore]));
                if (!check.ok) { await botReply(check.message, "sad"); return; }
              }
              if (!currentUser?._id) { setShowPurchaseSA(false); return; }
              try {
                const res = await fetch("/api/updateShoppingListFulfillment", {
                  method:"POST",
                  headers:{"Content-Type":"application/json"},
                  body:JSON.stringify({
                    _id: currentUser._id,
                    items: fulfilled.map(p => ({
                      lineItemId: p.lineItemId,
                      fulfilledStore: p.fulfilledStore.trim(),
                      fulfilledDate: p.fulfilledDate ? new Date(p.fulfilledDate).toISOString() : new Date().toISOString(),
                    })),
                  }),
                });
                const data = await res.json();
                setShowPurchaseSA(false);
                setSrx({});
                const name = currentUser?.name?.split(" ")[0] || "friend";
                await botReply(data.message || `Thank you ${name}!`, "happy");
              } catch(_) { setShowPurchaseSA(false); }
            }} style={{padding:"12px",borderRadius:"10px",border:"none",background:"#2563EB",color:"#fff",fontSize:"14px",fontWeight:"700",cursor:"pointer"}}>Done</button>
          </div>
        </div>
      )}

      {/* ── SHOPSAVVY: category picker (MBTS viewCategory) ── */}
      {showCategoryModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,backdropFilter:"blur(8px)"}} onClick={()=>setShowCategoryModal(false)}>
          <div style={{background:"#fff",borderRadius:"12px",padding:"20px",width:"min(480px,94vw)",maxHeight:"85vh",display:"flex",flexDirection:"column",gap:"12px"}} onClick={e=>e.stopPropagation()}>
            <span style={{fontSize:"16px",fontWeight:"800",color:"#000",textAlign:"center"}}>Select a Category</span>
            <p style={{fontSize:"12px",color:"#666",textAlign:"center",margin:0}}>Only categories with inventory are shown. Empty categories are hidden.</p>
            <div style={{overflowY:"auto",flex:1}}>
              {/* Biz: categories come from the thing database (only ones with products) */}
              {(Array.isArray(srx.data) && srx.data.length && typeof srx.data[0] === "string" ? srx.data : SHOPSAVVY_CATEGORIES).map((cat,i) => (
                <div key={i}
                  onClick={async()=>{
                    setShowCategoryModal(false);
                    setMessages(p=>[...p,{text:cat,time:formatTime(),isUser:true}]);
                    await continueSrxWithValue(cat);
                  }}
                  style={{padding:"14px 10px",borderBottom:"1px solid #eee",cursor:"pointer",fontSize:"14px",color:"#000",textTransform:"capitalize"}}>{cat}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SHOPSAVVY: unit picker (MBTS viewUnit) ── */}
      {showUnitModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,backdropFilter:"blur(8px)"}} onClick={()=>setShowUnitModal(false)}>
          <div style={{background:"#fff",borderRadius:"12px",padding:"20px",width:"min(480px,94vw)",maxHeight:"85vh",display:"flex",flexDirection:"column",gap:"12px"}} onClick={e=>e.stopPropagation()}>
            <span style={{fontSize:"16px",fontWeight:"800",color:"#000",textAlign:"center"}}>Select a Unit</span>
            <div style={{overflowY:"auto",flex:1}}>
              {/* Biz: units mirror how things in the thing database are measured */}
              {(Array.isArray(srx.data) && srx.data.length && typeof srx.data[0] === "string" ? srx.data : SHOPSAVVY_UNITS).map((unit,i) => (
                <div key={i} onClick={async()=>{ setShowUnitModal(false); setMessages(p=>[...p,{text:unit,time:formatTime(),isUser:true}]); await continueSrxWithValue(unit); }}
                  style={{padding:"14px 10px",borderBottom:"1px solid #eee",cursor:"pointer",fontSize:"14px",color:"#000"}}>{unit}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SHOPSAVVY: catalogue add items (MBTS addSA) ── */}
      {showShopSavvy && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,backdropFilter:"blur(8px)"}} onClick={()=>setShowShopSavvy(false)}>
          <div style={{background:"#fff",borderRadius:"12px",padding:"20px",width:"min(480px,94vw)",maxHeight:"85vh",display:"flex",flexDirection:"column",gap:"12px"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:"16px",fontWeight:"800",color:"#000"}}>🛍️ Add to Shopping List</span>
              <button onClick={()=>setShowShopSavvy(false)} style={{border:"none",background:"transparent",fontSize:"20px",cursor:"pointer"}}>✕</button>
            </div>
            <div style={{overflowY:"auto",flex:1}}>
              {(saModalData||[]).map((item,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:"10px",padding:"12px 0",borderBottom:"1px solid #eee"}}>
                  <input type="number" min="1" defaultValue={item.quantity||1} id={`sa-qty-${i}`} style={{width:"60px",padding:"4px 8px",border:"1px solid #ddd",borderRadius:"6px",fontSize:"13px"}}/>
                  <span style={{flex:1,fontSize:"14px",color:"#000"}}>{item.unitName ? `${item.unitName} of ${item.brand} ${item.type} ${item.thingName}` : (item.thingName || item.name || item.item)}</span>
                </div>
              ))}
            </div>
            <button onClick={async()=>{
              const itemsToAdd = (saModalData||[]).map((item,i)=>{
                const qty = parseInt(document.getElementById(`sa-qty-${i}`)?.value || "1", 10);
                return qty > 0 ? { thingId: item._id, quantity: qty } : null;
              }).filter(Boolean);
              if (!itemsToAdd.length) { await botReply("Please enter a quantity of 1 or more for at least one item.", "neutral"); return; }
              try {
                const res = await fetch("/api/addItemsToShoppingList", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ _id:currentUser?._id, items:itemsToAdd }) });
                const data = await res.json();
                setShowShopSavvy(false);
                // Biz: loop until the user says NO — keep the srx alive in the
                // "any more items?" state so a yes re-opens the category list.
                const sessionName = sessionFirstName || sessionStorage.getItem("btcg_session_name") || "";
                setSrx(enrichSrxState({
                  type: "input",
                  selectedPackage: "SHOPSAVVY",
                  intent: srxRef.current?.intent || "i want to add items to my shopping list",
                  moreCheck: srxRef.current?.inputList?.[0] || "2",
                  inputList: [],
                }, currentUser, sessionName));
                await botReply(`${data.message || "Items added to your shopping list"}. Do you have any more items to add?`, "happy");
              } catch(_) { setShowShopSavvy(false); }
            }} style={{padding:"12px",borderRadius:"10px",border:"none",background:"#2563EB",color:"#fff",fontSize:"14px",fontWeight:"700",cursor:"pointer"}}>Save</button>
          </div>
        </div>
      )}

      {showFeelingPicker && ledgerEmotions.length > 0 && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:450,backdropFilter:"blur(8px)"}} onClick={()=>setShowFeelingPicker(false)}>
          <div style={{background:"#fff",borderRadius:"12px",padding:"20px",width:"min(400px,94vw)",maxHeight:"70vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <span style={{fontSize:"16px",fontWeight:"800",color:"#000",display:"block",textAlign:"center",marginBottom:"12px"}}>
              {ledgerStep === 3 ? "How did it make you feel?" : "How were you feeling before?"}
            </span>
            <div style={{display:"flex",flexWrap:"wrap",gap:"8px",justifyContent:"center"}}>
              {ledgerEmotions.map(em => (
                <button key={em} type="button" onClick={()=>selectLedgerFeeling(em)}
                  style={{padding:"8px 14px",borderRadius:"20px",border:"1px solid #ccc",background:"#f5f5f5",cursor:"pointer",fontSize:"13px"}}>
                  {em}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showViewTodoModal && <ViewTodoModal theme={theme} items={todoModalData} onClose={()=>{setShowViewTodoModal(false);setSrx({});}}
        onEdit={()=>{ setShowViewTodoModal(false); setShowUpdateTodoModal(true); }}/>}
      <ChecklistPickerWeb open={checklistModal?.kind==="checklistPicker"} data={checklistModal?.data}
        onPick={async (option)=>{ setChecklistModal(null); await continueSrxWithValue(option.id); }}
        onClose={()=>{ setChecklistModal(null); setSrx({}); }} />
      <CatchphraseModal open={!!catchphrasePopup} email={verifyRef.current?.maskedEmail||""} busy={!!catchphrasePopup?.busy} error={catchphrasePopup?.error||""}
        onSubmit={async (value)=>{
          setCatchphrasePopup(p=>({ ...(p||{}), busy:true, error:"" }));
          await submitCatchphrase(value);
          // Still at the catchphrase step: it did not match; anything else (signed
          // in, the e-mail code after three tries, cancelled) closes the pop-up.
          if (loginStepRef.current==="awaiting_catchphrase") setCatchphrasePopup({ busy:false, error:"Your catchphrase did not match." });
          else setCatchphrasePopup(null);
        }}
        onCancel={async ()=>{ endVerification(); await botReply('Okay, sign-in cancelled. Say "login" whenever you are ready.',"neutral"); }}
      />
      <ChecklistViewWeb open={checklistModal?.kind==="checklistView"} data={checklistModal?.data} onClose={()=>setChecklistModal(null)}
        onEdit={()=>{ const n=checklistModal?.data?.list?.name; setChecklistModal(null); handleSend(n ? `edit my ${n} checklist` : "edit my checklist"); }} />
      <ViewChecklistWeb open={infoModal?.kind==="viewChecklist"} message={infoModal?.message} items={Array.isArray(infoModal?.data)?infoModal.data:[]} onClose={()=>setInfoModal(null)} />
      <EducationResourcesWeb open={infoModal?.kind==="educationResources"} message={infoModal?.message} links={infoModal?.data?.links||infoModal?.data||[]} onClose={()=>setInfoModal(null)} />
      <SafetyContactsWeb open={infoModal?.kind==="safetyContacts"} message={infoModal?.message}
        onNotifyContacts={()=>{ setInfoModal(null); setPostNeedPreset("digither"); setTimeout(()=>setShowPostNeedModal(true),200); }}
        onClose={()=>setInfoModal(null)} />
      <MealBreakdownWeb open={!!mealModal} mealDescription={mealModal?.description||""} onSave={finishMealBreakdown}
        onClose={()=>{ setMealModal(null); setLedgerActive(false); setLedgerStep(0); botReply('Okay, I have not saved that meal. Say "log my food" to try again.',"neutral"); }} />
      <PostNeedFormWeb
        open={showPostNeedModal}
        onClose={() => { setShowPostNeedModal(false); setSrx({}); }}
        onSubmit={handlePostNeed}
        userPackages={currentUser?.package || []}
        presetNeedType={postNeedPreset}
        currentUser={currentUser}
      />
      <ScheduleModalWeb
        open={showScheduleModal}
        onClose={() => { setShowScheduleModal(false); }}
        onSave={handleSaveSchedule}
        initialWake={srx?.wakeTime || currentUser?.wakeTime || ""}
        initialSleep={srx?.sleepTime || currentUser?.sleepTime || ""}
        initialSchedule={srx?.schedule || currentUser?.schedule || null}
      />
      <SecurityQuestionsModal
        open={!!secqForm}
        data={secqForm}
        busy={secqBusy}
        onSubmit={submitSecurityQuestions}
        onCancel={() => submitSecurityQuestions({ cancel: true })}
      />
      <GoalKeeperModalWeb
        open={showGoalModal}
        onClose={() => { setShowGoalModal(false); setSrx({}); }}
        onSave={handleSaveGoals}
        goals={goalModalData.goals}
        selectedGoals={goalModalData.selectedGoals}
      />
      <NeedsScreenWeb
        open={showNeedsScreen}
        mode={needsScreenMode}
        needTypeFilter={needsTypeFilter}
        currentUser={currentUser}
        onClose={() => { setShowNeedsScreen(false); setSrx({}); }}
        onBidPosted={(msg) => botReply(msg, "happy")}
      />
      {showUpdateTodoModal && (
        <UpdateTodoModal theme={theme} items={todoModalData} onClose={()=>setShowUpdateTodoModal(false)}
          onSave={async(updatedItems)=>{
            try { const res=await fetch("/api/updateTodoList",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({_id:currentUser?._id,items:updatedItems})}); const data=await res.json(); setShowUpdateTodoModal(false); if(data.message) await botReply(data.message,"happy"); } catch(_) { setShowUpdateTodoModal(false); }
          }}/>
      )}

      {/* ── HEADER: fixed on mobile so it never scrolls off-screen ── */}
      <header style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:headerPad,paddingTop:isSmall?"max(8px, env(safe-area-inset-top))":"4px",paddingBottom:isSmall?"8px":"4px",background:theme.headerBg,backdropFilter:"blur(18px)",borderBottom:`1px solid ${theme.headerBorder}`,boxShadow:`0 2px 20px ${theme.glowColor}`,zIndex:200,flexShrink:0,minHeight:isSmall?"52px":"60px",position:"fixed",top:0,left:0,right:0,WebkitBackdropFilter:"blur(18px)",touchAction:"manipulation",gap:isSmall?"8px":"12px"}}>
        <div style={{display:"flex",alignItems:"center",gap:isSmall?"8px":"10px",minWidth:0,flex:1}}>
          <div className="hov-pill" role="button" aria-label="Open menu" style={{display:"flex",flexDirection:"column",gap:"5px",cursor:"pointer",padding:isSmall?"8px":"10px 12px",borderRadius:"8px",flexShrink:0,minWidth:"44px",minHeight:"44px",justifyContent:"center",touchAction:"manipulation"}} onClick={()=>setMenuOpen(true)}>
            <span style={{width:"20px",height:"2px",background:theme.pill,borderRadius:"2px",display:"block"}}/>
            <span style={{width:"20px",height:"2px",background:theme.pill,borderRadius:"2px",display:"block"}}/>
            <span style={{width:"20px",height:"2px",background:theme.pill,borderRadius:"2px",display:"block"}}/>
          </div>
          <img src="/boticon.png" alt="mascot" style={{width:logoSize,height:logoSize,objectFit:"contain",filter:`drop-shadow(0 2px 12px ${theme.glowColor})`,transition:"all 0.4s ease",flexShrink:0}}/>
          <div style={{display:"flex",flexDirection:"column",gap:"3px",minWidth:0}}>
            <span style={{fontSize:titleSize,fontWeight:"800",color:theme.pill,letterSpacing:"0.3px",lineHeight:1.1,whiteSpace:"nowrap"}}>BOTCIERGE®</span>
            <span style={{fontSize:"11px",color:"#22c55e",fontWeight:"600",display:"flex",alignItems:"center",gap:"5px"}}>
              <span style={{width:"7px",height:"7px",borderRadius:"50%",background:"#22c55e",display:"inline-block",animation:"pulse 2s infinite",flexShrink:0}}/>
              Online
              {!isSmall && weather && <span title={[weather.city, weather.description].filter(Boolean).join(": ")} style={{marginLeft:"4px",fontSize:"10px",color:theme.primaryLight}}>{weather.icon} {(weatherUnit||weather.unit)==="F" ? (weather.tempF ?? weather.temp) : (weather.tempC ?? weather.temp)}°{weatherUnit || weather.unit || "C"}</span>}
              {!isSmall && chatError && <span style={{marginLeft:"6px",fontSize:"10px",color:"#f59e0b"}}>⚠ API offline</span>}
            </span>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:isSmall?"8px":"6px",flexShrink:0}}>
          {currentUser ? (
            <div style={{display:"flex",alignItems:"center",gap:isSmall?"8px":"6px",overflow:"hidden"}}>
              {!isSmall && visibleUserPackages(currentUser.package).length > 0 && (
                <div style={{display:"flex",gap:"4px",overflowX:"auto",maxWidth:"140px",WebkitOverflowScrolling:"touch",flexShrink:0}}>
                  {visibleUserPackages(currentUser.package).map((pkg,i)=>(
                    <img key={i} src={resolvePackageIcon(pkg, currentUser?.botVariants)} alt={pkg} title={pkg} style={{width:"26px",height:"26px",borderRadius:"7px",objectFit:"cover",border:`1.5px solid ${theme.pillBorder}`,flexShrink:0}}/>
                  ))}
                </div>
              )}
              <button className="hov-pill" onClick={()=>setMenuOpen(true)}
                style={{display:"flex",alignItems:"center",gap:"5px",background:`linear-gradient(135deg,${theme.primary}22,${theme.primaryLight}22)`,border:`1.5px solid ${theme.pillBorder}`,borderRadius:"20px",padding:isSmall?"6px 12px":"4px 10px",backdropFilter:"blur(8px)",flexShrink:0,cursor:"pointer"}}>
                <span style={{fontSize:"12px"}}>👤</span>
                <span style={{fontSize:pillFontSize,fontWeight:"800",color:theme.pill,whiteSpace:"nowrap",maxWidth:isSmall?"100px":"120px",overflow:"hidden",textOverflow:"ellipsis"}}>{currentUser.name?.split(" ")[0] || currentUser.name}</span>
              </button>
              {!isSmall && (
                <button className="hov-pill" onClick={()=>{logoutUser();setCurrentUser(null);setStep(null);setSrx({});setGalleryBg(null);setGalleryType(null);}}
                  style={{padding:"4px 10px",borderRadius:"20px",cursor:"pointer",fontSize:"11px",fontWeight:"600",border:`1px solid ${theme.pillBorder}`,background:"rgba(239,68,68,0.1)",color:"#ef4444",backdropFilter:"blur(8px)",whiteSpace:"nowrap",flexShrink:0}}>
                  Sign out
                </button>
              )}
            </div>
          ) : (
            !isSmall && (
            <div style={{display:"flex",gap:"4px",overflowX:"auto",WebkitOverflowScrolling:"touch",flexShrink:0}}>
              {AVATAR_LIST.map(name=>{
                const isActive=activeAvatar===name;
                return (
                  <button key={name} className="hov-pill" onClick={()=>{window._menuAvatarSwitch=true;setActiveAvatar(name);}}
                    style={{padding:"8px 16px",borderRadius:"24px",cursor:"pointer",fontSize:"13px",fontWeight:isActive?"700":"500",border:isActive?"1.5px solid transparent":`1.5px solid ${theme.pillBorder}`,background:isActive?`linear-gradient(135deg,${theme.primary},${theme.primaryLight})`:"rgba(255,255,255,0.06)",color:isActive?"#fff":theme.pill,boxShadow:isActive?`0 4px 14px ${theme.glowColor}`:"none",backdropFilter:"blur(8px)",whiteSpace:"nowrap",flexShrink:0,transition:"all 0.3s ease"}}>
                    {name}
                  </button>
                );
              })}
            </div>
            )
          )}
        </div>
      </header>
      <div aria-hidden="true" style={{height:headerSpacerH,flexShrink:0}} />

      {/* ── AVATAR SECTION ── */}
      <section ref={avatarRef} style={{position:"relative",height:avatarHeight,flexShrink:0,overflow:"hidden",background:activeBgImage?"transparent":avatarBgColor,transition:"height 0.3s ease"}}>
        {/* WeatherBOT card, top-left over the avatar. */}
        <WeatherCard weather={weather} compact={isSmall} unit={weatherUnit} onUnitChange={chooseWeatherUnit} />
        {activeBgImage && (
          <div style={{
            position:"absolute", top:0, left:0, right:0, bottom:0,
            width:"100%", height:"100%", minWidth:"100%", minHeight:"100%",
            backgroundImage:`url(${activeBgImage})`,
            backgroundSize:"cover",
            backgroundPosition:"center center",
            backgroundRepeat:"no-repeat",
            transform:`translate(${-parallax.x*0.25}px,${-parallax.y*0.25}px) scale(1.12)`,
            transformOrigin:"center center",
            transition:"transform 0.12s ease-out",
            zIndex:0,
          }}/>
        )}
        <ParticleLayer theme={theme}/>
        {emojiReactions.map(r => <EmojiReaction key={r.id} emoji={r.emoji} onDone={()=>setEmojiReactions(p=>p.filter(x=>x.id!==r.id))}/>)}
        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",transform:`translate(${parallax.x}px,${parallax.y}px)`,transition:"transform 0.08s ease-out",zIndex:2}}>
          {botState.avatarType==="3D" ? (
            <ThreeDAvatar emotion={activeEmotion}
              onReady={(e)=>{ if(e) headEngineRef.current=e; setEngineReady(true); setAvatarLoading(false); if(window._pendingGreeting && !window._greetingVoiceOnly){ setMessages([{text:window._pendingGreeting, time:formatTime()}]); } }}
              avatarName={activeAvatar} background={activeBgImage} isTyping={isTyping}/>
          ) : (
            <TwoDAvatar emotion={activeEmotion} theme={theme} avatarName={activeAvatar}/>
          )}
        </div>
        <div style={{position:"absolute",bottom:"12px",right:"14px",fontSize:"12px",fontWeight:"600",color:"#fff",background:theme.nameTagBg,backdropFilter:"blur(10px)",padding:"4px 12px",borderRadius:"20px",zIndex:5,border:"1px solid rgba(255,255,255,0.2)",letterSpacing:"0.5px"}}>
          {activeAvatar}
        </div>
      </section>

      {/* ── SCENES + CITIES BAR ── */}
      <div style={{display:"flex",alignItems:"center",gap:"8px",padding:scenePad,background:theme.barBg,borderBottom:`1px solid ${theme.sectionBorder}`,overflowX:"auto",flexShrink:0}}>
        <button onClick={()=>setShowScenesGallery(true)}
          style={{padding:isSmall?"5px 12px":"6px 16px",borderRadius:"10px",cursor:"pointer",fontSize:isSmall?"11px":"12px",fontWeight:"700",border:`1.5px solid ${galleryType==="scene"?theme.primary:theme.pillBorder}`,background:galleryType==="scene"?`linear-gradient(135deg,${theme.primary},${theme.primaryLight})`:"rgba(255,255,255,0.06)",color:galleryType==="scene"?"#fff":theme.pill,backdropFilter:"blur(8px)",whiteSpace:"nowrap",flexShrink:0,transition:"all 0.2s ease",boxShadow:galleryType==="scene"?`0 4px 14px ${theme.glowColor}`:"none"}}>
          🎬 {galleryType==="scene" && galleryBg ? galleryBg.label.split(" ").slice(1,2).join(" ").replace(/,+$/,"") : "Scenes"}
        </button>
        <button onClick={()=>setShowCitiesGallery(true)}
          style={{padding:isSmall?"5px 12px":"6px 16px",borderRadius:"10px",cursor:"pointer",fontSize:isSmall?"11px":"12px",fontWeight:"700",border:`1.5px solid ${galleryType==="city"?theme.primary:theme.pillBorder}`,background:galleryType==="city"?`linear-gradient(135deg,${theme.primary},${theme.primaryLight})`:"rgba(255,255,255,0.06)",color:galleryType==="city"?"#fff":theme.pill,backdropFilter:"blur(8px)",whiteSpace:"nowrap",flexShrink:0,transition:"all 0.2s ease",boxShadow:galleryType==="city"?`0 4px 14px ${theme.glowColor}`:"none"}}>
          🌆 {galleryType==="city" && galleryBg ? galleryBg.label.split(" ").slice(1,2).join(" ").replace(/,+$/,"") : "Cities"}
        </button>
        {galleryBg && (
          <button onClick={()=>{ setGalleryBg(null); setGalleryType(null); }}
            style={{padding:"4px 10px",borderRadius:"20px",border:`1px solid ${theme.pillBorder}`,background:"transparent",color:theme.textMeta,fontSize:"10px",cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
            ✕ Clear
          </button>
        )}
        {BG_LIST.filter(k=>k!=="default").map(key=>{
          const t=BG_THEMES[key]; const isActive=activeBgKey===key&&!galleryBg;
          // LAZY_BACKGROUNDS_ENABLED: the picker shows 160 px thumbnails (/thumbs); the full photo loads only when chosen.
          const thumb=t.bgImage&&process.env.REACT_APP_LAZY_BACKGROUNDS_ENABLED!=="false"?t.bgImage.replace(/^\/(bg\d\.jpg)$/,"/thumbs/$1"):t.bgImage;
          const btnBg=thumb?`url(${thumb}) center/cover`:(t.bgStyle||"rgba(107,78,170,0.15)");
          return (
            <button key={key} className="hov-bg" title={t.label} onClick={()=>{switchBg(key);setGalleryBg(null);setGalleryType(null);}}
              style={{width:sceneBtnSize,height:sceneBtnSize,borderRadius:"10px",cursor:"pointer",flexShrink:0,overflow:"hidden",border:isActive?`2.5px solid ${theme.primary}`:"2px solid rgba(255,255,255,0.15)",background:btnBg,boxShadow:isActive?`0 0 0 3px ${theme.glowColor},0 2px 8px rgba(0,0,0,0.4)`:"0 1px 4px rgba(0,0,0,0.25)",transform:isActive?"scale(1.16)":"scale(1)",transition:"all 0.22s ease"}}/>
          );
        })}
      </div>

      {/* ── CHAT ── */}
      <div style={{flex:1,overflowY:"auto",padding:"14px 20px",display:"flex",flexDirection:"column",gap:"10px",alignItems:"center",background:theme.chatBg}}>
        {messages.map((msg, i) => (
          <ChatBubble key={i} msg={msg} avatarName={activeAvatar} theme={theme}
            onOpenLink={openSearchLink} onEditGoals={openGoalEditor}/>
        ))}
        {showTypingIndicator && <div style={{width:"100%",maxWidth:"580px"}}><TypingIndicator theme={theme}/></div>}
        <div ref={chatEndRef}/>
      </div>

      {/* ── YES/NO BUTTONS ── */}
      {showYesNo && (
        <div style={{padding:"8px 16px",background:theme.headerBg,borderTop:`1px solid ${theme.sectionBorder}`,display:"flex",gap:"12px",justifyContent:"center",flexShrink:0}}>
          <button onClick={handleYes} style={{padding:"10px 32px",borderRadius:"24px",background:"linear-gradient(135deg,#22c55e,#16a34a)",color:"#fff",border:"none",fontSize:"14px",fontWeight:"700",cursor:"pointer",boxShadow:"0 4px 14px rgba(34,197,94,0.35)"}}>✅ Yes</button>
          <button onClick={handleNo}  style={{padding:"10px 32px",borderRadius:"24px",background:"linear-gradient(135deg,#ef4444,#dc2626)",color:"#fff",border:"none",fontSize:"14px",fontWeight:"700",cursor:"pointer",boxShadow:"0 4px 14px rgba(239,68,68,0.35)"}}>❌ No</button>
        </div>
      )}

      {/* ── INPUT BAR ── */}
      <div style={{padding:inputPad,paddingBottom:isSmall?"max(8px, env(safe-area-inset-bottom))":"10px",background:theme.headerBg,borderTop:`1px solid ${theme.sectionBorder}`,backdropFilter:"blur(12px)",flexShrink:0}}>
        <div style={{maxWidth:"900px",margin:"0 auto",padding:isSmall?"0 4px":"0 40px"}}>
          <input ref={photoInputRef} type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={handlePhotoInputChange} />
          <input ref={askFileInputRef} type="file" accept={ASK_ACCEPT} multiple style={{display:"none"}} onChange={handleAskFileChange} />
          {/* ASK attachments waiting for the next request. */}
          {(askAttachments.length > 0 || askUploading) && (
            <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:"6px",marginBottom:"6px"}}>
              {askAttachments.map((a) => (
                <span key={a.id} style={{display:"inline-flex",alignItems:"center",gap:"6px",maxWidth:"240px",padding:"4px 6px 4px 10px",borderRadius:"14px",background:"rgba(255,255,255,0.08)",border:`1px solid ${theme.pillBorder}`,color:theme.textPrimary,fontSize:"12px"}}>
                  <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📄 {a.name}</span>
                  <button onClick={()=>setAskAttachments((p)=>p.filter((x)=>x.id!==a.id))} aria-label={`Remove ${a.name}`}
                    style={{border:"none",background:"transparent",color:theme.textMeta,cursor:"pointer",fontSize:"13px",padding:"0 2px"}}>✕</button>
                </span>
              ))}
              {askUploading && <span style={{fontSize:"12px",color:theme.textMeta}}>Uploading…</span>}
              {askAttachments.length > 0 && <span style={{fontSize:"11px",color:theme.textMeta}}>Ask an LLM about {askAttachments.length > 1 ? "them" : "it"}, e.g. "ask chatgpt to summarise this"</span>}
            </div>
          )}
          <div style={{display:"flex",alignItems:"center",gap:"8px",background:loginStep?"rgba(107,78,170,0.08)":theme.inputBg,border:`1.5px solid ${loginStep?theme.primary:inputFocused?theme.primary:theme.inputBorder}`,borderRadius:"28px",padding:"6px 6px 6px 18px",transition:"all 0.25s ease",boxShadow:inputFocused?`0 0 0 3px ${theme.glowColor}`:"none"}}>
            {showRememBotPhoto && (
              <button onClick={()=>photoInputRef.current?.click()} title="Upload a photo memory"
                style={{width:"34px",height:"34px",border:`1.5px solid ${theme.pillBorder}`,borderRadius:"50%",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(255,255,255,0.06)",color:theme.pill,fontSize:"16px",flexShrink:0}}>
                📷
              </button>
            )}
            {currentUser && !loginStep && (
              <button onClick={()=>askFileInputRef.current?.click()} title="Attach a file for ASK (PDF, Word, Excel, CSV, text, image)" aria-label="Attach a file"
                disabled={askUploading || askAttachments.length >= MAX_ATTACHMENTS}
                style={{width:"34px",height:"34px",border:`1.5px solid ${theme.pillBorder}`,borderRadius:"50%",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(255,255,255,0.06)",color:theme.pill,fontSize:"16px",flexShrink:0,opacity:askUploading?0.5:1}}>
                📎
              </button>
            )}
            <input style={{flex:1,border:"none",background:"transparent",fontSize:"14px",outline:"none",color:theme.textPrimary,padding:"6px 0",minWidth:0}}
              type={maskedEntry && !showSecret ? "password" : "text"}
              autoComplete={maskedEntry ? "off" : undefined}
              placeholder={inputPlaceholder} value={inputText} autoCapitalize={loginStep==="awaiting_catchphrase"?"off":undefined} autoCorrect={loginStep==="awaiting_catchphrase"?"off":undefined} spellCheck={loginStep==="awaiting_catchphrase"?false:undefined}
              onChange={handleInputChange} onKeyDown={handleKeyDown}
              onFocus={()=>setInputFocused(true)} onBlur={()=>setInputFocused(false)}/>
            {maskedEntry && (
              <button type="button" onClick={()=>setShowSecret(v=>!v)} aria-label={showSecret?"Hide answer":"Show answer"} title={showSecret?"Hide answer":"Show answer"} aria-pressed={showSecret}
                style={{width:"34px",height:"34px",border:"none",borderRadius:"50%",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",background:"transparent",color:theme.primaryLight}}>
                <EyeIcon open={showSecret}/>
              </button>
            )}
            <button onClick={toggleVoice} title="Voice input"
              style={{width:"36px",height:"36px",border:"none",borderRadius:"50%",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",background:listening?"rgba(239,68,68,0.15)":"transparent",animation:listening?"micPulse 1s infinite":"none",transition:"all 0.2s ease",flexShrink:0}}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill={listening?"#ef4444":theme.primaryLight}>
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
            </button>
            <button className="hov-send" onClick={handleSend}
              style={{width:"40px",height:"40px",border:"none",background:`linear-gradient(135deg,${theme.primary},${theme.primaryLight})`,borderRadius:"50%",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 3px 10px ${theme.glowColor}`,flexShrink:0,transition:"all 0.2s ease"}}>
              <svg viewBox="0 0 24 24" fill="white" width="18" height="18"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
            </button>
          </div>
          {listening && <p style={{textAlign:"center",fontSize:"11px",color:"#ef4444",marginTop:"4px",animation:"pulse 1s infinite"}}>🎙️ Listening... speak now</p>}
        </div>
      </div>

      {/* ── SIDE MENU ── */}
      {menuOpen && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",display:"flex",zIndex:10000,backdropFilter:"blur(6px)",touchAction:"none",WebkitOverflowScrolling:"touch",paddingTop:"env(safe-area-inset-top, 0px)"}} onClick={()=>setMenuOpen(false)}>
          <div style={{width:menuWidth,background:theme.headerBg,backdropFilter:"blur(24px)",padding:"24px 20px",display:"flex",flexDirection:"column",gap:"10px",boxShadow:`4px 0 32px ${theme.glowColor}`,animation:"slideInMenu 0.25s ease",border:`1px solid ${theme.sectionBorder}`,overflowY:"auto",WebkitOverflowScrolling:"touch",touchAction:"pan-y"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
              <img src="/boticon.png" alt="mascot" style={{width:40,height:40,objectFit:"contain"}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:"18px",fontWeight:"800",color:theme.pill}}>BOTCIERGE®</div>
                <div style={{fontSize:"11px",color:"#22c55e",fontWeight:500}}>● Online</div>
              </div>
              {/* Biz: hamburger menus close via the little x in the top right corner (or clicking off the menu) */}
              <button onClick={()=>setMenuOpen(false)} aria-label="Close menu"
                style={{width:"30px",height:"30px",borderRadius:"8px",border:`1px solid ${theme.sectionBorder}`,background:"rgba(255,255,255,0.06)",color:theme.textPrimary,fontSize:"14px",fontWeight:"700",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                ✕
              </button>
            </div>
            <div style={{height:"1px",background:`linear-gradient(90deg,${theme.primary},transparent)`,margin:"4px 0"}}/>
            {currentUser ? (
              <div style={{padding:"10px 12px",borderRadius:"10px",background:theme.inputBg,border:`1px solid ${theme.sectionBorder}`}}>
                <div style={{fontSize:"12px",fontWeight:"700",color:theme.primaryLight}}>👤 Signed in as</div>
                <div style={{fontSize:"14px",fontWeight:"600",color:theme.textPrimary,marginTop:"2px"}}>{currentUser.name}</div>
              </div>
            ) : (
              <div style={{padding:"10px 12px",borderRadius:"10px",background:theme.inputBg,border:`1px solid ${theme.sectionBorder}`,fontSize:"12px",color:theme.textMeta}}>Say "login" in chat to sign in</div>
            )}
            <p style={{fontSize:"10px",fontWeight:"700",color:theme.primaryLight,letterSpacing:"1.5px",margin:"4px 0"}}>AVATAR</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
              {AVATAR_LIST.map((name)=>(
                <button key={name} onClick={()=>{window._menuAvatarSwitch=true;setActiveAvatar(name);setMenuOpen(false);}}
                  style={{padding:"9px 6px",borderRadius:"10px",cursor:"pointer",fontSize:"12px",fontWeight:activeAvatar===name?"700":"400",border:activeAvatar===name?"none":`1.5px solid ${theme.sectionBorder}`,background:activeAvatar===name?`linear-gradient(135deg,${theme.primary},${theme.primaryLight})`:"rgba(255,255,255,0.05)",color:activeAvatar===name?"#fff":theme.textPrimary,textAlign:"center"}}>
                  {AVATAR_ICONS[name]} {name}
                </button>
              ))}
            </div>
            <p style={{fontSize:"10px",fontWeight:"700",color:theme.primaryLight,letterSpacing:"1.5px",margin:"4px 0"}}>AVATAR TYPE</p>
            <div style={{display:"flex",gap:"8px"}}>
              {["3D","2D"].map(type=>(
                <button key={type} onClick={()=>{controlBotcierge({avatarType:type,emotion:activeEmotion});setMenuOpen(false);}}
                  style={{flex:1,padding:"10px",borderRadius:"10px",cursor:"pointer",fontSize:"13px",fontWeight:botState.avatarType===type?"600":"400",border:botState.avatarType===type?"none":`1.5px solid ${theme.sectionBorder}`,background:botState.avatarType===type?`linear-gradient(135deg,${theme.primary},${theme.primaryLight})`:"rgba(255,255,255,0.05)",color:botState.avatarType===type?"#fff":theme.textPrimary}}>
                  {type==="3D"?"✦ 3D":"◈ 2D"}
                </button>
              ))}
            </div>
            <p style={{fontSize:"10px",fontWeight:"700",color:theme.primaryLight,letterSpacing:"1.5px",margin:"4px 0"}}>EMOTION</p>
            <select style={{width:"100%",padding:"10px 12px",border:`1.5px solid ${theme.sectionBorder}`,borderRadius:"10px",background:theme.inputBg,fontSize:"13px",color:theme.textPrimary,outline:"none"}}
              value={activeEmotion} onChange={e=>{handleEmotionClick(e.target.value);setMenuOpen(false);}}>
              {EMOTIONS.map(m=><option key={m} value={m}>{EMOTION_EMOJIS[m]} {m}</option>)}
            </select>
            <button onClick={()=>{setShowScenesGallery(true);setMenuOpen(false);}}
              style={{padding:"10px",borderRadius:"10px",border:`1.5px solid ${theme.pillBorder}`,background:"rgba(255,255,255,0.05)",color:theme.pill,fontSize:"13px",fontWeight:"600",cursor:"pointer"}}>
              🎬 Scenes
            </button>
            <button onClick={()=>{setShowCitiesGallery(true);setMenuOpen(false);}}
              style={{padding:"10px",borderRadius:"10px",border:`1.5px solid ${theme.pillBorder}`,background:"rgba(255,255,255,0.05)",color:theme.pill,fontSize:"13px",fontWeight:"600",cursor:"pointer"}}>
              🌆 Cities
            </button>
            {/* Biz: xShare users get the xShare options in the menu (mobile parity) */}
            {currentUser && (currentUser.package||[]).some(p=>XSHARE_PACKAGES.includes(p)) && (
              <>
                <p style={{fontSize:"10px",fontWeight:"700",color:theme.primaryLight,letterSpacing:"1.5px",margin:"4px 0"}}>XSHARE</p>
                <button onClick={()=>{setMenuOpen(false);setPostNeedPreset("");setTimeout(()=>setShowPostNeedModal(true),150);}}
                  style={{padding:"10px",borderRadius:"10px",border:`1.5px solid ${theme.pillBorder}`,background:"rgba(255,255,255,0.05)",color:theme.pill,fontSize:"13px",fontWeight:"600",cursor:"pointer",textAlign:"left"}}>
                  📝 Post a Need
                </button>
                <button onClick={()=>{setMenuOpen(false);setNeedsScreenMode("activeNeeds");setNeedsTypeFilter("");setTimeout(()=>setShowNeedsScreen(true),150);}}
                  style={{padding:"10px",borderRadius:"10px",border:`1.5px solid ${theme.pillBorder}`,background:"rgba(255,255,255,0.05)",color:theme.pill,fontSize:"13px",fontWeight:"600",cursor:"pointer",textAlign:"left"}}>
                  🌍 Active Needs
                </button>
                <button onClick={()=>{setMenuOpen(false);setNeedsScreenMode("myNeeds");setNeedsTypeFilter("");setTimeout(()=>setShowNeedsScreen(true),150);}}
                  style={{padding:"10px",borderRadius:"10px",border:`1.5px solid ${theme.pillBorder}`,background:"rgba(255,255,255,0.05)",color:theme.pill,fontSize:"13px",fontWeight:"600",cursor:"pointer",textAlign:"left"}}>
                  📋 My Needs
                </button>
              </>
            )}
            {/* Biz: Activity Ledger removed from menu — it belongs in the bot-service flow, not as a menu option */}
            {currentUser && visibleUserPackages(currentUser.package).length > 0 && (
              <div style={{padding:"8px 0"}}>
                <p style={{fontSize:"10px",fontWeight:"700",color:theme.primaryLight,letterSpacing:"1.5px",margin:"0 0 6px"}}>MY SERVICES</p>
                <div style={{display:"flex",flexWrap:"wrap",gap:"6px"}}>
                  {visibleUserPackages(currentUser.package).map((pkg,i)=>(
                    <img key={i} src={resolvePackageIcon(pkg, currentUser?.botVariants)} alt={pkg} title={pkg} style={{width:"32px",height:"32px",borderRadius:"8px",objectFit:"cover",border:`1.5px solid ${theme.pillBorder}`}}/>
                  ))}
                </div>
              </div>
            )}
            {/* Biz: Exit is the ONLY button on the menu. Always visible (auth or not).
                It exits the app back to "Tap to continue" — used to escape hung handler loops. */}
            <button onClick={handleExitApp}
              style={{marginTop:"8px",width:"100%",padding:"12px",background:"rgba(239,68,68,0.15)",color:"#ef4444",borderRadius:"10px",cursor:"pointer",fontWeight:"700",fontSize:"13px",border:`1px solid rgba(239,68,68,0.3)`}}>
              Exit
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
